import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from "recharts";
import { createApi } from "./api.js";

// معلومات الإصدار — للتتبّع (Semantic Versioning)
export const VERSION = "1.0.0";
export const RELEASED = "2026-07-20";

const T = {
  petrol: "#13282C", petrolSoft: "#1C3A3E", verdi: "#2F6D68", verdiSoft: "#E3EFED",
  brass: "#8C7448", ink: "#12262A", ink2: "#4B5D60", muted: "#84979A", line: "#DCE3E1",
  ok: "#2F7A63", warn: "#B07C2A", bad: "#A84A3F", gray: "#93A3A2", surface: "#FFFFFF", surface2: "#F6F8F7",
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Almarai:wght@400;700;800&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap');
:root{
  --porcelain:#EDF0EF; --surface:#FFFFFF; --surface-2:#F6F8F7;
  --ink:#12262A; --ink-2:#4B5D60; --muted:#84979A;
  --line:#DCE3E1; --line-soft:#E9EEEC;
  --petrol:#13282C; --petrol-2:#1C3A3E; --verdi:#2F6D68; --verdi-soft:#E3EFED; --brass:#8C7448;
  --ok:#2F7A63; --ok-bg:#E6F0EC; --warn:#B07C2A; --warn-bg:#F6EEDF; --bad:#A84A3F; --bad-bg:#F6E7E5;
  --gray:#93A3A2; --gray-bg:#EBEEED;
  --r:14px; --r-sm:10px;
}
*{box-sizing:border-box}
.app{font-family:'Noto Sans Arabic',sans-serif;color:var(--ink);min-height:100vh;background:var(--porcelain)}
.app h1,.app h2,.app h3,.dsp{font-family:'Almarai',sans-serif;font-weight:700}
.fade{animation:fade .34s ease both}
@keyframes fade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
@media(prefers-reduced-motion:reduce){*{animation:none!important;transition:none!important}}
:focus-visible{outline:2px solid var(--verdi);outline-offset:2px}
::-webkit-scrollbar{width:9px;height:9px}::-webkit-scrollbar-thumb{background:var(--line);border-radius:9px}
.top{position:sticky;top:0;z-index:30;background:var(--petrol);border-bottom:1px solid #0b1c1f}
.top-in{max-width:1180px;margin:0 auto;padding:13px 22px;display:flex;align-items:center;justify-content:space-between;gap:14px}
.brand{display:flex;align-items:center;gap:11px}
.brand-mark{width:38px;height:38px;border-radius:11px;display:grid;place-items:center;background:var(--verdi);color:#DCEAE7;border:1px solid rgba(140,116,72,.55)}
.brand-name{color:#F1F5F4;font-size:15.5px}
.brand-sub{color:#7E9997;font-size:11px;margin-top:2px}
.hd-actions{display:flex;align-items:center;gap:10px;min-width:0}
.nav{display:flex;align-items:center;gap:2px;min-width:0}
.nav button{background:0;border:0;color:#8FA8A6;font-family:inherit;font-size:12.8px;font-weight:600;padding:8px 13px;border-radius:9px;cursor:pointer}
.nav button.on{background:rgba(255,255,255,.07);color:#fff}
.avatar{width:32px;height:32px;border-radius:50%;display:grid;place-items:center;background:var(--verdi);color:#fff;font-size:12px;font-weight:700;border:1px solid rgba(140,116,72,.5)}
.wrap{max-width:1180px;margin:0 auto;padding:24px 22px 64px;display:flex;flex-direction:column;gap:20px}
.card{background:var(--surface);border:1px solid var(--line);border-radius:var(--r);min-width:0}
.pad{min-width:0}
.pad{padding:20px}
.h-row{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap}
.title{display:flex;align-items:center;gap:9px;font-size:16px;font-family:'Almarai';font-weight:700}
.title svg{color:var(--verdi)}
.hint{color:var(--muted);font-size:12.4px;margin:6px 0 0;line-height:1.7}
.btn{display:inline-flex;align-items:center;gap:6px;height:36px;padding:0 14px;border-radius:var(--r-sm);font-family:inherit;font-size:12.8px;font-weight:600;border:1px solid transparent;cursor:pointer;white-space:nowrap;transition:.15s}
.btn-p{background:var(--petrol);color:#fff}
.btn-p:hover{background:var(--petrol-2)}
.btn-g{background:var(--surface);border-color:var(--line);color:var(--ink-2)}
.btn-g:hover{background:var(--surface-2)}
.btn-v{background:var(--verdi-soft);color:var(--verdi);border-color:#CBDFDB}
.btn-sm{height:30px;padding:0 10px;font-size:11.8px}
.btn:disabled{opacity:.42;cursor:not-allowed}
.pill{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:99px;font-size:11.4px;font-weight:600;white-space:nowrap}
.p-ok{background:var(--ok-bg);color:#255f4e}.p-warn{background:var(--warn-bg);color:#815a17}
.p-bad{background:var(--bad-bg);color:#853a31}.p-gray{background:var(--gray-bg);color:#65726f}
.p-v{background:var(--verdi-soft);color:var(--verdi)}
.src{display:inline-flex;align-items:center;gap:4px;background:var(--surface-2);border:1px solid var(--line);color:var(--ink-2);font-size:10.8px;font-weight:600;padding:2px 7px;border-radius:6px}
.mut{color:var(--muted)}
.seal{position:relative;width:56px;height:56px;flex:0 0 auto}
.seal-big{width:74px;height:74px}
.badge360{display:inline-flex;align-items:center;gap:8px;background:var(--petrol);border:1px solid var(--brass);border-radius:99px;padding:5px 12px 5px 5px}
.badge360 i{width:22px;height:22px;border-radius:99px;background:var(--verdi);color:#EAF3F1;font-size:9.5px;font-style:normal;display:grid;place-items:center;font-family:'Almarai';font-weight:800;border:1px solid rgba(140,116,72,.7)}
.badge360 span{color:#D7E4E1;font-size:11.4px;font-weight:700;font-family:'Almarai'}
.grid-cards{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.pcard{position:relative;text-align:right;font-family:inherit;background:var(--surface);border:1px solid var(--line);border-radius:var(--r);padding:18px;cursor:pointer;transition:.18s;display:flex;flex-direction:column;gap:13px}
.pcard:hover{transform:translateY(-2px);box-shadow:0 14px 30px -22px rgba(18,38,42,.5)}
.pcard.is360{border-color:#C6D8D4;box-shadow:0 0 0 3px rgba(47,109,104,.07)}
.pcard.is360::before{content:"";position:absolute;inset-inline-start:0;top:18px;bottom:18px;width:3px;border-radius:9px;background:linear-gradient(var(--verdi),var(--brass))}
.pc-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.pc-name{font-family:'Almarai';font-weight:700;font-size:17px}
.pc-lbl{color:var(--muted);font-size:10.2px;margin-top:8px}
.pc-val{font-size:12.6px;font-weight:600;margin-top:2px}
.pc-tgt2{font-size:14px;font-weight:700;color:var(--ink-2);font-family:'Almarai'}
.pc-desc{color:var(--ink-2);font-size:12.4px;line-height:1.75;margin-top:3px;min-height:38px}
.pc-dates{display:flex;gap:16px;font-size:11.6px;color:var(--ink-2);border-top:1px solid var(--line-soft);border-bottom:1px solid var(--line-soft);padding:10px 0}
.pc-dates b{display:block;color:var(--ink);font-weight:600;margin-top:3px}
.lampchip{border:1px solid #6b2b2b;background:#fbeaea;color:#8a1f1f;border-radius:999px;padding:2px 9px;font-size:10.8px;font-weight:600;cursor:pointer;max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.lampchip:hover{background:#f6dede}
.segbar{display:flex;gap:6px;flex-wrap:wrap}
.seg{background:var(--surface-2);border:1px solid var(--line);color:var(--ink-2);font-family:inherit;font-size:12px;font-weight:600;padding:6px 12px;border-radius:99px;cursor:pointer}
.seg b{color:var(--muted);margin-inline-start:3px}
.seg.on{background:var(--verdi);color:#fff;border-color:var(--verdi)}
.seg.on b{color:#dbeae7}
.pc-money{display:flex;justify-content:space-between;align-items:flex-end}
.pc-rev{font-family:'Almarai';font-weight:800;font-size:19px;color:var(--verdi)}
.pc-tgt{font-size:11.6px;color:var(--muted)}
.track{height:6px;background:var(--line-soft);border-radius:99px;overflow:hidden}
.track>div{height:100%;border-radius:99px;transition:width .5s}
.pc-foot{display:flex;align-items:center;justify-content:space-between;font-size:11.6px;color:var(--muted);border-top:1px solid var(--line-soft);padding-top:11px}
.info{display:grid;grid-template-columns:repeat(4,1fr);border-top:1px solid var(--line-soft)}
.info-i{padding:12px 16px;border-inline-start:1px solid var(--line-soft);border-bottom:1px solid var(--line-soft)}
.info-i:nth-child(4n+1){border-inline-start:0}
.info-l{font-size:10.6px;color:var(--muted);margin-bottom:4px}
.info-v{font-size:12.8px;font-weight:600}
.brief{display:grid;grid-template-columns:repeat(3,1fr);gap:0}
.brief-i{padding:14px 18px;border-inline-start:1px solid var(--line-soft)}
.brief-i:first-child{border-inline-start:0}
.brief-l{display:flex;align-items:center;gap:6px;font-size:10.8px;color:var(--muted);font-weight:600;margin-bottom:6px}
.brief-t{font-size:12.8px;line-height:1.8}
.lens{position:relative;display:flex;align-items:center;justify-content:center;gap:26px;padding:26px 20px;background:linear-gradient(180deg,var(--surface),var(--surface-2));border:1px solid var(--line);border-radius:var(--r);flex-wrap:wrap}
.disc{position:relative;width:214px;height:214px;flex:0 0 auto}
.disc-ring{position:absolute;inset:0;border-radius:99px;background:conic-gradient(var(--verdi),var(--petrol) 30%,var(--brass) 50%,var(--verdi) 74%,var(--petrol));opacity:.85}
.disc-in{position:absolute;inset:5px;border-radius:99px;background:radial-gradient(circle at 32% 26%,var(--petrol-2),var(--petrol));border:1px solid rgba(140,116,72,.45);display:grid;place-items:center;text-align:center;padding:34px}
.disc-dash{position:absolute;inset:22px;border-radius:99px;border:1px dashed rgba(140,116,72,.35)}
.disc-k{color:#9BB6B2;font-size:9.6px;letter-spacing:3px;font-family:'Almarai'}
.disc-n{color:#fff;font-family:'Almarai';font-weight:800;font-size:31px;line-height:1;margin:3px 0 8px}
.disc-line{width:40px;height:1px;background:rgba(140,116,72,.7);margin:0 auto 9px}
.disc-t{color:#DFE9E7;font-size:11.6px;font-family:'Almarai';line-height:1.7}
.gates{display:flex;flex-direction:column;gap:10px;min-width:250px;flex:1 1 250px;max-width:320px}
.gate{display:flex;align-items:center;gap:11px;text-align:start;font-family:inherit;background:var(--surface);border:1px solid var(--line);border-radius:12px;padding:12px 13px;cursor:pointer;transition:.15s}
.gate:hover{border-color:#C6D8D4;transform:translateX(-2px)}
.gate.on{background:var(--petrol);border-color:var(--brass)}
.gate.on .g-t{color:#fff}.gate.on .g-s{color:#9BB6B2}
.g-ic{width:32px;height:32px;border-radius:9px;background:var(--verdi-soft);color:var(--verdi);display:grid;place-items:center;flex:0 0 auto}
.gate.on .g-ic{background:rgba(255,255,255,.09);color:#BFD6D2}
.g-t{font-family:'Almarai';font-weight:700;font-size:14px}
.g-s{font-size:11.4px;color:var(--muted);margin-top:2px}
.g-dot{margin-inline-start:auto;width:9px;height:9px;border-radius:99px}
.ind-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:12px}
.ind{text-align:right;font-family:inherit;background:var(--surface);border:1px solid var(--line);border-radius:12px;padding:14px;cursor:pointer;transition:.15s;display:flex;flex-direction:column;gap:9px}
.ind:hover{border-color:#C6D8D4;background:var(--surface-2)}
.ind.on{border-color:var(--verdi);box-shadow:0 0 0 3px rgba(47,109,104,.09)}
.ind-n{font-size:12.4px;font-weight:600;color:var(--ink-2)}
.ind-v{font-family:'Almarai';font-weight:800;font-size:24px;line-height:1}
.ind-u{font-size:11.6px;color:var(--muted);font-weight:600;margin-right:4px}
.ind-f{display:flex;justify-content:space-between;align-items:center;border-top:1px dashed var(--line-soft);padding-top:8px}
.trend{font-size:11.8px;font-weight:700}
.t-up{color:var(--ok)}.t-down{color:var(--bad)}.t-flat{color:var(--muted)}
.detail{grid-column:1/-1;margin:2px 0 6px;border:1px solid var(--verdi);border-radius:var(--r);overflow:hidden;background:var(--surface);box-shadow:0 10px 26px -20px rgba(18,38,42,.55);animation:fade .25s ease both}
.detail-h{background:var(--verdi-soft);padding:13px 16px;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}
.gswitch{display:inline-flex;border:1px solid var(--line);border-radius:9px;overflow:hidden;margin-bottom:12px}
.gbtn{background:var(--surface);border:0;border-inline-start:1px solid var(--line);padding:7px 14px;font-family:inherit;font-size:11.8px;font-weight:600;color:var(--ink-2);cursor:pointer;display:inline-flex;align-items:center;gap:6px}
.gbtn:first-child{border-inline-start:0}
.gbtn:hover{background:var(--surface-2)}
.gbtn.on{background:var(--petrol);color:#fff}
.gbtn i{font-style:normal;font-size:8.8px;opacity:.6}
.detail-b{padding:16px;display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:18px;min-width:0}
.read{display:flex;gap:20px;flex-wrap:wrap;margin-bottom:12px}
.read div span{display:block;font-size:10.6px;color:var(--muted)}
.read div b{font-family:'Almarai';font-size:17px}
.tw{overflow-x:auto;-webkit-overflow-scrolling:touch;border:1px solid var(--line);border-radius:var(--r);background:var(--surface)}
.tbl{width:100%;border-collapse:collapse;font-size:12.6px;min-width:860px}
.tbl thead th{background:var(--surface-2);color:var(--ink-2);font-weight:600;text-align:right;padding:11px 13px;border-bottom:1px solid var(--line);white-space:nowrap;font-size:11.8px}
.tbl td{padding:11px 13px;border-bottom:1px solid var(--line-soft);vertical-align:middle}
.tbl tr:last-child td{border-bottom:0}
.tbl tr.clk{cursor:pointer}
.tbl tr.clk:hover{background:var(--surface-2)}
.mini{display:inline-block;width:52px;height:6px;background:var(--line-soft);border-radius:99px;overflow:hidden;vertical-align:middle}
.mini>div{height:100%}
.pct{font-size:11.2px;color:var(--ink-2);margin-right:6px}
.lamps{display:flex;gap:4px;flex-wrap:wrap}
.lmp{display:inline-flex;align-items:center;gap:5px;border-radius:99px;padding:2px 8px 2px 6px;font-size:10.6px;font-weight:600;border:1px solid transparent;cursor:pointer;font-family:inherit}
.lmp-dot{width:8px;height:8px;border-radius:99px}
.lmp-red{background:var(--bad-bg);color:#853a31;border-color:#e7cbc7}
.lmp-amber{background:var(--warn-bg);color:#815a17;border-color:#e6d7b8}
.pulse{animation:pl 2.2s ease-in-out infinite}
@keyframes pl{0%,100%{opacity:1}50%{opacity:.35}}
.inp{border:1px solid var(--line);background:var(--surface-2);border-radius:var(--r-sm);padding:9px 11px;font-family:inherit;font-size:12.8px;color:var(--ink);outline:none;width:100%;resize:vertical}
.inp:focus{border-color:var(--verdi);background:#fff}
.ov{position:fixed;inset:0;background:rgba(11,26,29,.5);overflow-y:auto;padding:40px 18px;z-index:70}
.modal{background:var(--surface);border:1px solid var(--line);border-radius:16px;width:100%;max-width:520px;max-height:calc(100vh - 80px);overflow:auto;margin:0 auto}
.modal-h{display:flex;align-items:center;justify-content:space-between;padding:15px 18px;border-bottom:1px solid var(--line);position:sticky;top:0;background:var(--surface)}
.modal-b{padding:18px;display:flex;flex-direction:column;gap:12px}
.field label{display:block;font-size:11.8px;color:var(--ink-2);font-weight:600;margin-bottom:5px}
.chips{display:flex;gap:6px;flex-wrap:wrap}
.chip{border:1px solid var(--line);background:var(--surface-2);border-radius:99px;padding:6px 12px;font-family:inherit;font-size:12px;cursor:pointer;color:var(--ink-2)}
.chip.on{background:var(--petrol);color:#fff;border-color:var(--petrol)}
.acts{display:flex;justify-content:flex-end;gap:9px;margin-top:4px}
.icon{width:32px;height:32px;border-radius:9px;border:1px solid var(--line);background:var(--surface-2);display:grid;place-items:center;cursor:pointer;color:var(--ink-2)}
.cyc{display:inline-flex;align-items:center}
.cyc i{width:8px;height:8px;border-radius:99px;background:var(--line);font-style:normal}
.cyc i.d{background:var(--ok)}.cyc i.n{background:var(--verdi);width:11px;height:11px}
.cyc s{width:14px;height:2px;background:var(--line);text-decoration:none}
.cyc s.d{background:var(--ok)}
.toast{position:fixed;inset-inline-start:18px;bottom:18px;z-index:80;background:var(--petrol);color:#fff;border:1px solid rgba(140,116,72,.5);border-radius:11px;padding:11px 15px;font-size:12.6px}
.empty{text-align:center;color:var(--muted);padding:26px;font-size:12.6px}
.foot{border-top:1px solid var(--line);background:var(--surface)}
.foot-in{max-width:1180px;margin:0 auto;padding:16px 22px;display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;font-size:11.4px;color:var(--muted)}
.verd{color:var(--verdi);font-weight:700;font-family:'Almarai'}
.mwrap{display:flex;flex-direction:column;gap:10px}
.msec{border:1px solid var(--line);border-radius:12px;background:var(--surface)}
.msec.on{border-color:#C6D8D4}
.msec-h{width:100%;display:flex;align-items:center;flex-wrap:wrap;row-gap:6px;gap:11px;padding:15px 16px;background:0;border:0;font-family:inherit;text-align:right;cursor:pointer}
.msec-h:hover{background:var(--surface-2)}
.msec.on .msec-h{border-bottom:1px solid var(--line);background:var(--surface-2)}
.msec-sum{font-size:11.2px;color:var(--muted);display:inline-flex;align-items:center;gap:6px}
.msec-act{margin-inline-start:auto;font-size:11px;color:var(--muted);white-space:nowrap}
.msec-b{padding:6px 16px 16px;overflow-x:auto;-webkit-overflow-scrolling:touch}
.mhead{display:flex;align-items:center;gap:10px;padding-bottom:9px;border-bottom:1px solid var(--line);margin-bottom:2px}
.mtitle{font-family:'Almarai';font-weight:700;font-size:14.5px}
.mcount{font-size:11px;color:var(--muted);background:var(--surface-2);border:1px solid var(--line);border-radius:99px;padding:1px 8px}
.lnk{background:0;border:0;font-family:inherit;font-size:11.8px;font-weight:600;color:var(--ink-2);cursor:pointer;padding:2px 0}
.lnk:hover{color:var(--verdi);text-decoration:underline;text-underline-offset:3px}
.mhead .lnk{margin-inline-start:auto}
.sdot{width:8px;height:8px;border-radius:99px;flex:0 0 auto;display:inline-block}
.cards-list{display:flex;flex-direction:column;gap:12px}
.ocard{border:1px solid var(--line);border-radius:12px;background:var(--surface);overflow:hidden}
.ocard.on{border-color:#C6D8D4;box-shadow:0 8px 24px -20px rgba(18,38,42,.5)}
.ocard-head{width:100%;display:flex;align-items:center;flex-wrap:wrap;gap:12px;padding:14px 15px;background:0;border:0;font-family:inherit;text-align:right;cursor:pointer}
.ocard-head:hover{background:var(--surface-2)}
.ocard.on .ocard-head{border-bottom:1px solid var(--line-soft);background:var(--surface-2)}
.oname{font-family:'Almarai';font-weight:700;font-size:13.6px;flex:1 1 auto;min-width:0}
.ocard-body{padding:14px 15px 16px}
.odesc{font-size:12.6px;color:var(--ink-2);line-height:1.8;margin:0 0 6px;max-width:680px}
.oupd{font-size:10.8px;color:var(--muted);margin-bottom:14px}
.zones{display:grid;grid-template-columns:1.15fr .85fr;gap:18px}
.zone{min-width:0}
.zone-h{display:flex;align-items:center;justify-content:space-between;font-size:11px;color:var(--muted);font-weight:700;letter-spacing:.4px;border-bottom:1px solid var(--line);padding-bottom:7px;margin-bottom:4px}
.nochal{font-size:11.8px;color:var(--muted);padding:14px 2px}
.ctabs{display:flex;gap:6px;margin:10px 0 6px}
.ctab{display:inline-flex;align-items:center;gap:6px;border:1px solid var(--line);background:var(--surface);border-radius:99px;padding:5px 11px;font-family:inherit;font-size:11.2px;font-weight:600;color:var(--ink-2);cursor:pointer}
.ctab b{font-family:'Almarai'}
.ctab.red.on{background:var(--bad-bg);border-color:#e7cbc7;color:#853a31}
.ctab.green.on{background:var(--ok-bg);border-color:#cfe2d6;color:#255f4e}
.ctab.on{background:var(--surface-2);border-color:var(--ink-2);color:var(--ink)}
.chals{display:flex;flex-direction:column}
.chal{border-bottom:1px dashed var(--line-soft);border-inline-start:2px solid transparent;padding-inline-start:8px}
.chal.opened{border-inline-start-color:var(--bad)}
.chal.closed{border-inline-start-color:var(--ok);opacity:.62}
.chal-dept{font-size:10.2px;font-weight:700;color:var(--ink-2);background:var(--surface-2);border:1px solid var(--line);border-radius:6px;padding:2px 7px;white-space:nowrap}
.chal-head{width:100%;display:flex;align-items:center;gap:9px;padding:10px 2px;background:0;border:0;font-family:inherit;text-align:right;cursor:pointer;flex-wrap:wrap}
.chal-head:hover{background:var(--surface-2)}
.chal-t{font-size:12.2px;font-weight:600;flex:1 1 auto;min-width:0}
.chal-b{padding:0 16px 12px}
.chal-need{font-size:12.2px;color:var(--ink-2);margin-bottom:5px}
.vtag{font-size:10.4px;font-weight:700;border-radius:6px;padding:2px 8px;white-space:nowrap}
.v-green{background:var(--ok-bg);color:#255f4e}.v-yellow{background:var(--warn-bg);color:#815a17}
.v-red{background:var(--bad-bg);color:#853a31}.v-gray{background:var(--gray-bg);color:#65726f}
.nostn{text-align:center;padding:16px;border:1px dashed var(--line);border-radius:10px;background:var(--surface-2)}
.nostn p{font-size:12.6px;color:var(--muted);margin:0 0 12px}
.sugg{display:flex;gap:6px;flex-wrap:wrap;justify-content:center;margin-bottom:14px}
.sugg .fchip{border:1px solid var(--line);background:var(--surface)}
.idx{width:22px;height:22px;border-radius:6px;background:var(--surface-2);border:1px solid var(--line);color:var(--ink-2);font-size:11px;font-weight:700;display:grid;place-items:center;flex:0 0 auto}
.mname{font-size:13.2px;font-weight:600;flex:1 1 160px;min-width:0;overflow-wrap:anywhere}
.mbar{width:74px;height:3px;background:var(--line);border-radius:99px;overflow:hidden;flex:0 0 auto}
.mbar.sm{width:56px}
.mbar>span{display:block;height:100%;border-radius:99px;transition:width .4s}
.mpct{font-size:11px;color:var(--ink-2);min-width:30px}
.mlamp{display:inline-flex;align-items:center;gap:5px;background:0;border:0;font-family:inherit;font-size:10.8px;font-weight:600;color:var(--ink-2);cursor:pointer;padding:2px 0}
.mlamp:hover{color:var(--ink)}
.mlamp b{font-weight:700}
.mlamp .why{color:var(--muted);font-weight:400;max-width:210px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.mlamp i{font-style:normal;font-size:9.6px;color:var(--muted)}
.chev{width:16px;text-align:center;color:var(--muted);font-size:14px;flex:0 0 auto;margin-inline-start:auto}
.stns{display:flex;flex-direction:column}
.stn{display:flex;align-items:center;flex-wrap:wrap;row-gap:7px;gap:12px;padding:9px 2px;border-bottom:1px dashed var(--line-soft);min-width:0}
.stn:last-child{border-bottom:0}
.stn-n{font-size:12.2px;font-weight:600;flex:1 1 150px;min-width:0;overflow-wrap:anywhere}
.stn-act{display:flex;gap:13px;align-items:center;margin-inline-start:auto;flex-wrap:wrap}
.fchip{background:0;border:1px solid transparent;border-radius:99px;padding:4px 10px;font-family:inherit;font-size:11.2px;color:var(--muted);cursor:pointer;display:inline-flex;gap:5px;align-items:center}
.fchip:hover{color:var(--ink)}
.fchip.on{background:var(--petrol);color:#fff}
.mreplies{border-top:1px dashed var(--line);margin-top:4px;padding-top:9px;display:flex;flex-direction:column;gap:8px}
.mreplies>div{font-size:12.2px;color:var(--ink-2);line-height:1.7}
.mreplies span{display:block;font-size:10.2px;color:var(--muted);margin-bottom:2px}
.mlinks{display:flex;gap:16px;margin-top:10px}
@media(max-width:820px){.zones{grid-template-columns:1fr}}
@media(max-width:1020px){.grid-cards{grid-template-columns:repeat(2,1fr)}.ind-grid{grid-template-columns:repeat(3,1fr)}.info{grid-template-columns:repeat(2,1fr)}.info-i:nth-child(2n+1){border-inline-start:0}.detail-b{grid-template-columns:1fr}.brief{grid-template-columns:1fr}.brief-i{border-inline-start:0;border-top:1px solid var(--line-soft)}.brief-i:first-child{border-top:0}}
@media(max-width:680px){.wrap{padding:16px 13px 46px}.grid-cards,.ind-grid{grid-template-columns:1fr}.lens{padding:18px 14px}.gates{max-width:none}.card .pad,.msec-b{padding-inline:12px}.mbar{width:56px}.mbar.sm{width:44px}.meta{gap:16px}.top-in{flex-wrap:wrap;padding:10px 14px;gap:9px}.brand-name{font-size:13.5px}.brand-sub{font-size:10px}.hd-actions{width:100%;justify-content:space-between;gap:8px}.nav{flex:1 1 auto;overflow-x:auto;flex-wrap:nowrap;scrollbar-width:none;-webkit-overflow-scrolling:touch}.nav::-webkit-scrollbar{display:none}.nav button{white-space:nowrap;padding:7px 9px;font-size:12px}.switcher{flex:0 0 auto;padding:3px 4px 3px 8px;gap:5px}.switcher span{display:none}.switcher select{max-width:38vw}}
.actbar{display:flex;gap:8px;flex-wrap:wrap;margin:2px 0 14px;padding-bottom:14px;border-bottom:1px solid var(--line-soft)}
.actbtn{display:inline-flex;align-items:center;gap:7px;height:34px;padding:0 13px;border-radius:var(--r-sm);font-family:inherit;font-size:12.4px;font-weight:600;cursor:pointer;border:1px solid var(--line);background:var(--surface);color:var(--ink-2);transition:.15s;white-space:nowrap}
.actbtn:hover{background:var(--surface-2);border-color:#C6D8D4;color:var(--ink)}
.actbtn svg{color:var(--verdi)}
.actbtn.upd svg{color:var(--brass)}
.actbtn.chal svg{color:var(--bad)}
.actbtn.edit svg{color:var(--verdi)}
.actbtn.del{color:#853a31;border-color:#e7cbc7}
.actbtn.del:hover{background:var(--bad-bg);border-color:#d9b3ad;color:#6f2f27}
.actbtn.del svg{color:var(--bad)}
.dngr{background:var(--bad);color:#fff;border-color:var(--bad)}
.dngr:hover{background:#8f3a30}
.lnk.del:hover{color:var(--bad)}
.upd-log{display:flex;flex-direction:column;gap:8px;margin:2px 0 4px}
.upd-item{border-inline-start:2px solid var(--verdi);padding:6px 10px;background:var(--surface-2);border-radius:0 8px 8px 0}
.upd-item .u-m{font-size:10.2px;color:var(--muted);margin-bottom:3px}
.upd-item .u-t{font-size:12.4px;line-height:1.7;color:var(--ink-2)}
.pri{display:inline-flex;align-items:center;gap:4px;font-size:10.4px;font-weight:700;border-radius:6px;padding:2px 7px;white-space:nowrap}
.pri-low{background:var(--gray-bg);color:#65726f}.pri-mid{background:var(--warn-bg);color:#815a17}
.pri-high{background:#F3E2DD;color:#8a3f31}.pri-crit{background:#8a2f26;color:#fff}
.lvl{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:700;color:var(--verdi);background:var(--verdi-soft);border:1px solid #CBDFDB;border-radius:6px;padding:2px 7px;white-space:nowrap}
.setg-sec{border:1px solid var(--line);border-radius:12px;background:var(--surface);margin-bottom:12px;overflow:hidden}
.setg-sec-h{width:100%;display:flex;align-items:center;gap:10px;padding:13px 16px;background:var(--surface-2);border:0;border-bottom:1px solid var(--line);font-family:inherit;text-align:right;cursor:pointer}
.setg-sec-h .mtitle{flex:1}
.setg-sec-b{padding:6px 16px 14px}
.setg-card{border:1px solid var(--line-soft);border-radius:10px;padding:13px 14px;margin-top:11px;background:var(--surface)}
.setg-name{font-family:'Almarai';font-weight:700;font-size:13.4px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.setg-desc{font-size:12px;color:var(--ink-2);line-height:1.7;margin:5px 0 8px}
.usemap{display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:11px;color:var(--muted);background:var(--surface-2);border:1px dashed var(--line);border-radius:8px;padding:6px 10px;margin-bottom:9px}
.usemap b{color:var(--verdi);font-weight:700}
.usemap svg{flex:0 0 auto}
.crumb{display:inline-flex;align-items:center;gap:5px;flex-wrap:wrap}
.crumb i{font-style:normal;color:var(--brass)}
.vals{display:flex;gap:6px;flex-wrap:wrap;align-items:center}
.val{display:inline-flex;align-items:center;gap:6px;background:var(--surface-2);border:1px solid var(--line);border-radius:99px;padding:3px 5px 3px 10px;font-size:11.6px;color:var(--ink-2)}
.val button{background:0;border:0;color:var(--muted);cursor:pointer;font-size:13px;line-height:1;padding:0 2px}
.val button:hover{color:var(--bad)}
.val-add{display:inline-flex;gap:5px;align-items:center}
.val-add input{width:120px;height:28px;padding:0 8px;border:1px solid var(--line);border-radius:99px;font-family:inherit;font-size:11.6px;background:var(--surface)}
.search-in{display:flex;align-items:center;gap:8px;border:1px solid var(--line);background:var(--surface-2);border-radius:var(--r-sm);padding:0 12px;height:38px;margin-bottom:14px}
.search-in input{flex:1;border:0;background:0;outline:none;font-family:inherit;font-size:13px;color:var(--ink)}
.urole{display:inline-flex;align-items:center;gap:5px;font-size:10.6px;font-weight:700;border-radius:6px;padding:2px 8px;white-space:nowrap}
.ur-admin{background:#2f2a3d;color:#e7e2f5}.ur-dept{background:var(--verdi);color:#eaf3f1}
.ur-enable{background:#3a2f1f;color:#f3e7cf}
.ur-product{background:var(--verdi-soft);color:var(--verdi)}.ur-project{background:var(--warn-bg);color:#815a17}
.switcher{display:inline-flex;align-items:center;gap:7px;background:rgba(255,255,255,.06);border:1px solid rgba(140,116,72,.4);border-radius:99px;padding:3px 4px 3px 11px}
.switcher span{color:#9BB6B2;font-size:10.6px;white-space:nowrap}
.switcher select{background:var(--petrol-2);color:#F1F5F4;border:1px solid rgba(255,255,255,.12);border-radius:99px;font-family:inherit;font-size:11.6px;font-weight:600;padding:5px 9px;cursor:pointer;max-width:150px}
.gate-note{display:flex;align-items:flex-start;gap:10px;background:var(--warn-bg);border:1px solid #e6d7b8;border-radius:12px;padding:13px 15px;font-size:12.4px;color:#7a5713;line-height:1.7}
.denied{text-align:center;padding:50px 20px}
.denied .lock{width:54px;height:54px;border-radius:50%;background:var(--bad-bg);color:var(--bad);display:grid;place-items:center;margin:0 auto 14px}
.tog{position:relative;width:40px;height:22px;border-radius:99px;border:0;cursor:pointer;background:var(--line);transition:.2s;flex:0 0 auto}
.tog.on{background:var(--ok)}
.tog::after{content:"";position:absolute;top:2px;inset-inline-start:2px;width:18px;height:18px;border-radius:50%;background:#fff;transition:.2s}
.tog.on::after{inset-inline-start:20px}
.reqmsg{background:var(--bad-bg);border:1px solid #e7cbc7;color:#853a31;border-radius:9px;padding:8px 11px;font-size:11.8px;font-weight:600}
.link-picker{display:flex;flex-direction:column;gap:7px}
.lp-opt{display:flex;align-items:center;gap:9px;border:1px solid var(--line);border-radius:10px;padding:10px 12px;cursor:pointer;font-size:12.6px}
.lp-opt.on{border-color:var(--verdi);background:var(--verdi-soft)}
.lp-opt input{margin:0}
`;
function Style() { useEffect(() => { const e = document.createElement("style"); e.textContent = CSS; document.head.appendChild(e); return () => document.head.removeChild(e); }, []); return null; }

const I = {
  scale: (s = 18) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M12 3v18M6 7h12M8 7l-3 6h6zM16 7l-3 6h6z" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  wallet: (s = 17) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="6" width="18" height="13" rx="3" /><path d="M16 12h3" /></svg>,
  pulse: (s = 17) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 12h4l3-8 4 16 3-8h4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  cube: (s = 17) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 3l7 4v10l-7 4-7-4V7z" strokeLinejoin="round" /><path d="M12 12l7-4M12 12v9M12 12L5 8" opacity=".6" /></svg>,
  bulb: (s = 17) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" /></svg>,
  clock: (s = 17) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" strokeLinecap="round" /></svg>,
  back: () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 6l-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  note: (s = 15) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 5h16M4 10h16M4 15h10" strokeLinecap="round" /></svg>,
  flag: (s = 15) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 21V4m0 0h11l-2 4 2 4H5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  step: (s = 15) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 18h5v-5H4zM10 13h5V8h-5zM16 8h4V3h-4z" strokeLinejoin="round" /></svg>,
  edit: (s = 14) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 20h4l10-10-4-4L4 16zM14 6l4 4" strokeLinejoin="round" /></svg>,
  user: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" strokeLinecap="round" /></svg>,
  users: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="9" cy="8" r="3.4" /><path d="M3 20c0-3.3 3-5 6-5s6 1.7 6 5" /><path d="M16 5.2a3.4 3.4 0 0 1 0 6.4M17 15c2.4.5 4 2.2 4 5" opacity=".65" /></svg>,
  search: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" strokeLinecap="round" /></svg>,
  lock: (s = 24) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
  loc: (s = 13) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 21s7-6 7-11a7 7 0 0 0-14 0c0 5 7 11 7 11z" /><circle cx="12" cy="10" r="2.4" /></svg>,
  trash: (s = 14) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" strokeLinecap="round" strokeLinejoin="round" /></svg>,
};
const Seal = ({ big }) => (
  <div className={`seal ${big ? "seal-big" : ""}`}>
    <svg viewBox="0 0 100 100" width="100%" height="100%">
      <circle cx="50" cy="50" r="47" fill="none" stroke={T.brass} strokeWidth="1.6" />
      <circle cx="50" cy="50" r="41" fill={T.petrol} stroke={T.verdi} strokeWidth="1.4" />
      {Array.from({ length: 24 }).map((_, i) => <line key={i} x1="50" y1="6" x2="50" y2="10" stroke={T.brass} strokeWidth="1.3" opacity={i % 2 ? .3 : .75} transform={`rotate(${i * 15} 50 50)`} />)}
      <text x="50" y="53" textAnchor="middle" fontSize="24" fontWeight="800" fill="#DCEAE7" fontFamily="Almarai">360</text>
      <text x="50" y="67" textAnchor="middle" fontSize="7.6" fill={T.brass} fontFamily="Almarai" letterSpacing="1">MATURITY</text>
    </svg>
  </div>
);

const DEPTS = ["التقنية", "المالية", "المشتريات", "القانونية", "العميل / الوزارة", "إدارة المنتج", "التشغيل", "البيانات", "الحوكمة / الاعتماد"];
const DEV_STD = [
  { name: "قيد التحليل", gate: "وثيقة متطلبات معتمدة ونطاق واضح", ev: "تقرير مختصر" },
  { name: "قيد التطوير", gate: "بناء مكتمل ومراجعة كود", ev: "رابط شاشة" },
  { name: "قيد الربط", gate: "تكامل ناجح مع الأنظمة ذات العلاقة", ev: "رابط شاشة" },
  { name: "قيد الاختبار", gate: "اجتياز اختبار الأعمال دون ملاحظات حرجة", ev: "تقرير مختصر" },
  { name: "تم الإطلاق", gate: "نشر في الإنتاج وتفعيل للمستخدمين", ev: "Screenshot" },
  { name: "مغلق", gate: "التطوير أُنجز — ينتقل إلى قياس الأثر", ev: "مؤشر قبل / بعد" },
];
const FATES = ["قيد الدراسة", "بانتظار قرار", "تتحول إلى تطوير", "مغلقة", "فشلت", "مجمدة"];
const fmt = (n) => Number(n).toLocaleString("en-US");
const st = (pairs) => pairs.map(([name, progress, owner]) => ({
  name, progress, owner: owner || "إدارة المنتج",
  status: progress === 100 ? "green" : progress > 0 ? "yellow" : "gray",
}));

const IND = (o) => ({ history: [], ...o });
const sr = (labels, vals) => labels.map((x, i) => ({ x, v: vals[i] }));

const KH_FIN = [
  IND({ id: "f1", name: "الإيراد الفعلي", value: 1780000, unit: "﷼", prev: 1633000, delta: 9, good: "up", gran: "شهري", chart: "area", source: "بوابة الدفع", method: "API", by: "النظام", at: "08-07 · 09:12", series: sr(["فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو"], [1420000, 1510000, 1495000, 1602000, 1633000, 1780000]), history: [{ d: "08-07", v: "1,780,000 ﷼", by: "النظام", src: "API", note: "مزامنة تلقائية" }, { d: "01-07", v: "1,633,000 ﷼", by: "النظام", src: "API", note: "إقفال يونيو" }] }),
  IND({ id: "f2", name: "المستهدف الشهري", value: 2400000, unit: "﷼", prev: 2400000, delta: 0, good: "up", gran: "شهري", chart: "bar", source: "خطة الإدارة", method: "يدوي", by: "أ. نورة الشمري", at: "01-07 · 11:20", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [2200000, 2200000, 2400000, 2400000]), history: [{ d: "01-07", v: "2,400,000 ﷼", by: "أ. نورة الشمري", src: "يدوي", note: "اعتماد مستهدف يوليو" }] }),
  IND({ id: "f3", name: "نسبة تحقيق المستهدف", value: 74, unit: "%", prev: 68, delta: 6, good: "up", gran: "أسبوعي", chart: "line", source: "محتسب", method: "API", by: "النظام", at: "08-07 · 09:12", series: sr(["أ1", "أ2", "أ3", "أ4", "أ5", "أ6", "أ7"], [9, 19, 30, 41, 53, 64, 74]), history: [{ d: "08-07", v: "74%", by: "النظام", src: "API", note: "—" }] }),
  IND({ id: "f4", name: "الإيراد حسب الخدمة", value: "ندب خبير 46%", unit: "", prev: "44%", delta: 2, good: "up", gran: "شهري", chart: "barh", source: "بوابة الدفع", method: "API", by: "النظام", at: "08-07", series: [{ x: "ندب خبير", v: 820000 }, { x: "مراجعة فنية", v: 340000 }, { x: "خبرة أعمال", v: 265000 }, { x: "مركز خبرة", v: 210000 }, { x: "تقارير إضافية", v: 145000 }], history: [{ d: "08-07", v: "5 خدمات", by: "النظام", src: "API", note: "تركّز 46% في ندب خبير" }] }),
  IND({ id: "f5", name: "الخصومات والحسميات", value: 60000, unit: "﷼", prev: 57000, delta: 5, good: "down", gran: "شهري", chart: "barh", source: "المالية", method: "يدوي", by: "م. عبدالله الحارثي", at: "07-07 · 16:40", series: [{ x: "حسم جودة التقرير", v: 26000 }, { x: "استرداد رسوم", v: 19000 }, { x: "حسم اتفاقية جهة", v: 15000 }], history: [{ d: "07-07", v: "60,000 ﷼", by: "م. عبدالله الحارثي", src: "يدوي", note: "تصحيح حسم اتفاقية" }] }),
  IND({ id: "f6", name: "العمليات المدفوعة", value: 1248, unit: "", prev: 1162, delta: 7, good: "up", gran: "أسبوعي", chart: "line", source: "بوابة الدفع", method: "API", by: "النظام", at: "08-07", series: sr(["أ1", "أ2", "أ3", "أ4", "أ5", "أ6", "أ7"], [147, 172, 188, 177, 197, 183, 184]), history: [{ d: "08-07", v: "1,248", by: "النظام", src: "API", note: "—" }] }),
  IND({ id: "f7", name: "متوسط قيمة العملية", value: 1426, unit: "﷼", prev: 1405, delta: 1, good: "up", gran: "شهري", chart: "line", source: "محتسب", method: "API", by: "النظام", at: "08-07", series: sr(["مارس", "أبريل", "مايو", "يونيو", "يوليو"], [1380, 1392, 1401, 1405, 1426]), history: [{ d: "08-07", v: "1,426 ﷼", by: "النظام", src: "API", note: "—" }] }),
];
const KH_OPS = [
  IND({ id: "o1", name: "عدد الطلبات", value: 212, unit: "/ اليوم", prev: 198, delta: 12, good: "up", gran: "يومي", chart: "line", source: "منصة الطلبات", method: "API", by: "النظام", at: "08-07 · 07:00", series: sr(["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"], [178, 240, 226, 231, 258, 96, 183]), history: [{ d: "08-07", v: "212", by: "النظام", src: "API", note: "مزامنة يومية" }] }),
  IND({ id: "o2", name: "العمليات المكتملة", value: 1187, unit: "/ الأسبوع", prev: 1089, delta: 9, good: "up", gran: "أسبوعي", chart: "line", source: "منصة الطلبات", method: "API", by: "النظام", at: "08-07", series: sr(["أ1", "أ2", "أ3", "أ4", "أ5", "أ6", "أ7"], [880, 940, 1010, 980, 1080, 1120, 1187]), history: [{ d: "08-07", v: "1,187", by: "النظام", src: "API", note: "—" }] }),
  IND({ id: "o3", name: "العمليات المتعثرة", value: 127, unit: "/ الأسبوع", prev: 85, delta: 50, good: "down", gran: "أسبوعي", chart: "area", source: "احتساب يدوي", method: "يدوي", by: "أ. سارة الدوسري", at: "06-07 · 14:10", series: sr(["أ1", "أ2", "أ3", "أ4", "أ5", "أ6", "أ7"], [71, 71, 68, 70, 72, 94, 127]), history: [{ d: "06-07", v: "127", by: "أ. سارة الدوسري", src: "يدوي", note: "تعريف التعثر غير معتمد" }] }),
  IND({ id: "o4", name: "متوسط زمن الإنجاز", value: 9.4, unit: "يوم", prev: 9.8, delta: -4, good: "down", gran: "شهري", chart: "line", source: "مصدران متعارضان", method: "يدوي", by: "أ. سارة الدوسري", at: "06-07", series: sr(["مارس", "أبريل", "مايو", "يونيو", "يوليو"], [11.2, 10.6, 10.1, 9.8, 9.4]), history: [{ d: "06-07", v: "9.4 يوم", by: "أ. سارة الدوسري", src: "يدوي", note: "بانتظار اعتماد مصدر البيانات" }] }),
  IND({ id: "o5", name: "الالتزام بالـ SLA", value: 88, unit: "%", prev: 90, delta: -2, good: "up", gran: "شهري", chart: "line", source: "Dashboard عمليات", method: "API", by: "النظام", at: "08-07", series: sr(["مارس", "أبريل", "مايو", "يونيو", "يوليو"], [92, 91, 90, 90, 88]), history: [{ d: "08-07", v: "88%", by: "النظام", src: "API", note: "—" }] }),
  IND({ id: "o6", name: "الشكاوى", value: 37, unit: "/ الأسبوع", prev: 34, delta: 9, good: "down", gran: "أسبوعي", chart: "bar", source: "مركز الاتصال", method: "يدوي", by: "أ. سارة الدوسري", at: "06-07", series: sr(["أ4", "أ5", "أ6", "أ7"], [28, 31, 34, 37]), history: [{ d: "06-07", v: "37", by: "أ. سارة الدوسري", src: "يدوي", note: "معظمها زمن اختيار الخبير" }] }),
  IND({ id: "o7", name: "الاقتراحات", value: 14, unit: "/ الأسبوع", prev: 13, delta: 3, good: "up", gran: "أسبوعي", chart: "bar", source: "مركز الاتصال", method: "يدوي", by: "أ. سارة الدوسري", at: "06-07", series: sr(["أ4", "أ5", "أ6", "أ7"], [9, 11, 13, 14]), history: [] }),
  IND({ id: "o8", name: "جودة الخدمة", value: 81, unit: "%", prev: 79, delta: 2, good: "up", gran: "شهري", chart: "line", source: "استبيان ما بعد الخدمة", method: "يدوي", by: "أ. سارة الدوسري", at: "06-07", series: sr(["مارس", "أبريل", "مايو", "يونيو", "يوليو"], [76, 78, 79, 79, 81]), history: [] }),
];

const KHIBRAH = {
  id: "khibrah", name: "خبرة", has360: "full", status: "yellow",
  desc: "منصة الخبرة القضائية الذكية: من سؤال القاضي إلى التقرير والحكم والتعلم.",
  owner: "م. عبدالله الحارثي", entity: "الإدارة العامة للخبرة",
  start: "02-2024", stage: "تشغيل وتوسع", target: "12-2026",
  revenue: 1780000, finTarget: 2400000, method: "API", updated: "08-07-2026", lastBy: "النظام",
  brief: { blocker: "الربط مع تقاضي متوقف منذ 26 يومًا — قدرة «نقد نطاق الندب» عالقة عند الاختبار.", decision: "اعتماد نموذج التسعير لفرصة «خبرة أعمال» قبل 20-07.", change: "التعثر ارتفع من 6% إلى 9% رغم نمو الطلبات 12%." },
  fin: KH_FIN, ops: KH_OPS,
  opps: [
    { id: "OP1", name: "خبرة أعمال", desc: "توسيع المنتج لخدمة قطاعات الأعمال.", start: "12-04", stage: "اعتماد نموذج العمل", progress: 55, status: "yellow", owner: "مدير المنتج", due: "20-07", fate: "بانتظار قرار", decision: "اعتماد نموذج التسعير", subs: st([["بناء ملف الفرصة",100,"إدارة المنتج"],["تحليل نموذج العمل",100,"المالية"],["تقدير الإيراد المتوقع",100,"المالية"],["مواءمة مع الوزارة",70,"العميل / الوزارة"],["عرض على المدير",40,"إدارة المنتج"],["اعتماد نموذج التسعير",10,"المالية"]]) },
    { id: "OP2", name: "مركز خبرة للقضايا عالية الحساسية", desc: "مسار مستقل بضوابط أعلى للقضايا ذات القيمة المرتفعة.", start: "03-03", stage: "نموذج تشغيل", progress: 60, status: "yellow", owner: "التشغيل", due: "21-07", fate: "بانتظار قرار", decision: "تحديد عتبات الدخول", subs: st([["تحديد نموذج التشغيل",100,"التشغيل"],["دراسة الطاقة الاستيعابية",100,"التشغيل"],["تقدير الإيراد",85,"المالية"],["تحديد عتبات الدخول",40,"التشغيل"],["اعتماد الحوكمة",20,"الحوكمة / الاعتماد"],["إطلاق تجريبي",0,"إدارة المنتج"]]) },
    { id: "OP3", name: "مراجعة فنية مدفوعة", desc: "مراجعة التقرير بعد الحكم مقابل رسوم، مع ردّها عند ثبوت الخلل.", start: "28-05", stage: "دراسة مالية", progress: 35, status: "red", owner: "المالية", due: "31-07", fate: "مجمدة", decision: "اعتماد السياسة المالية", subs: st([["بناء ملف الفرصة",100,"إدارة المنتج"],["دراسة الكلفة والتسعير",60,"المالية"],["رأي قانوني في رد الرسوم",25,"القانونية"],["اعتماد السياسة المالية",0,"المالية"],["إطلاق تجريبي",0,"إدارة المنتج"]]) },
  ],
  devs: [
    { id: "DV1", name: "نقد نطاق الندب بالذكاء", desc: "يراجع الذكاء نطاق الندب قبل طرحه ويقترح صياغة أوضح للقاضي.", start: "02-03", due: "16-07", stage: 3, progress: 70, status: "yellow", owner: "التقنية", decision: "دعم تكامل تقاضي", evidence: ["لقطة النموذج", "رابط بيئة الاختبار"], serves: ["الوزارة", "المستخدم الداخلي", "البيانات", "الأثر"] },
    { id: "DV2", name: "تحويل النطاق إلى سؤال قابل للحكم", desc: "صياغة النطاق كسؤال محدد يجيب عليه الخبير ويحكم به القاضي.", start: "20-03", due: "30-07", stage: 1, progress: 55, status: "yellow", owner: "إدارة المنتج", decision: "—", evidence: [], serves: ["الوزارة", "المستفيد النهائي"] },
    { id: "DV3", name: "حفظ سلسلة النطاق", desc: "توثيق كل تعديل على النطاق بمعيار اكتمال قابل للتدقيق.", start: "05-04", due: "12-08", stage: 0, progress: 30, status: "yellow", owner: "البيانات", decision: "اعتماد قاموس البيانات", evidence: [], serves: ["الحوكمة", "البيانات"] },
    { id: "DV4", name: "بناء ملف القضية الذكي", desc: "تجميع مستندات القضية وتلخيصها آليًا للخبير.", start: "10-02", due: "05-08", stage: 1, progress: 60, status: "green", owner: "التقنية", decision: "—", evidence: ["نموذج تلخيص 50 قضية"], serves: ["المستخدم الداخلي", "البيانات"] },
    { id: "DV5", name: "الترشيح الذكي للخبراء", desc: "مطابقة تخصص الخبير وسجل أدائه مع نطاق الندب.", start: "12-01", due: "14-07", stage: 3, progress: 88, status: "yellow", owner: "إدارة المنتج", decision: "ربط بمؤشر قبل الإغلاق", evidence: ["تقرير A/B"], serves: ["الشركاء", "مزودو الخدمة", "العمليات"] },
    { id: "DV6", name: "بطاقة عرض الخبير للأطراف", desc: "عرض مبسّط للأطراف يوضح الخبير وأتعابه ومنهجيته.", start: "01-02", due: "02-06", stage: 4, progress: 100, status: "red", owner: "إدارة المنتج", decision: "رفع Evidence بعد الإطلاق", evidence: [], serves: ["المستفيد النهائي", "الشركاء"] },
    { id: "DV7", name: "متابعة التنفيذ بندًا بندًا", desc: "لوحة متابعة لبنود خطة الخبير مع إنذار مبكر.", start: "08-04", due: "15-08", stage: 1, progress: 50, status: "yellow", owner: "التشغيل", decision: "اعتماد مصدر زمن الإجراء", evidence: [], serves: ["العمليات", "البيانات"] },
    { id: "DV8", name: "مركز خبرة", desc: "وحدة متخصصة للقضايا عالية الحساسية بمسار مستقل.", start: "03-03", due: "21-07", stage: 0, progress: 25, status: "red", owner: "التشغيل", decision: "تحديد عتبات الدخول", evidence: [], serves: ["الوزارة", "العمليات", "المالية"] },
  ],
  impact: [
    { id: "IM1", name: "نقد نطاق الندب بالذكاء", launched: "16-07", cycle: 1, rounds: "2 من 4", expected: "تحسين جودة نطاق الندب", result: "أثر جزئي", status: "yellow", evidence: ["Screenshot", "رقم استخدام: 320"], kpi: "نسبة قبول القاضي لمقترح الذكاء", kpiLinked: true, prev: "58%", curr: "63%", next: "22-07", owner: "إدارة المنتج", decision: "تمديد دورة القياس", late: false },
    { id: "IM2", name: "Dashboard مالي", launched: "10-04", cycle: 3, rounds: "4 من 4", expected: "وضوح الإيراد حسب الخدمة", result: "أثر مثبت", status: "green", evidence: ["رابط اللوحة", "مؤشر قبل / بعد"], kpi: "وضوح الإيراد حسب الخدمة", kpiLinked: true, prev: "71%", curr: "94%", next: "05-08", owner: "المالية", decision: "إغلاق كمؤشر دائم", late: false },
    { id: "IM3", name: "بطاقة عرض الخبير للأطراف", launched: "02-06", cycle: 0, rounds: "1 من 4", expected: "خفض الاعتراض على الخبير", result: "أثر غير واضح", status: "red", evidence: [], kpi: "—", kpiLinked: false, prev: "—", curr: "—", next: "متأخر", owner: "إدارة المنتج", decision: "رفع Evidence", late: true },
  ],
  cases: [
    { id: "C-01", sec: "opps", rowId: "OP1", dept: "المالية", title: "اعتماد نموذج التسعير", cause: "لم تُعتمد آلية التسعير ورسوم الخدمة الجديدة.", need: "اعتماد التسعير أو نموذج بديل.", owner: "المالية / مدير المنتج", decision: true, state: "بانتظار قرار", updated: "05-07", due: "20-07", replies: [{ w: "مدير المنتج", d: "18-06", t: "رُفع مقترح بثلاث شرائح." }, { w: "المالية", d: "05-07", t: "الشريحة الثالثة تحتاج مراجعة هامش." }] },
    { id: "C-02", sec: "opps", rowId: "OP1", dept: "العميل / الوزارة", title: "بانتظار مواءمة الجهة", cause: "لم تُحدَّد جهة التعاقد داخل الوزارة.", need: "تحديد ممثل رسمي.", owner: "إدارة المنتج", decision: false, state: "قيد المعالجة", updated: "02-07", due: "18-07", replies: [] },
    { id: "C-03", sec: "opps", rowId: "OP2", dept: "الحوكمة / الاعتماد", title: "تحديد عتبات مركز خبرة", cause: "لا معيار لتصنيف القضية كعالية الحساسية.", need: "اعتماد ثلاث عتبات: القيمة، النوع، جهة الإحالة.", owner: "التشغيل / الحوكمة", decision: true, state: "بانتظار قرار", updated: "04-07", due: "21-07", replies: [{ w: "التشغيل", d: "04-07", t: "قُدّم مقترح العتبات." }] },
    { id: "C-04", sec: "opps", rowId: "OP3", dept: "القانونية", title: "سياسة رد الرسوم تحتاج رأيًا نظاميًا", cause: "لا سند نظامي لرد الرسوم عند ثبوت الخلل.", need: "مذكرة من الإدارة القانونية.", owner: "القانونية", decision: true, state: "بانتظار قرار", updated: "06-07", due: "31-07", replies: [] },
    { id: "C-05", sec: "opps", rowId: "OP3", dept: "المالية", title: "الدراسة المالية غير مكتملة", cause: "لم تُقدَّر كلفة المراجعة الفنية.", need: "استكمال الدراسة قبل العرض على المدير.", owner: "المالية", decision: false, state: "مفتوح", updated: "01-07", due: "25-07", replies: [] },
    { id: "C-06", sec: "devs", rowId: "DV1", dept: "التقنية", title: "جاهزية تكامل تقاضي", cause: "لم تُؤكَّد جاهزية واجهة التكامل ولم تُفتح صلاحيات النقل.", need: "دعم التكامل وتحديد عينة اختبار.", owner: "التقنية", decision: true, state: "بانتظار قرار", updated: "07-07", due: "16-07", replies: [{ w: "التقنية", d: "01-07", t: "اختبار جزئي ناجح على 40 طلبًا." }] },
    { id: "C-07", sec: "devs", rowId: "DV3", dept: "البيانات", title: "نقص مصادر البيانات", cause: "مصدران متعارضان لزمن الإجراء.", need: "اعتماد قاموس البيانات ومصدر الحقيقة.", owner: "البيانات", decision: true, state: "قيد المعالجة", updated: "03-07", due: "24-07", replies: [{ w: "البيانات", d: "03-07", t: "مسودة القاموس جاهزة." }] },
    { id: "C-08", sec: "devs", rowId: "DV5", dept: "إدارة المنتج", title: "عدم ارتباط التطوير بـ KPI", cause: "التطوير يقترب من الإطلاق دون ربطه بمؤشر.", need: "اختيار مؤشر أو إنشاء مؤشر جديد.", owner: "مدير المنتج", decision: false, state: "مفتوح", updated: "04-07", due: "14-07", replies: [] },
    { id: "C-09", sec: "devs", rowId: "DV7", dept: "التشغيل", title: "تحديد مالك التشغيل", cause: "لا جهة مسؤولة عن متابعة بنود خطة الخبير.", need: "تعيين مالك تشغيلي.", owner: "التشغيل", decision: false, state: "مفتوح", updated: "28-06", due: "18-07", replies: [] },
    { id: "C-10", sec: "devs", rowId: "DV8", dept: "المشتريات", title: "تعاقد مزودي الخبرة المتخصصة", cause: "لا اتفاقية إطارية مع بيوت الخبرة المتخصصة.", need: "طرح منافسة أو أمر شراء مباشر.", owner: "المشتريات", decision: true, state: "بانتظار قرار", updated: "05-07", due: "28-07", replies: [] },
    { id: "C-11", sec: "devs", rowId: "DV6", dept: "إدارة المنتج", title: "نقص Evidence بعد الإطلاق", cause: "لم تُرفق لقطات أو أرقام استخدام بعد شهر.", need: "رفع تقرير استخدام + مؤشر قبل/بعد.", owner: "إدارة المنتج", decision: false, state: "قيد المعالجة", updated: "06-07", due: "14-07", replies: [] },
    { id: "C-12", sec: "impact", rowId: "IM1", dept: "البيانات", title: "عينة القياس غير كافية", cause: "320 عملية فقط مرّت بالقدرة.", need: "توسيع التفعيل على 3 محاكم.", owner: "البيانات", decision: false, state: "قيد المعالجة", updated: "08-07", due: "14-08", replies: [] },
    { id: "C-13", sec: "impact", rowId: "IM3", dept: "إدارة المنتج", title: "لا Evidence ولا KPI مرتبط", cause: "أُطلق التطوير دون تعريف مؤشر.", need: "تعريف KPI وبدء الرصد الأولي.", owner: "مدير المنتج", decision: true, state: "مفتوح", updated: "01-07", due: "12-07", replies: [] },
  ],
  kpis: ["نسبة قبول القاضي لمقترح الذكاء", "وضوح الإيراد حسب الخدمة", "الالتزام بالـ SLA", "نسبة التعثر"],
};

const NF_FIN = [
  IND({ id: "f1", name: "الإيراد الفعلي", value: 920000, unit: "﷼", prev: 868000, delta: 6, good: "up", gran: "شهري", chart: "area", source: "سجل المزادات", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07 · 14:00", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [790000, 831000, 868000, 920000]), history: [{ d: "06-07", v: "920,000 ﷼", by: "أ. ريم القحطاني", src: "يدوي", note: "إدخال عمولات الأسبوع" }] }),
  IND({ id: "f2", name: "المستهدف الشهري", value: 1045000, unit: "﷼", prev: 1045000, delta: 0, good: "up", gran: "شهري", chart: "bar", source: "خطة الإدارة", method: "يدوي", by: "أ. ريم القحطاني", at: "01-06", series: sr(["مايو", "يونيو", "يوليو"], [1000000, 1045000, 1045000]) }),
  IND({ id: "f3", name: "نسبة تحقيق المستهدف", value: 88, unit: "%", prev: 83, delta: 5, good: "up", gran: "شهري", chart: "line", source: "محتسب", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [79, 83, 83, 88]) }),
  IND({ id: "f4", name: "الإيراد حسب الخدمة", value: "عمولة المزادات 52%", unit: "", prev: "50%", delta: 2, good: "up", gran: "شهري", chart: "barh", source: "سجل المزادات", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07", series: [{ x: "عمولة المزادات", v: 478000 }, { x: "رسوم التقييم", v: 193000 }, { x: "خدمات التسييل", v: 147000 }, { x: "تقارير الأصول", v: 102000 }] }),
  IND({ id: "f5", name: "عدد العمليات المدفوعة", value: 512, unit: "", prev: 478, delta: 7, good: "up", gran: "أسبوعي", chart: "line", source: "سجل المزادات", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07", series: sr(["أ4", "أ5", "أ6", "أ7"], [112, 128, 134, 138]) }),
];
const NF_OPS = [
  IND({ id: "o1", name: "عدد الطلبات", value: 640, unit: "/ الأسبوع", prev: 593, delta: 8, good: "up", gran: "أسبوعي", chart: "line", source: "منصة نافع", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07 · 14:00", series: sr(["أ4", "أ5", "أ6", "أ7"], [551, 578, 593, 640]) }),
  IND({ id: "o2", name: "العمليات المتعثرة", value: 27, unit: "/ الأسبوع", prev: 31, delta: -12, good: "down", gran: "أسبوعي", chart: "area", source: "منصة نافع", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07", series: sr(["أ4", "أ5", "أ6", "أ7"], [38, 34, 31, 27]) }),
  IND({ id: "o3", name: "زمن إغلاق المزاد", value: 7.4, unit: "يوم", prev: 9.2, delta: -20, good: "down", gran: "شهري", chart: "line", source: "سجل المزادات", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [9.8, 9.2, 8.1, 7.4]) }),
  IND({ id: "o4", name: "الالتزام بالـ SLA", value: 94, unit: "%", prev: 93, delta: 1, good: "up", gran: "شهري", chart: "line", source: "منصة نافع", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [91, 92, 93, 94]) }),
  IND({ id: "o5", name: "رضا المزايد", value: 89, unit: "%", prev: 88, delta: 1, good: "up", gran: "شهري", chart: "line", source: "استبيان", method: "يدوي", by: "أ. ريم القحطاني", at: "06-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [85, 87, 88, 89]) }),
];
const NAFE = {
  id: "nafe", name: "نافع", has360: "inprogress", status: "green",
  desc: "منصة بيع وتصفية الأصول التنفيذية إلكترونيًا.",
  owner: "أ. ريم القحطاني", entity: "قطاع التنفيذ", start: "05-2023", stage: "تشغيل", target: "06-2027",
  revenue: 920000, finTarget: 1045000, method: "يدوي", updated: "06-07-2026", lastBy: "أ. ريم القحطاني",
  brief: { blocker: "جهة حكومية لم تردّ على آلية العمولة منذ 3 أسابيع.", decision: "اعتماد آلية عمولة المزادات الحكومية قبل 25-07.", change: "زمن إغلاق المزاد انخفض من 9.2 إلى 7.4 يوم." },
  fin: NF_FIN, ops: NF_OPS,
  opps: [
    { id: "OP1", name: "توسع مزادات الجهات الحكومية", desc: "إتاحة المنصة لتصفية أصول الجهات الحكومية.", start: "01-05", stage: "مواءمة مع الجهة", progress: 45, status: "yellow", owner: "أ. ريم القحطاني", due: "25-07", fate: "بانتظار قرار", decision: "اعتماد آلية العمولة", subs: st([["بناء ملف الفرصة",100,"إدارة المنتج"],["دراسة آلية العمولة",100,"المالية"],["عرض على الجهة",60,"العميل / الوزارة"],["رد الجهة واعتمادها",40,"العميل / الوزارة"],["توقيع الاتفاقية",0,"القانونية"]]) },
    { id: "OP2", name: "خدمة تقييم أصول", desc: "تقييم معتمد للأصول قبل طرحها في المزاد.", start: "18-05", stage: "دراسة الإيراد", progress: 30, status: "yellow", owner: "المالية", due: "10-08", fate: "قيد الدراسة", decision: "—", subs: st([["بناء ملف الفرصة",100,"إدارة المنتج"],["مسح السوق ومزودي التقييم",70,"المشتريات"],["تقدير التسعير",30,"المالية"],["اعتماد الخدمة",0,"إدارة المنتج"]]) },
  ],
  devs: [
    { id: "DV1", name: "لوحة تتبع المزادات", desc: "متابعة دورة حياة المزاد حتى الإغلاق.", start: "10-03", due: "25-07", stage: 2, progress: 65, status: "yellow", owner: "البيانات", decision: "اعتماد مصدر بيانات المزاد", evidence: ["نموذج اللوحة"], serves: ["العمليات", "الإدارة", "البيانات"] },
    { id: "DV2", name: "تحسين تجربة المزايد", desc: "تبسيط رحلة التسجيل والمزايدة على الجوال.", start: "05-02", due: "20-05", stage: 4, progress: 100, status: "yellow", owner: "إدارة المنتج", decision: "بدء دورة قياس الأثر", evidence: [], serves: ["المستفيد النهائي", "الشركاء"] },
    { id: "DV3", name: "ربط سجل الأصول", desc: "ربط آلي مع سجلات الملكية للتحقق قبل الطرح.", start: "12-04", due: "30-08", stage: 0, progress: 20, status: "gray", owner: "التقنية", decision: "—", evidence: [], serves: ["الحوكمة", "البيانات"] },
  ],
  impact: [{ id: "IM1", name: "تقليل زمن إغلاق المزاد", launched: "20-05", cycle: 1, rounds: "1 من 4", expected: "خفض زمن الإغلاق يومين", result: "أثر جزئي", status: "yellow", evidence: ["رقم استخدام: 148"], kpi: "زمن إغلاق المزاد", kpiLinked: true, prev: "9.2 يوم", curr: "7.4 يوم", next: "28-07", owner: "أ. ريم القحطاني", decision: "استمرار القياس", late: false }],
  cases: [
    { id: "N-01", sec: "opps", rowId: "OP1", dept: "العميل / الوزارة", title: "انتظار رد من جهة خارجية", cause: "لم ترد الجهة على آلية العمولة المقترحة.", need: "متابعة رسمية وتحديد موعد اجتماع.", owner: "أ. ريم القحطاني", decision: true, state: "بانتظار قرار", updated: "04-07", due: "25-07", replies: [{ w: "إدارة المنتج", d: "04-07", t: "أُرسل تذكير ثانٍ." }] },
    { id: "N-02", sec: "opps", rowId: "OP2", dept: "المالية", title: "تقدير الإيراد غير مكتمل", cause: "لا مرجعية لتسعير خدمة التقييم.", need: "دراسة مقارنة مع السوق.", owner: "المالية", decision: false, state: "مفتوح", updated: "02-07", due: "05-08", replies: [] },
    { id: "N-03", sec: "devs", rowId: "DV1", dept: "البيانات", title: "نقص مصادر البيانات", cause: "بيانات المزاد تُدخل يدويًا.", need: "اعتماد مصدر بيانات آلي.", owner: "البيانات", decision: false, state: "قيد المعالجة", updated: "02-07", due: "20-07", replies: [] },
    { id: "N-04", sec: "devs", rowId: "DV2", dept: "إدارة المنتج", title: "لم تبدأ دورة قياس الأثر", cause: "أُطلق التطوير ولم يُنقل إلى قياس الأثر.", need: "فتح عنصر قياس أثر.", owner: "إدارة المنتج", decision: false, state: "مفتوح", updated: "05-07", due: "15-07", replies: [] },
    { id: "N-05", sec: "impact", rowId: "IM1", dept: "إدارة المنتج", title: "تأخر تحديث القراءة الأسبوعية", cause: "القراءة تُدخل يدويًا وتأخرت 4 أيام.", need: "أتمتة القراءة أو تثبيت موعد.", owner: "إدارة المنتج", decision: false, state: "مفتوح", updated: "06-07", due: "12-07", replies: [] },
  ],
  kpis: ["زمن إغلاق المزاد", "رضا المزايد"],
};

const NJ_FIN = [
  IND({ id: "f1", name: "الإيراد الفعلي", value: 2100000, unit: "﷼", prev: 2186000, delta: -4, good: "up", gran: "شهري", chart: "area", source: "إدخال يدوي", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07 · 12:00", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [2240000, 2201000, 2186000, 2100000]), history: [{ d: "01-07", v: "2,100,000 ﷼", by: "أ. سلطان العتيبي", src: "يدوي", note: "قراءة شهرية — متأخرة" }] }),
  IND({ id: "f2", name: "المستهدف الشهري", value: 3450000, unit: "﷼", prev: 3450000, delta: 0, good: "up", gran: "شهري", chart: "bar", source: "خطة الإدارة", method: "يدوي", by: "الإدارة", at: "01-04", series: sr(["مايو", "يونيو", "يوليو"], [3450000, 3450000, 3450000]) }),
  IND({ id: "f3", name: "نسبة تحقيق المستهدف", value: 61, unit: "%", prev: 63, delta: -2, good: "up", gran: "شهري", chart: "line", source: "محتسب", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [65, 64, 63, 61]) }),
  IND({ id: "f4", name: "الخصومات والحسميات", value: 92000, unit: "﷼", prev: 74000, delta: 24, good: "down", gran: "شهري", chart: "barh", source: "المالية", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07", series: [{ x: "استرداد رسوم تنفيذ", v: 51000 }, { x: "حسم اتفاقيات شركات", v: 41000 }] }),
];
const NJ_OPS = [
  IND({ id: "o1", name: "عدد الطلبات", value: 3880, unit: "/ الأسبوع", prev: 3695, delta: 5, good: "up", gran: "أسبوعي", chart: "line", source: "إدخال يدوي", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07", series: sr(["أ4", "أ5", "أ6", "أ7"], [3510, 3620, 3695, 3880]) }),
  IND({ id: "o2", name: "العمليات المتعثرة", value: 524, unit: "/ الأسبوع", prev: 444, delta: 18, good: "down", gran: "أسبوعي", chart: "area", source: "احتساب يدوي", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07", series: sr(["أ4", "أ5", "أ6", "أ7"], [388, 412, 444, 524]) }),
  IND({ id: "o3", name: "متوسط زمن الإنجاز", value: 12.6, unit: "يوم", prev: 11.8, delta: 7, good: "down", gran: "شهري", chart: "line", source: "غير محدد", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [11.1, 11.4, 11.8, 12.6]) }),
  IND({ id: "o4", name: "الالتزام بالـ SLA", value: 71, unit: "%", prev: 75, delta: -5, good: "up", gran: "شهري", chart: "line", source: "غير محدد", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [78, 77, 75, 71]) }),
  IND({ id: "o5", name: "الشكاوى", value: 128, unit: "/ الشهر", prev: 105, delta: 22, good: "down", gran: "شهري", chart: "bar", source: "مركز الاتصال", method: "يدوي", by: "أ. سلطان العتيبي", at: "01-07", series: sr(["أبريل", "مايو", "يونيو", "يوليو"], [92, 98, 105, 128]) }),
];
const NAJIZ = {
  id: "najiz", name: "ناجز أعمال", has360: "none", status: "red",
  desc: "الخدمات العدلية لقطاع الأعمال: التوثيق والتنفيذ والاستعلامات.",
  owner: "أ. سلطان العتيبي", entity: "قطاع الأعمال", start: "01-2022", stage: "تشغيل بلا رادار", target: "غير محدد",
  revenue: 2100000, finTarget: 3450000, method: "يدوي", updated: "01-07-2026", lastBy: "أ. سلطان العتيبي",
  brief: { blocker: "لا Dashboard عمليات، والتعثر يُحتسب يدويًا (13.5%).", decision: "تعيين مالك تشغيل لقناة شكاوى المنشآت قبل 15-07.", change: "الشكاوى ارتفعت 22% والقراءة متأخرة 9 أيام." },
  fin: NJ_FIN, ops: NJ_OPS,
  opps: [
    { id: "OP1", name: "حزم خدمات للشركات", desc: "باقات اشتراك سنوية بدل الرسوم لكل طلب.", start: "20-02", stage: "عرض على المدير", progress: 40, status: "red", owner: "أ. سلطان العتيبي", due: "15-07", fate: "بانتظار قرار", decision: "اعتماد نموذج الاشتراك", subs: st([["بناء ملف الفرصة",100,"إدارة المنتج"],["تحليل نموذج الاشتراك",80,"إدارة المنتج"],["دراسة التسعير",50,"المالية"],["عرض على المدير",30,"إدارة المنتج"],["اعتماد الحوكمة",10,"الحوكمة / الاعتماد"]]) },
    { id: "OP2", name: "ربط نقاط البيع", desc: "ربط الخدمات بنقاط بيع الغرف التجارية.", start: "11-04", stage: "بناء ملف الفرصة", progress: 15, status: "gray", owner: "إدارة المنتج", due: "—", fate: "مجمدة", decision: "—", subs: st([["فكرة أولية",60,"إدارة المنتج"],["دراسة الشركاء",0,"المشتريات"]]) },
  ],
  devs: [
    { id: "DV1", name: "حسابات فرعية للمنشآت", desc: "تمكين المنشأة من إنشاء حسابات لموظفيها.", start: "02-01", due: "10-04", stage: 4, progress: 100, status: "red", owner: "إدارة المنتج", decision: "بدء قياس الأثر وربط KPI", evidence: [], serves: ["المستخدم الداخلي", "العمليات"] },
    { id: "DV2", name: "رفع حد طلبات التنفيذ", desc: "زيادة الحد الأعلى للمنشآت الكبرى.", start: "15-03", due: "05-08", stage: 1, progress: 35, status: "yellow", owner: "القانونية", decision: "اعتماد الحد الجديد", evidence: [], serves: ["العمليات", "المالية"] },
    { id: "DV3", name: "Dashboard عمليات", desc: "لوحة تشغيلية أساسية للتعثر وزمن الإجراء.", start: "01-06", due: "30-08", stage: 0, progress: 10, status: "red", owner: "البيانات", decision: "تعيين مالك ومصدر بيانات", evidence: [], serves: ["الإدارة", "العمليات", "البيانات"] },
  ],
  impact: [{ id: "IM1", name: "زيادة استخدام المنشآت", launched: "10-04", cycle: 0, rounds: "0 من 4", expected: "رفع استخدام المنشآت 25%", result: "لا يوجد أثر", status: "red", evidence: [], kpi: "—", kpiLinked: false, prev: "—", curr: "—", next: "متأخر", owner: "غير محدد", decision: "ربط KPI فورًا", late: true }],
  cases: [
    { id: "J-01", sec: "opps", rowId: "OP1", dept: "المالية", title: "نموذج الاشتراك بلا دراسة تسعير", cause: "لا تقدير لأثر الاشتراك على الإيراد.", need: "دراسة مالية قبل العرض.", owner: "المالية", decision: true, state: "بانتظار قرار", updated: "28-06", due: "15-07", replies: [] },
    { id: "J-02", sec: "opps", rowId: "OP1", dept: "الحوكمة / الاعتماد", title: "تأخر اعتماد الحوكمة", cause: "نموذج الاشتراك يحتاج رأي الحوكمة.", need: "جلسة اعتماد.", owner: "الحوكمة", decision: true, state: "بانتظار قرار", updated: "28-06", due: "15-07", replies: [] },
    { id: "J-03", sec: "devs", rowId: "DV3", dept: "البيانات", title: "نقص مصادر البيانات", cause: "لا مصدر آلي لبيانات التعثر.", need: "اعتماد مصدر ومسؤول عنه.", owner: "البيانات", decision: true, state: "مفتوح", updated: "01-07", due: "20-07", replies: [] },
    { id: "J-04", sec: "devs", rowId: "DV3", dept: "التشغيل", title: "تحديد مالك التشغيل", cause: "لا مالك لمؤشر SLA ولا لقناة الشكاوى.", need: "تعيين مالك تشغيل.", owner: "الإدارة", decision: true, state: "بانتظار قرار", updated: "01-07", due: "15-07", replies: [] },
    { id: "J-05", sec: "devs", rowId: "DV2", dept: "القانونية", title: "رفع الحد يحتاج تعديل لائحة", cause: "الحد الحالي منصوص عليه في اللائحة.", need: "مذكرة تعديل.", owner: "القانونية", decision: true, state: "قيد المعالجة", updated: "30-06", due: "05-08", replies: [] },
    { id: "J-06", sec: "impact", rowId: "IM1", dept: "إدارة المنتج", title: "عدم ارتباط التطوير بـ KPI", cause: "أُطلق التطوير دون تعريف مؤشر ولا Evidence.", need: "تعريف KPI وبدء الرصد الأولي.", owner: "مدير المنتج", decision: true, state: "مفتوح", updated: "01-07", due: "14-07", replies: [] },
  ],
  kpis: ["نسبة التعثر", "الالتزام بالـ SLA"],
};
const SEED = [KHIBRAH, NAFE, NAJIZ];

const STAGE_MAP = {
  "C-01": 5, "C-02": 3, "C-03": 4, "C-04": 1, "C-05": 2,
  "C-06": 2, "C-07": 0, "C-08": 3, "C-09": 1, "C-10": 0, "C-11": 4,
  "C-12": 1, "C-13": 0,
  "N-01": 3, "N-02": 2, "N-03": 2, "N-04": 4, "N-05": 1,
  "J-01": 2, "J-02": 4, "J-03": 0, "J-04": 0, "J-05": 1, "J-06": 0,
};
SEED.forEach((p) => p.cases.forEach((c) => { c.stage = STAGE_MAP[c.id] ?? 0; }));

SEED.forEach((prod) => {
  prod.devs.forEach((d) => {
    d.subs = DEV_STD.map((std, i) => ({
      name: std.name,
      owner: ["التحليل", "التقنية", "التقنية", "التقنية", "التشغيل", "إدارة المنتج"][i] || "إدارة المنتج",
      progress: i < d.stage ? 100 : i === d.stage ? d.progress : 0,
      status: i < d.stage ? "green" : i === d.stage ? "yellow" : "gray",
    }));
    d.progress = Math.round(d.subs.reduce((a, b) => a + b.progress, 0) / d.subs.length);
    delete d.stage; delete d.evidence; delete d.serves;
  });
  prod.cases = prod.cases.filter((c) => c.sec !== "impact");
  delete prod.impact;
  prod.impacts = prod.impacts || [];
});

(() => {
  const kh = SEED[0];
  ["بطاقة عرض الخبير", "قالب عرض الخبير"].forEach((n) => {
    const d = kh.devs.find((x) => x.name.includes(n));
    if (!d) return;
    d.subs.forEach((sb) => { sb.progress = 100; sb.status = "green"; });
    d.progress = 100;
  });
})();

// ===================== الأدوار والصلاحيات (RBAC مركزي) =====================
const ROLES = {
  dept:    { key: "dept",    name: "مدير الإدارة",  cls: "ur-dept",    scope: "جميع المنتجات", viewAll: true },
  enable:  { key: "enable",  name: "إدارة التمكين", cls: "ur-enable",  scope: "جميع المنتجات", viewAll: true },
  product: { key: "product", name: "مدير المنتج",   cls: "ur-product", scope: "المنتجات المسندة إليه", viewAll: false },
  project: { key: "project", name: "مدير المشروع",  cls: "ur-project", scope: "منتجات أسندها له مدير المنتج", viewAll: false },
};
const roleOf = (r) => ROLES[r] || { name: r, cls: "ur-product", scope: "—", viewAll: false };
const viewsAll = (u) => !!(u && roleOf(u.role).viewAll);
const ORG_UNITS = ["الإدارة العامة للخبرة", "قطاع التنفيذ", "قطاع الأعمال"];
const ENABLE_ORG = "إدارة التمكين";

// مفاتيح الصلاحيات (مصفوفة مركزية — لا شروط عشوائية داخل المكوّنات)
const P = {
  PRODUCTS_VIEW_ALL: "products.view_all", PRODUCTS_VIEW_ASSIGNED: "products.view_assigned",
  PRODUCTS_CREATE: "products.create", PRODUCTS_UPDATE: "products.update", PRODUCTS_DELETE: "products.delete",
  PM_ASSIGN: "product_managers.assign", PJM_ASSIGN: "project_managers.assign", PJM_REMOVE: "project_managers.remove",
  IND_CREATE: "indicators.create", IND_UPDATE: "indicators.update", IND_DELETE: "indicators.delete", READ_CREATE: "readings.create",
  OPP_CREATE: "opportunities.create", OPP_UPDATE: "opportunities.update", OPP_DELETE: "opportunities.delete",
  DEV_CREATE: "developments.create", DEV_UPDATE: "developments.update", DEV_DELETE: "developments.delete",
  IMP_CREATE: "impacts.create", IMP_UPDATE: "impacts.update", IMP_CLOSE: "impacts.close",
  LAMPS: "lamps.manage", CASE_CREATE: "cases.create", CASE_UPDATE: "cases.update", CASE_CLOSE: "cases.close",
  EVIDENCE: "evidence.add", KPI: "kpi.link", USERS: "users.manage", SETTINGS: "settings.manage", AUDIT: "audit_logs.view",
};
const ROLE_PERMS = {
  dept: "ALL",
  enable: [P.PRODUCTS_VIEW_ALL, P.IND_CREATE, P.IND_UPDATE, P.IND_DELETE, P.READ_CREATE,
    P.OPP_CREATE, P.OPP_UPDATE, P.OPP_DELETE, P.DEV_CREATE, P.DEV_UPDATE, P.DEV_DELETE,
    P.IMP_CREATE, P.IMP_UPDATE, P.IMP_CLOSE, P.LAMPS, P.CASE_CREATE, P.CASE_UPDATE, P.CASE_CLOSE,
    P.EVIDENCE, P.KPI, P.USERS, P.SETTINGS, P.AUDIT, P.PM_ASSIGN, P.PJM_ASSIGN, P.PJM_REMOVE],
  product: [P.PRODUCTS_VIEW_ASSIGNED, P.PRODUCTS_UPDATE, P.IND_CREATE, P.IND_UPDATE, P.IND_DELETE, P.READ_CREATE,
    P.OPP_CREATE, P.OPP_UPDATE, P.OPP_DELETE, P.DEV_CREATE, P.DEV_UPDATE, P.DEV_DELETE,
    P.IMP_CREATE, P.IMP_UPDATE, P.IMP_CLOSE, P.LAMPS, P.CASE_CREATE, P.CASE_UPDATE, P.CASE_CLOSE,
    P.EVIDENCE, P.KPI, P.PJM_ASSIGN, P.PJM_REMOVE, P.AUDIT],
  project: [P.PRODUCTS_VIEW_ASSIGNED, P.READ_CREATE, P.OPP_CREATE, P.OPP_UPDATE, P.DEV_CREATE, P.DEV_UPDATE,
    P.IMP_UPDATE, P.LAMPS, P.CASE_CREATE, P.CASE_UPDATE, P.CASE_CLOSE, P.EVIDENCE],
};
// صلاحيات عامة لا تحتاج سياق منتج
const GLOBAL_PERMS = new Set([P.PRODUCTS_VIEW_ALL, P.PRODUCTS_CREATE, P.PM_ASSIGN, P.USERS, P.SETTINGS, P.AUDIT]);
// نقطة القرار المركزية: تُستخدم في الواجهة وفي تنفيذ الإجراء معًا
function can(user, perm, ctx = {}) {
  if (!user || !user.active) return false;
  const set = ROLE_PERMS[user.role];
  const has = set === "ALL" || (set && set.includes(perm));
  if (!has) return false;
  if (!GLOBAL_PERMS.has(perm) && !viewsAll(user) && ctx.product) {
    const pid = ctx.product.id || ctx.product;
    if (!(user.products || []).includes(pid)) return false;
  }
  if (perm === P.CASE_CLOSE && user.role === "project" && ctx.decision) return false; // إغلاق قرار إداري يتطلب صلاحية أعلى
  return true;
}
const visibleProducts = (user, products) => {
  if (!user) return [];
  if (viewsAll(user)) return products;
  return products.filter((p) => (user.products || []).includes(p.id));
};
const canSeeProduct = (user, p) => !!p && (viewsAll(user) || (user.products || []).includes(p.id));
const canEditProduct = (user, p) => can(user, P.PRODUCTS_UPDATE, { product: p });
const managedUsers = (user, users) => {
  if (!user) return [];
  if (viewsAll(user)) return users.filter((u) => u.id !== user.id);
  if (user.role === "product") return users.filter((u) => u.role === "project" && (u.products || []).some((pid) => (user.products || []).includes(pid)));
  return [];
};
const creatableRoles = (user) => {
  if (!user) return [];
  if (user.role === "dept") return ["dept", "enable", "product", "project"];
  if (user.role === "enable") return ["product", "project"];
  if (user.role === "product") return ["project"];
  return [];
};
const projectManagersOf = (users, pid) => users.filter((u) => u.role === "project" && (u.products || []).includes(pid));

// ===================== الإعدادات القابلة للإدارة =====================
// قيم افتراضية — يديرها Admin من صفحة الإعدادات، وتنعكس في النماذج مباشرة
const SETTINGS_SEED = {
  depts: [...DEPTS],
  oppStatus: ["قيد الدراسة", "نشطة", "معلّقة", "بانتظار قرار", "تتحوّل إلى تطوير", "مغلقة", "أخرى"],
  stationStatus: ["قيد التحليل", "قيد التعليق", "قيد البناء", "قيد التنفيذ", "مكتملة", "أخرى"],
  challengeStatus: ["مفتوح", "قيد المعالجة", "بانتظار قرار", "مغلق"],
  challengePriority: ["منخفضة", "متوسطة", "عالية", "حرجة"],
  oppFate: [...FATES],
  orgUnits: [...ORG_UNITS],
};
// وصف كل إعداد + أين يُستخدم بالضبط (الصفحة ← النموذج ← الحقل)
const SETTINGS_META = [
  { group: "إعدادات الفرص", items: [
    { key: "oppStatus", name: "حالات الفرص", desc: "قائمة الحالات المتاحة للفرصة عند إنشائها أو تعديلها.", page: "فرص 360", form: "إضافة فرصة", field: "حقل «حالة الفرصة»" },
    { key: "oppFate", name: "مصير الفرصة", desc: "المآل النهائي للفرصة بعد دراستها.", page: "فرص 360", form: "تفاصيل الفرصة", field: "حقل «المصير»" },
  ]},
  { group: "إعدادات المحطات", items: [
    { key: "stationStatus", name: "حالات المحطات", desc: "قيم قائمة المحطة عند إضافتها. «أخرى» تتيح كتابة اسم مخصص.", page: "فرص 360 وتطويرات 360", form: "إضافة محطة", field: "حقل «حالة المحطة»" },
  ]},
  { group: "إعدادات التحديات", items: [
    { key: "challengeStatus", name: "حالات التحدي", desc: "الحالة الحالية للتحدي/الـ Case.", page: "فرص 360 وتطويرات 360", form: "إضافة تحدٍّ", field: "حقل «الحالة»" },
    { key: "challengePriority", name: "أولويات التحدي", desc: "مستوى أولوية التحدي.", page: "فرص 360 وتطويرات 360", form: "إضافة تحدٍّ", field: "حقل «الأولوية»" },
  ]},
  { group: "إعدادات عامة", items: [
    { key: "depts", name: "الجهات", desc: "الجهات/الإدارات المسؤولة، تُستخدم كمالك للمحطات والتحديات.", page: "فرص 360 وتطويرات 360", form: "إضافة محطة / تحدٍّ", field: "حقل «الجهة»" },
    { key: "orgUnits", name: "الإدارات/القطاعات", desc: "قائمة الإدارات العامة والقطاعات المتاحة لربط المنتج أو المستخدم بها.", page: "المنتجات وإدارة المستخدمين", form: "إضافة/تعديل منتج، إضافة/تعديل مستخدم", field: "حقل «الإدارة/الجهة»" },
  ]},
];
const OTHER = "أخرى";

const openCases = (p, sec, rowId) => p.cases.filter((c) => c.sec === sec && c.rowId === rowId && c.state !== "مغلق");
const casesAt = (p, sec, rowId, stage) => openCases(p, sec, rowId).filter((c) => c.stage === stage);
const lampAt = (p, sec, rowId, stage) => {
  const cs = casesAt(p, sec, rowId, stage);
  if (!cs.length) return null;
  const lead = cs.find((c) => c.decision) || cs[0];
  return { dept: lead.dept, status: cs.some((c) => c.decision) ? "red" : "yellow", ids: cs.map((c) => c.id), n: cs.length, why: lead.title, need: lead.need };
};
const totals = (p) => {
  const open = p.cases.filter((c) => c.state !== "مغلق");
  return { open: open.length, decisions: open.filter((c) => c.decision).length };
};
const finPct = (p) => Math.round((p.revenue / p.finTarget) * 100);

const rowStatus = (p, sec, id) => {
  const cs = openCases(p, sec, id);
  if (!cs.length) return { s: "green", t: "يمشي", why: null };
  const dec = cs.find((c) => c.decision);
  return dec
    ? { s: "red", t: "متعثر", why: `${dec.dept} — ${dec.title}` }
    : { s: "yellow", t: "يحتاج متابعة", why: `${cs[0].dept} — ${cs[0].title}` };
};
const rowUpdated = (p, sec, id, fallback) => {
  const cs = p.cases.filter((c) => c.sec === sec && c.rowId === id);
  return cs.length ? cs.map((c) => c.updated).sort().slice(-1)[0] : (fallback || "—");
};
const prodStatus = (p) => {
  const open = p.cases.filter((c) => c.state !== "مغلق");
  if (!open.length) return { s: "green", t: "يمشي" };
  const dec = open.filter((c) => c.decision).length;
  return dec ? { s: "red", t: `${dec} قرار مطلوب` } : { s: "yellow", t: `${open.length} حالة مفتوحة` };
};

const DAY_L = ["أحد", "إثنين", "ثلاثاء", "أربعاء", "خميس", "جمعة", "سبت"];
const WEEK_L = ["أ1", "أ2", "أ3", "أ4", "أ5", "أ6", "أ7"];
const MON_L = ["فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو"];
const GRANS = ["يومي", "أسبوعي", "شهري"];
function seriesFor(ind, gran) {
  if (gran === ind.gran) return ind.series;
  const v = Number(ind.value) || 0;
  const flat = /%|يوم/.test(ind.unit || "");
  const base = flat ? v : gran === "يومي" ? v / 7 : gran === "أسبوعي" ? v / 4.3 : v;
  const f = { يومي: [.72, .95, .89, .92, 1.02, .38, .74], أسبوعي: [.82, .88, .93, .9, .97, .95, 1], شهري: [.8, .85, .88, .92, .96, 1] }[gran];
  const L = gran === "يومي" ? DAY_L : gran === "أسبوعي" ? WEEK_L : MON_L;
  const mul = gran === "شهري" && !flat ? 1 : 1;
  return L.map((x, i) => ({ x, v: Math.round(base * f[i] * mul * 10) / 10 }));
}

const CLR = { green: T.ok, yellow: T.warn, red: T.bad, gray: T.gray };
const Modal = ({ title, onClose, children, onSubmit, label = "إضافة", disabled }) => createPortal(
  <div className="ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="modal fade">
      <div className="modal-h"><div className="title">{title}</div><button className="icon" onClick={onClose}>✕</button></div>
      <div className="modal-b">
        {children}
        <div className="acts"><button className="btn btn-g" onClick={onClose}>إلغاء</button><button className="btn btn-p" disabled={disabled} onClick={onSubmit}>{label}</button></div>
      </div>
    </div>
  </div>,
  document.body
);
const Field = ({ label, children }) => <div className="field"><label>{label}</label>{children}</div>;
const Chips = ({ opts, v, on }) => <div className="chips">{opts.map((o) => <button key={o} className={`chip ${v === o ? "on" : ""}`} onClick={() => on(o)}>{o}</button>)}</div>;
function ConfirmModal({ title = "تأكيد الحذف", message, confirmLabel = "نعم، احذف", onConfirm, onClose }) {
  return createPortal(
    <div className="ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal fade" style={{ maxWidth: 420 }}>
        <div className="modal-h"><div className="title" style={{ color: T.bad }}>{I.trash(16)} {title}</div><button className="icon" onClick={onClose}>✕</button></div>
        <div className="modal-b">
          <p className="hint" style={{ margin: 0, fontSize: 13 }}>{message}</p>
          <div className="acts">
            <button className="btn btn-g" onClick={onClose}>إلغاء</button>
            <button className="btn dngr" onClick={onConfirm}>{confirmLabel}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Lens({ tab, setTab, p }) {
  const t = totals(p), pct = finPct(p);
  const stuck = p.ops.find((i) => i.name === "العمليات المتعثرة");
  const req = p.ops.find((i) => i.name === "عدد الطلبات");
  const stkPct = stuck && req ? Math.round((stuck.value / req.value) * 1000) / 10 : 0;
  const gates = [
    { k: "fin", t: "المنظور المالي", s: `${pct}% من المستهدف`, ic: I.wallet, st: pct >= 90 ? "green" : pct >= 70 ? "yellow" : "red" },
    { k: "ops", t: "المنظور التشغيلي", s: `تعثر ${stkPct}%`, ic: I.pulse, st: stkPct <= 6 ? "green" : stkPct <= 10 ? "yellow" : "red" },
    { k: "dev", t: "منظور 360", s: `${t.open} تحدٍّ · ${t.decisions} قرار`, ic: I.cube, st: t.decisions >= 3 ? "red" : t.decisions ? "yellow" : "green" },
    { k: "imp", t: "قياس الأثر", s: p.impacts.length ? `${p.impacts.filter((im) => !im.closed).length} قيد المسار` : "لا شيء بعد", ic: I.clock, st: p.impacts.some((im) => !im.closed && im.step === 0) ? "red" : p.impacts.some((im) => !im.closed) ? "yellow" : p.impacts.length ? "green" : "gray" },
    { k: "cases", t: "الحالات (Cases)", s: `${p.cases.filter((c) => c.state !== "مغلق").length} مفتوحة`, ic: I.flag, st: p.cases.some((c) => c.decision) ? "red" : p.cases.some((c) => c.state !== "مغلق") ? "yellow" : "green" },
  ];
  return (
    <div className="lens">
      <div className="disc">
        <div className="disc-ring" /><div className="disc-in">
          <div>
            <div className="disc-k">عدسة</div>
            <div className="disc-n">360</div>
            <div className="disc-line" />
            <div className="disc-t">التطوير الناجح..<br />يستوعب السياقات!</div>
          </div>
        </div>
        <div className="disc-dash" />
      </div>
      <div className="gates">
        {gates.map((g) => (
          <button key={g.k} className={`gate ${tab === g.k ? "on" : ""}`} onClick={() => setTab(g.k)}>
            <span className="g-ic">{g.ic(17)}</span>
            <span style={{ flex: 1 }}><span className="g-t" style={{ display: "block" }}>{g.t}</span><span className="g-s">{g.s}</span></span>
            <span className="g-dot" style={{ background: CLR[g.st] }} />
          </button>
        ))}
      </div>
    </div>
  );
}

function IndicatorLibrary({ p, kind, act, currentUser }) {
  const list = kind === "fin" ? p.fin : p.ops;
  const cCreate = can(currentUser, P.IND_CREATE, { product: p });
  const cUpdate = can(currentUser, P.IND_UPDATE, { product: p });
  const cDelete = can(currentUser, P.IND_DELETE, { product: p });
  const cRead = can(currentUser, P.READ_CREATE, { product: p });
  const [sel, setSel] = useState(null);
  const [dlg, setDlg] = useState(false);
  const [upd, setUpd] = useState(null);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);
  const [gran, setGran] = useState(null);
  const [f, setF] = useState({ name: "", value: "", unit: "", gran: "شهري", source: "", method: "يدوي" });
  const cur = list.find((i) => i.id === sel);
  const val = (i) => (typeof i.value === "number" ? fmt(i.value) : i.value);

  const chartOf = (i, data) => {
    const col = kind === "fin" ? T.verdi : T.petrolSoft;
    if (i.chart === "barh") return (
      <BarChart data={data} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" stroke={T.line} horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => (v > 9999 ? `${Math.round(v / 1000)}k` : v)} reversed />
        <YAxis type="category" dataKey="x" orientation="right" width={100} tick={{ fontSize: 11, fill: T.ink }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ borderRadius: 10, border: `1px solid ${T.line}`, fontSize: 12 }} formatter={(v) => [fmt(v), ""]} />
        <Bar dataKey="v" radius={[0, 5, 5, 0]}>{data.map((_, k) => <Cell key={k} fill={[T.verdi, "#4A857F", "#6E9E99", "#96B8B4", "#BBD0CD"][k] || T.verdi} />)}</Bar>
      </BarChart>
    );
    if (i.chart === "bar") return (
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={T.line} vertical={false} />
        <XAxis dataKey="x" tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} reversed />
        <YAxis orientation="right" width={46} tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => (v > 9999 ? `${Math.round(v / 1000)}k` : v)} />
        <Tooltip contentStyle={{ borderRadius: 10, border: `1px solid ${T.line}`, fontSize: 12 }} formatter={(v) => [fmt(v), ""]} />
        <Bar dataKey="v" radius={[5, 5, 0, 0]} fill={col} />
      </BarChart>
    );
    if (i.chart === "area") return (
      <AreaChart data={data}>
        <defs><linearGradient id={`g${i.id}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={col} stopOpacity={.3} /><stop offset="100%" stopColor={col} stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid strokeDasharray="3 3" stroke={T.line} vertical={false} />
        <XAxis dataKey="x" tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} reversed />
        <YAxis orientation="right" width={46} tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => (v > 9999 ? `${Math.round(v / 1000)}k` : v)} />
        <Tooltip contentStyle={{ borderRadius: 10, border: `1px solid ${T.line}`, fontSize: 12 }} formatter={(v) => [fmt(v), ""]} />
        <Area type="monotone" dataKey="v" stroke={col} strokeWidth={2.2} fill={`url(#g${i.id})`} />
      </AreaChart>
    );
    return (
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke={T.line} vertical={false} />
        <XAxis dataKey="x" tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} reversed />
        <YAxis orientation="right" width={40} tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} />
        <Tooltip contentStyle={{ borderRadius: 10, border: `1px solid ${T.line}`, fontSize: 12 }} formatter={(v) => [fmt(v), ""]} />
        <Line type="monotone" dataKey="v" stroke={col} strokeWidth={2.2} dot={{ r: 2.5 }} />
      </LineChart>
    );
  };

  return (
    <div className="fade">
      <div className="h-row" style={{ marginBottom: 4 }}>
        <div className="title">{kind === "fin" ? I.wallet() : I.pulse()} {kind === "fin" ? "المؤشرات المالية" : "المؤشرات التشغيلية"}</div>
        {cCreate && <button className="btn btn-v" onClick={() => setDlg(true)}>+ إضافة مؤشر {kind === "fin" ? "مالي" : "تشغيلي"}</button>}
      </div>
      <p className="hint" style={{ marginBottom: 14 }}>اضغط أي مؤشر لفتح سجله وقراءاته ومصدره — لكل مؤشر تقسيمه الزمني الطبيعي.</p>

      <div className="ind-grid">
        {list.map((i) => (
          <React.Fragment key={i.id}>
          <button className={`ind ${sel === i.id ? "on" : ""}`} onClick={() => { setSel(sel === i.id ? null : i.id); setGran(i.gran); }}>
            <div className="ind-n">{i.name}</div>
            <div><span className="ind-v">{val(i)}</span><span className="ind-u">{i.unit}</span></div>
            <div className="ind-f">
              <span className="src">{i.method} · {i.gran}</span>
              <span className={`trend ${!i.delta ? "t-flat" : (i.good === "up" ? i.delta > 0 : i.delta < 0) ? "t-up" : "t-down"}`}>
                {!i.delta ? "— ثابت" : `${i.delta > 0 ? "▲" : "▼"} ${Math.abs(i.delta)}%`}
              </span>
            </div>
            <div className="mut" style={{ fontSize: 10.4, textAlign: "center", borderTop: `1px dashed ${T.line}`, paddingTop: 6 }}>
              {sel === i.id ? "▲ إغلاق التفاصيل" : "▼ اضغط: الشارت + سجل التحديثات"}
            </div>
          </button>
          {sel === i.id && (
        <div className="detail">
          <div className="detail-h">
            <div>
              <div className="dsp" style={{ fontSize: 14 }}>{cur.name}</div>
              <div className="mut" style={{ fontSize: 11.4, marginTop: 3 }}>مصدر البيانات: {cur.source} · التحديث: {cur.method} · {cur.by} · {cur.at}</div>
              {cur.causes?.length > 0 && <div style={{ fontSize: 11.4, color: T.verdi, marginTop: 4, fontWeight: 600 }}>تحسّن بسبب: {cur.causes.join("، ")}</div>}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {cRead && <button className="btn btn-p btn-sm" onClick={() => setUpd(cur)}>تحديث القراءة</button>}
              {cUpdate && <button className="btn btn-g btn-sm" onClick={() => setEditing(cur)}>{I.edit(13)} تعديل المؤشر</button>}
              {cDelete && <button className="btn btn-sm dngr" onClick={() => setDel(cur)}>{I.trash(12)} حذف</button>}
              <button className="btn btn-g btn-sm" onClick={() => setSel(null)}>إغلاق</button>
            </div>
          </div>
          <div className="detail-b">
            <div>
              <div className="read">
                <div><span>القراءة الحالية</span><b>{val(cur)} {cur.unit}</b></div>
                <div><span>القراءة السابقة</span><b className="mut">{typeof cur.prev === "number" ? fmt(cur.prev) : cur.prev} {cur.unit}</b></div>
                <div><span>التغير</span><b style={{ color: !cur.delta ? T.muted : (cur.good === "up" ? cur.delta > 0 : cur.delta < 0) ? T.ok : T.bad }}>
                  {!cur.delta ? "ثابت" : `${cur.delta > 0 ? "▲" : "▼"} ${Math.abs(cur.delta)}%`}</b></div>
                <div><span>المصدر</span><b style={{ fontSize: 13 }}>{cur.source}</b></div>
                <div><span>آلية التحديث</span><b style={{ fontSize: 13 }}>{cur.method}</b></div>
              </div>
              <div className="gswitch">
                {GRANS.map((g) => (
                  <button key={g} className={`gbtn ${(gran || cur.gran) === g ? "on" : ""}`} onClick={() => setGran(g)}>
                    {g}{g === cur.gran && <i>طبيعي</i>}
                  </button>
                ))}
              </div>
              <div style={{ height: 178 }}>
                <ResponsiveContainer>{chartOf(cur, cur.chart === "barh" ? cur.series : seriesFor(cur, gran || cur.gran))}</ResponsiveContainer>
              </div>

              {cur.chart !== "barh" && (
                <>
                  <div className="mut" style={{ fontSize: 11.2, margin: "12px 0 4px" }}>مقارنة القراءة الحالية بالسابقة</div>
                  <div style={{ height: 120 }}>
                    <ResponsiveContainer>
                      <BarChart data={[{ x: "السابقة", v: Number(cur.prev) || 0 }, { x: "الحالية", v: Number(cur.value) || 0 }]}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.line} vertical={false} />
                        <XAxis dataKey="x" tick={{ fontSize: 10.5, fill: T.muted }} axisLine={false} tickLine={false} reversed />
                        <YAxis orientation="right" width={44} tick={{ fontSize: 10, fill: T.muted }} axisLine={false} tickLine={false} tickFormatter={(v) => (v > 9999 ? `${Math.round(v / 1000)}k` : v)} />
                        <Tooltip contentStyle={{ borderRadius: 10, border: `1px solid ${T.line}`, fontSize: 12 }} formatter={(v) => [fmt(v), cur.name]} />
                        <Bar dataKey="v" radius={[5, 5, 0, 0]}>
                          <Cell fill={T.line} /><Cell fill={kind === "fin" ? T.verdi : T.petrolSoft} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}
            </div>
            <div>
              <div className="mut" style={{ fontSize: 11.4, marginBottom: 8 }}>سجل القراءات والتحديثات ({cur.history.length})</div>
              <div className="tw" style={{ borderRadius: 10 }}>
                <table className="tbl" style={{ minWidth: 0, fontSize: 11.6 }}>
                  <thead><tr><th>التاريخ</th><th>القراءة</th><th>المصدر</th><th>من</th></tr></thead>
                  <tbody>
                    {cur.history.length === 0 && <tr><td colSpan={4} className="empty">لا سجل بعد. حدّث القراءة لتبدأ الذاكرة.</td></tr>}
                    {cur.history.map((h, k) => (
                      <React.Fragment key={k}>
                        <tr><td className="mut">{h.d}</td><td><b>{h.v}</b></td><td><span className="src">{h.src}</span></td><td className="mut">{h.by}</td></tr>
                        {h.note && h.note !== "—" && <tr><td colSpan={4} className="mut" style={{ fontSize: 11, paddingTop: 0, borderBottom: `1px solid ${T.line}` }}>ملاحظة: {h.note}</td></tr>}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="row-act" style={{ marginTop: 10 }}>
                {cRead && <button className="btn btn-p btn-sm" onClick={() => setUpd(cur)}>تحديث القراءة</button>}
                <span className="mut" style={{ fontSize: 11, alignSelf: "center" }}>آخر تحديث: {cur.by} · {cur.at}</span>
              </div>
            </div>
          </div>
        </div>
          )}
          </React.Fragment>
        ))}
      </div>

      {upd && (
        <FormModal title={`تحديث قراءة — ${upd.name}`} label="حفظ القراءة" onClose={() => setUpd(null)}
          fields={[
            { k: "value", l: `القراءة الجديدة (${upd.unit || "قيمة"})`, v: String(upd.value), req: true },
            { k: "note", l: "ملاحظة", v: "", area: true, ph: "اختياري — تُحفظ في السجل" },
          ]}
          onSubmit={(v) => { act.updateReading(kind, upd.id, v.value, v.note); setUpd(null); }} />
      )}

      {editing && (
        <FormModal title={`تعديل المؤشر — ${editing.name}`} label="حفظ التعديل" onClose={() => setEditing(null)}
          fields={[
            { k: "name", l: "اسم المؤشر", v: editing.name, req: true },
            { k: "unit", l: "الوحدة", v: editing.unit || "", ph: "﷼ / % / يوم" },
            { k: "source", l: "مصدر البيانات", v: editing.source },
            { k: "method", l: "آلية التحديث", v: editing.method, opts: ["يدوي", "API"], req: true },
            { k: "gran", l: "التقسيم الزمني", v: editing.gran, opts: ["يومي", "أسبوعي", "شهري"], req: true },
            { k: "good", l: "الاتجاه الأفضل", v: editing.good === "up" ? "ارتفاع أفضل" : "انخفاض أفضل", opts: ["ارتفاع أفضل", "انخفاض أفضل"], req: true, hint: "يحدّد لون التغيّر (أخضر/أحمر)." },
          ]}
          onSubmit={(v) => { act.editInd(kind, editing.id, v); setEditing(null); }} />
      )}

      {del && (
        <ConfirmModal message={`سيُحذف المؤشر «${del.name}» وكامل سجل قراءاته. لا يمكن التراجع.`} onClose={() => setDel(null)}
          onConfirm={() => { if (sel === del.id) setSel(null); act.deleteInd(kind, del.id); setDel(null); }} />
      )}

      {dlg && (
        <Modal title={`إضافة مؤشر ${kind === "fin" ? "مالي" : "تشغيلي"}`} onClose={() => setDlg(false)} disabled={!f.name}
          onSubmit={() => { act.addInd(kind, f); setDlg(false); setF({ name: "", value: "", unit: "", gran: "شهري", source: "", method: "يدوي" }); }}>
          <Field label="اسم المؤشر"><input className="inp" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="القراءة الحالية"><input className="inp" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} /></Field>
            <Field label="الوحدة"><input className="inp" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="﷼ / % / يوم" /></Field>
          </div>
          <Field label="التقسيم الزمني الطبيعي"><Chips opts={["يومي", "أسبوعي", "شهري"]} v={f.gran} on={(v) => setF({ ...f, gran: v })} /></Field>
          <Field label="مصدر البيانات"><input className="inp" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} /></Field>
          <Field label="آلية التحديث"><Chips opts={["يدوي", "API"]} v={f.method} on={(v) => setF({ ...f, method: v })} /></Field>
        </Modal>
      )}
    </div>
  );
}

const stepState = (progress, lamp) => {
  if (lamp) return { s: lamp.status, t: lamp.status === "red" ? "متعثر" : "يحتاج متابعة" };
  if (progress === 100) return { s: "green", t: "مكتملة" };
  if (progress > 0) return { s: "green", t: "تمشي" };
  return { s: "gray", t: "لم تبدأ" };
};

function FormModal({ title, fields, onClose, onSubmit, label = "حفظ", validate, note }) {
  const [v, setV] = useState(Object.fromEntries(fields.map((f) => [f.k, f.v ?? ""])));
  const [errs, setErrs] = useState({});
  const set = (k) => (e) => { const val = e.target.value; setV((p) => ({ ...p, [k]: val })); setErrs((p) => ({ ...p, [k]: undefined })); };
  const shown = fields.filter((f) => !f.when || f.when(v));

  const submit = () => {
    const e = {};
    shown.forEach((f) => { if (f.req && !String(v[f.k] ?? "").trim()) e[f.k] = "هذا الحقل مطلوب"; });
    if (validate) Object.assign(e, validate(v) || {});
    if (Object.keys(e).length) { setErrs(e); return; }
    onSubmit(v);
  };

  const render = (f) => {
    const err = errs[f.k];
    const style = err ? { borderColor: T.bad, background: "#FBF1EF" } : undefined;
    if (f.opts) return (
      <select className="inp" style={style} value={v[f.k]} onChange={set(f.k)}>
        <option value="">— اختر —</option>
        {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
    if (f.type === "date") return <input type="date" className="inp" style={style} value={v[f.k]} onChange={set(f.k)} />;
    if (f.type === "number") return <input type="number" min={f.min ?? 0} max={f.max ?? 100} step={f.step ?? 1} className="inp" style={style} value={v[f.k]} onChange={set(f.k)} />;
    if (f.type === "password") return <input type="password" className="inp" style={style} value={v[f.k]} onChange={set(f.k)} placeholder={f.ph} />;
    if (f.area) return <textarea className="inp" style={style} rows={f.rows || 4} value={v[f.k]} onChange={set(f.k)} placeholder={f.ph} />;
    return <input className="inp" style={style} value={v[f.k]} onChange={set(f.k)} placeholder={f.ph} />;
  };

  return createPortal(
    <div className="ov" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal fade">
        <div className="modal-h"><div className="title">{title}</div><button className="icon" onClick={onClose}>✕</button></div>
        <div className="modal-b">
          {note && <div className="usemap" style={{ marginBottom: 2 }}>{note}</div>}
          {shown.map((f) => (
            <div className="field" key={f.k}>
              <label>{f.l}{f.req && <span style={{ color: T.bad }}> *</span>}</label>
              {render(f)}
              {f.hint && <div className="mut" style={{ fontSize: 10.6, marginTop: 4 }}>{f.hint}</div>}
              {errs[f.k] && <div className="reqmsg" style={{ marginTop: 6 }}>{errs[f.k]}</div>}
            </div>
          ))}
          <div className="acts">
            <button className="btn btn-g" onClick={onClose}>إلغاء</button>
            <button className="btn btn-p" onClick={submit}>{label}</button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const Sdot = ({ s, title }) => <span className="sdot" style={{ background: CLR[s] }} title={title} />;
const Lnk = ({ children, onClick, tone }) => (
  <button className="lnk" style={tone ? { color: tone } : null} onClick={(e) => { e.stopPropagation(); onClick(); }}>{children}</button>
);
const PRI_CLS = { "منخفضة": "pri-low", "متوسطة": "pri-mid", "عالية": "pri-high", "حرجة": "pri-crit" };
function Challenges({ p, sec, rowId, item, act, onEdit, onDelete, currentUser }) {
  const [open, setOpen] = useState(null);
  const [txt, setTxt] = useState({});
  const cReply = can(currentUser, P.CASE_UPDATE, { product: p });

  const list = p.cases.filter((c) => c.sec === sec && c.rowId === rowId);
  const linkLabel = (c) => (c.stage != null && c.stage >= 0)
    ? `المحطة: ${item.subs[c.stage]?.name || "—"}`
    : (sec === "opps" ? "الفرصة كاملة" : "التطوير كاملًا");

  return (
    <>
      {list.length === 0 && (
        <div className="nochal">لا تحديات بعد — استخدم زر «إضافة تحدٍّ» أعلى البطاقة.</div>
      )}

      <div className="chals">
        {list.map((c) => {
          const isOpen = open === c.id;
          const done = c.state === "مغلق";
          return (
            <div key={c.id} className={`chal ${done ? "closed" : "opened"}`}>
              <button className="chal-head" onClick={() => setOpen(isOpen ? null : c.id)}>
                <span className="sdot" style={{ background: done ? T.ok : (c.decision ? T.bad : T.warn) }} />
                <span className="chal-t">{c.title}</span>
                <span className={`pri ${PRI_CLS[c.priority] || "pri-mid"}`}>{I.flag(11)}{c.priority || "متوسطة"}</span>
                <span className="lvl">{I.loc(11)}{linkLabel(c)}</span>
                <span className="chev">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen && (
                <div className="chal-b fade">
                  <div className="mut" style={{ fontSize: 10.8, marginBottom: 6 }}>
                    الجهة: {c.dept} · المالك: {c.owner} · الحالة: {c.state} · الاستحقاق: {c.due || "—"} · آخر تحديث: {c.updated}
                  </div>
                  <div className="chal-need">الوصف: {c.cause}</div>
                  {c.need && c.need !== "—" && <div className="chal-need">المطلوب: <b>{c.need}</b></div>}
                  {c.replies.length > 0 && (
                    <div className="mreplies">{c.replies.map((r, i) => <div key={i}><span>{r.w} · {r.d}</span>{r.t}</div>)}</div>
                  )}
                  {!done && (
                    <>
                      {cReply && (
                        <div style={{ display: "flex", gap: 7, marginTop: 9 }}>
                          <input className="inp" placeholder="أضف ردًا/تحديثًا…" value={txt[c.id] || ""} onChange={(e) => setTxt({ ...txt, [c.id]: e.target.value })} />
                          <button className="btn btn-p btn-sm" onClick={() => { if ((txt[c.id] || "").trim()) { act.addReply(c.id, txt[c.id].trim()); setTxt({ ...txt, [c.id]: "" }); } }}>رد</button>
                        </div>
                      )}
                      <div className="mlinks">
                        {onEdit && <Lnk onClick={() => onEdit(c)}>{I.edit(13)} تعديل التحدي</Lnk>}
                        {can(currentUser, P.CASE_CLOSE, { product: p, decision: c.decision })
                          ? <Lnk onClick={() => act.closeCase(c.id)} tone={T.ok}>إغلاق التحدي</Lnk>
                          : c.decision ? <span className="mut" style={{ fontSize: 10.6 }}>{I.lock(11)} الإغلاق يتطلب قرارًا إداريًا</span> : null}
                        {onDelete && <Lnk onClick={() => onDelete(c)} tone={T.bad}>{I.trash(12)} حذف</Lnk>}
                        {!cReply && !onEdit && !onDelete && <span className="mut" style={{ fontSize: 10.6 }}>{I.lock(11)} عرض فقط</span>}
                      </div>
                    </>
                  )}
                  {done && onDelete && (
                    <div className="mlinks"><Lnk onClick={() => onDelete(c)} tone={T.bad}>{I.trash(12)} حذف التحدي</Lnk></div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

const fmtD = (d) => { if (!d || d === "—") return "—"; const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d); return m ? `${m[3]}-${m[2]}-${m[1]}` : d; };
const ActBtn = ({ icon, label, tip, cls, onClick }) => (
  <button className={`actbtn ${cls || ""}`} title={tip || label} onClick={(e) => { e.stopPropagation(); onClick(); }}>{icon} {label}</button>
);

// Stations tabbed by: القديمة (legacy — pre-existing, excluded from the item's overall
// progress), نشطة (new stations not yet done — these drive the item's completion), and
// مكتملة (new stations that reached 100%, land here once completed).
function StationsList({ p, sec, idx, item, act, setModal, cUpd, cCaseNew }) {
  const [tab, setTab] = useState("active");
  const withIdx = item.subs.map((sb, si) => ({ sb, si }));
  const legacy = withIdx.filter((x) => x.sb.legacy);
  const active = withIdx.filter((x) => !x.sb.legacy && x.sb.progress !== 100);
  const done = withIdx.filter((x) => !x.sb.legacy && x.sb.progress === 100);
  const list = tab === "done" ? done : tab === "legacy" ? legacy : active;

  return (
    <>
      <div className="ctabs">
        <button className={`ctab ${tab === "active" ? "on" : ""}`} onClick={() => setTab("active")}>نشطة <b>{active.length}</b></button>
        <button className={`ctab ${tab === "done" ? "on" : ""}`} onClick={() => setTab("done")}>✓ مكتملة <b>{done.length}</b></button>
        {legacy.length > 0 && <button className={`ctab ${tab === "legacy" ? "on" : ""}`} onClick={() => setTab("legacy")}>القديمة <b>{legacy.length}</b></button>}
      </div>
      {list.length === 0 && (
        <div className="nostn"><p>
          {tab === "active" && `لا محطات نشطة بعد.${cUpd ? " استخدم زر «إضافة محطة» بالأعلى." : ""}`}
          {tab === "done" && "لا محطات مكتملة بعد."}
          {tab === "legacy" && "لا محطات قديمة."}
        </p></div>
      )}
      <div className="stns">
        {list.map(({ sb, si }) => {
          const lamp = lampAt(p, sec, item.id, si);
          const st2 = stepState(sb.progress, lamp);
          const stCase = p.cases.find((c) => c.sec === sec && c.rowId === item.id && c.stage === si && c.state !== "مغلق");
          return (
            <div className="stn" key={si}>
              <Sdot s={st2.s} title={st2.t} />
              <span className="stn-n">{sb.name}</span>
              <span className="mbar sm"><span style={{ width: `${sb.progress}%`, background: CLR[st2.s] }} /></span>
              <span className="mpct">{sb.progress}%</span>
              {stCase && (st2.s === "red" || st2.s === "yellow") && (
                <button className="lampchip" title="افتح الـ Case المرتبط" onClick={() => setModal({ type: "editChallenge", sec, idx, cid: stCase.id })}>
                  {stCase.dept} {st2.s === "red" ? "🔴" : "🟡"} — {stCase.title}
                </button>
              )}
              <span className="stn-act">
                {cUpd && si > 0 && <Lnk onClick={() => act.reorderStation(sec, idx, si, -1)}>↑</Lnk>}
                {cUpd && si < item.subs.length - 1 && <Lnk onClick={() => act.reorderStation(sec, idx, si, 1)}>↓</Lnk>}
                {cUpd && sb.progress < 100 && <Lnk onClick={() => act.updateStation(sec, idx, si, { name: sb.name, owner: sb.owner, progress: 100 })} tone={T.ok}>✓ إكمال</Lnk>}
                {cUpd && <Lnk onClick={() => setModal({ type: "editStation", sec, idx, si })}>{I.edit(13)} تعديل</Lnk>}
                {cCaseNew && <Lnk onClick={() => setModal({ type: "addChallenge", sec, idx, stage: si })} tone={T.bad}>+ تحدٍّ</Lnk>}
                {cUpd && <Lnk onClick={() => setModal({ type: "confirm", message: `حذف المحطة «${sb.name}»؟`, run: () => act.deleteStation(sec, idx, si) })} tone={T.bad}>{I.trash(12)} حذف</Lnk>}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ItemCard({ p, sec, item, idx, isOpen, toggle, act, setModal, currentUser, showIndex = true, headerBadge, extraAction }) {
  const stt = rowStatus(p, sec, item.id);
  const uPerm = sec === "opps" ? P.OPP_UPDATE : P.DEV_UPDATE;
  const dPerm = sec === "opps" ? P.OPP_DELETE : P.DEV_DELETE;
  const cUpd = can(currentUser, uPerm, { product: p });
  const cDel = can(currentUser, dPerm, { product: p });
  const cCaseNew = can(currentUser, P.CASE_CREATE, { product: p });
  const cCaseUpd = can(currentUser, P.CASE_UPDATE, { product: p });
  const cStartImpact = sec === "devs" && can(currentUser, P.IMP_CREATE, { product: p });
  const alreadyTracked = sec === "devs" && p.impacts.some((im) => im.devId === item.id);
  const openCount = p.cases.filter((c) => c.sec === sec && c.rowId === item.id && c.state !== "مغلق").length;
  const closedCount = p.cases.filter((c) => c.sec === sec && c.rowId === item.id && c.state === "مغلق").length;
  return (
    <div className={`ocard ${isOpen ? "on" : ""}`}>
      <button className="ocard-head" onClick={toggle}>
        {sec === "devs" && showIndex && <span className="idx">{idx + 1}</span>}
        <Sdot s={stt.s} title={stt.t} />
        <span className="oname">{sec === "opps" ? `فرصة ${item.name}` : item.name}</span>
        <span className="mbar"><span style={{ width: `${item.progress}%`, background: CLR[stt.s] }} /></span>
        <span className="mpct">{item.progress}%</span>
        {(openCount > 0 || closedCount > 0) && (
          <>
            <span className="vtag v-red" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span className="sdot" style={{ background: T.bad }} />مفتوحة {openCount}
            </span>
            <span className="vtag v-green" style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
              <span className="sdot" style={{ background: T.ok }} />مغلقة {closedCount}
            </span>
          </>
        )}
        {item.progress === 100 && <span className="vtag v-green">✓ مكتملة</span>}
        {headerBadge}
        <span className="chev">{isOpen ? "−" : "+"}</span>
      </button>

      {isOpen && (
        <div className="ocard-body fade">
          <p className="odesc">{item.desc}</p>
          <div className="oupd">المالك: {item.owner} · البداية: {fmtD(item.start)} · المستهدف: {fmtD(item.due)} · آخر تحديث: {rowUpdated(p, sec, item.id, item.start)}</div>

          <div className="actbar">
            {cUpd && <ActBtn cls="upd" icon={I.note(15)} label="إضافة تحديث" tip="تسجيل تحديث أو ملاحظة على هذا العنصر — نموذج مستقل"
              onClick={() => setModal({ type: "addUpdate", sec, idx })} />}
            {cUpd && <ActBtn icon={I.step(15)} label="إضافة محطة" tip="إضافة محطة من قائمة الحالات (مع خيار «أخرى») — نموذج مستقل"
              onClick={() => setModal({ type: "addStation", sec, idx })} />}
            {cCaseNew && <ActBtn cls="chal" icon={I.flag(15)} label="إضافة تحدٍّ" tip="فتح تحدٍّ/Case مستقل وربطه بالعنصر أو بمحطة — نموذج مستقل"
              onClick={() => setModal({ type: "addChallenge", sec, idx, stage: -1 })} />}
            {cUpd && <ActBtn cls="edit" icon={I.edit(14)} label={sec === "opps" ? "تعديل الفرصة" : "تعديل التطوير"} tip="تعديل بيانات هذا العنصر (الاسم/الوصف/المالك/الحالة/التواريخ)"
              onClick={() => setModal({ type: "editItem", sec, idx })} />}
            {cDel && <ActBtn cls="del" icon={I.trash(14)} label="حذف" tip="حذف هذا العنصر وكل محطاته وتحدياته"
              onClick={() => setModal({ type: "confirm", message: `سيُحذف «${item.name}» وكل محطاته وتحدياته. لا يمكن التراجع.`, run: () => act.deleteItem(sec, idx) })} />}
            {cStartImpact && item.progress === 100 && !alreadyTracked && (
              <ActBtn cls="edit" icon={I.flag(15)} label="بدء قياس الأثر" tip="نقل هذا التطوير إلى قياس الأثر لمتابعته هناك"
                onClick={() => act.startImpact(idx)} />
            )}
            {extraAction}
            {!cUpd && !cDel && !cCaseNew && !extraAction && <span className="mut" style={{ fontSize: 11 }}>{I.lock(12)} حسابك للعرض فقط على هذا العنصر</span>}
          </div>

          {item.updates?.length > 0 && (
            <div className="upd-log">
              {item.updates.slice(0, 5).map((u, i) => (
                <div className="upd-item" key={i}>
                  <div className="u-m">{u.by} · {u.at}
                    {cUpd && <button className="lnk del" style={{ marginInlineStart: 8, fontSize: 10.6 }}
                      onClick={() => setModal({ type: "confirm", message: "حذف هذا التحديث؟", run: () => act.deleteUpdate(sec, idx, i) })}>حذف</button>}
                  </div>
                  <div className="u-t">{u.text}</div>
                </div>
              ))}
            </div>
          )}

          <div className="zones">
            <div className="zone">
              <div className="zone-h">
                <span>المحطات</span>
                {cUpd && <Lnk onClick={() => setModal({ type: "addStation", sec, idx })} tone={T.verdi}>+ محطة</Lnk>}
              </div>
              {item.subs.length === 0 ? (
                <div className="nostn"><p>لا محطات بعد.{cUpd ? " استخدم زر «إضافة محطة» بالأعلى." : ""}</p></div>
              ) : (
                <StationsList p={p} sec={sec} idx={idx} item={item} act={act} setModal={setModal} cUpd={cUpd} cCaseNew={cCaseNew} />
              )}
            </div>

            <div className="zone">
              <div className="zone-h">
                <span>التحديات (Cases)</span>
                {cCaseNew && <Lnk onClick={() => setModal({ type: "addChallenge", sec, idx, stage: -1 })} tone={T.bad}>+ تحدٍّ</Lnk>}
              </div>
              <Challenges p={p} sec={sec} rowId={item.id} item={item} act={act} currentUser={currentUser}
                onEdit={cCaseUpd ? (c) => setModal({ type: "editChallenge", sec, idx, cid: c.id }) : null}
                onDelete={cCaseUpd ? (c) => setModal({ type: "confirm", message: `حذف التحدي «${c.title}»؟`, run: () => act.deleteChallenge(c.id) }) : null} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabbedItemList({ items, sec, p, act, setModal, currentUser, openId, setOpenId }) {
  const [tab, setTab] = useState("active");
  if (items.length === 0) return null;
  const active = items.filter((it) => it.progress !== 100);
  const done = items.filter((it) => it.progress === 100);
  const list = tab === "active" ? active : done;
  return (
    <>
      <div className="ctabs">
        <button className={`ctab ${tab === "active" ? "on" : ""}`} onClick={() => setTab("active")}>نشطة <b>{active.length}</b></button>
        <button className={`ctab ${tab === "done" ? "on" : ""}`} onClick={() => setTab("done")}>✓ مكتملة <b>{done.length}</b></button>
      </div>
      {list.length === 0 && (
        <div className="empty">{tab === "active" ? "لا عناصر نشطة حاليًا." : "لا عناصر مكتملة بعد."}</div>
      )}
      <div className="cards-list">
        {list.map((item) => (
          <ItemCard key={item.id} p={p} sec={sec} item={item} idx={items.indexOf(item)} act={act} setModal={setModal} currentUser={currentUser}
            isOpen={openId === item.id} toggle={() => setOpenId(openId === item.id ? null : item.id)} />
        ))}
      </div>
    </>
  );
}

// Every modal an opportunity/development card's ActBtn/Lnk row can open (add/edit item,
// stations, challenges). Shared by DevView (فرص/تطويرات lists) and ImpactView (قياس الأثر
// cards reuse ItemCard on the same underlying dev item) so both dispatch identical actions.
function ItemModals({ p, act, settings, owners: ownerUsers, modal, setModal }) {
  const owners = (ownerUsers || []).map((u) => u.name);
  const ownerOpts = owners.length ? owners : ["غير محدد"];
  const dateValidate = (v) => { const e = {}; if (v.start && v.due && v.due < v.start) e.due = "التاريخ المستهدف لا يمكن أن يسبق تاريخ البداية"; return e; };
  const cur = modal && (modal.sec === "opps" ? p.opps : p.devs)[modal.idx];
  const word = modal && (modal.sec === "opps" ? "الفرصة" : "التطوير");
  const stationNames = cur ? cur.subs.map((s) => s.name) : [];

  return (
    <>
      {modal?.type === "addItem" && (
        <FormModal title={modal.sec === "opps" ? "إضافة فرصة جديدة" : "إضافة تطوير 360 جديد"} label="حفظ" onClose={() => setModal(null)}
          validate={dateValidate}
          fields={[
            { k: "name", l: modal.sec === "opps" ? "اسم الفرصة" : "اسم التطوير", v: "", req: true },
            { k: "desc", l: "الوصف", v: "", area: true, rows: 5, req: true, ph: "اكتب وصفًا وافيًا…" },
            { k: "owner", l: "المالك", v: ownerOpts[0], opts: ownerOpts, req: true, hint: "القائمة تعرض المستخدمين المؤهلين وفق المنتج والصلاحيات." },
            ...(modal.sec === "opps" ? [{ k: "status", l: "حالة الفرصة", v: settings.oppStatus[0], opts: settings.oppStatus, req: true }] : []),
            { k: "start", l: "تاريخ البداية", v: "", type: "date", req: true },
            { k: "due", l: "التاريخ المستهدف (المساندة)", v: "", type: "date", req: true },
          ]}
          onSubmit={(v) => { act.addItem(modal.sec, v); setModal(null); }} />
      )}

      {modal?.type === "addUpdate" && (
        <FormModal title={`إضافة تحديث — ${cur.name}`} label="حفظ التحديث" onClose={() => setModal(null)}
          note={<><span>{I.note(13)}</span><span>تحديث/ملاحظة على {word} — لا يفتح تحدّيًا ولا محطة.</span></>}
          fields={[{ k: "text", l: "نص التحديث", v: "", area: true, rows: 5, req: true, ph: "ما الجديد؟ آخر مستجدات هذا العنصر…" }]}
          onSubmit={(v) => { act.addUpdate(modal.sec, modal.idx, v.text); setModal(null); }} />
      )}

      {modal?.type === "addStation" && (
        <FormModal title={`إضافة محطة — ${cur.name}`} label="حفظ المحطة" onClose={() => setModal(null)}
          note={<><span>{I.step(13)}</span><span>«حالة المحطة» من الإعدادات ← حالات المحطات.</span></>}
          fields={[
            { k: "status", l: "حالة المحطة", v: settings.stationStatus[0], opts: settings.stationStatus, req: true },
            { k: "custom", l: "اسم المحطة المخصص", v: "", req: true, when: (v) => v.status === OTHER, ph: "اكتب اسم المحطة" },
            { k: "owner", l: "الجهة المسؤولة", v: settings.depts[5] || settings.depts[0], opts: settings.depts, req: true },
            { k: "progress", l: "نسبة التقدم %", v: "0", type: "number", min: 0, max: 100, hint: "يمكن الحفظ بنسبة 0%." },
          ]}
          onSubmit={(v) => { act.addStation(modal.sec, modal.idx, { name: v.status === OTHER ? v.custom : v.status, owner: v.owner, progress: v.progress }); setModal(null); }} />
      )}

      {modal?.type === "editStation" && (() => {
        const sb = cur.subs[modal.si];
        const known = settings.stationStatus.includes(sb.name);
        return (
          <FormModal title={`تعديل المحطة — ${sb.name}`} label="حفظ التعديل" onClose={() => setModal(null)}
            fields={[
              { k: "status", l: "حالة المحطة", v: known ? sb.name : OTHER, opts: settings.stationStatus, req: true },
              { k: "custom", l: "اسم المحطة المخصص", v: known ? "" : sb.name, req: true, when: (v) => v.status === OTHER },
              { k: "owner", l: "الجهة المسؤولة", v: sb.owner, opts: settings.depts, req: true },
              { k: "progress", l: "نسبة التقدم %", v: String(sb.progress), type: "number", min: 0, max: 100 },
              { k: "note", l: "ملاحظة (اختياري — تُسجَّل كتحديث)", v: "", area: true, rows: 3 },
            ]}
            onSubmit={(v) => { act.updateStation(modal.sec, modal.idx, modal.si, { name: v.status === OTHER ? v.custom : v.status, owner: v.owner, progress: v.progress, note: v.note }); setModal(null); }} />
        );
      })()}

      {modal?.type === "addChallenge" && (
        <FormModal title={`إضافة تحدٍّ — ${cur.name}`} label="فتح التحدي" onClose={() => setModal(null)}
          note={<><span>{I.flag(13)}</span><span>تحدٍّ/Case مستقل. حدّد ارتباطه: {word} كاملًا أو محطة محددة.</span></>}
          fields={[
            { k: "title", l: "عنوان التحدي", v: "", req: true },
            { k: "desc", l: "وصف التحدي", v: "", area: true, rows: 5, req: true, ph: "اشرح التحدي وسببه…" },
            { k: "level", l: "مستوى الارتباط", v: modal.stage >= 0 ? "محطة محددة" : `${word} كاملًا`, opts: [`${word} كاملًا`, "محطة محددة"], req: true },
            { k: "station", l: "المحطة المرتبطة", v: modal.stage >= 0 ? (stationNames[modal.stage] || "") : "", opts: stationNames, req: true, when: (v) => v.level === "محطة محددة" },
            { k: "dept", l: "الجهة", v: settings.depts[0], opts: settings.depts, req: true },
            { k: "owner", l: "المالك/المسؤول", v: ownerOpts[0], opts: ownerOpts },
            { k: "state", l: "الحالة", v: settings.challengeStatus[0], opts: settings.challengeStatus, req: true },
            { k: "priority", l: "الأولوية", v: settings.challengePriority[1] || settings.challengePriority[0], opts: settings.challengePriority, req: true },
            { k: "due", l: "تاريخ الاستحقاق", v: "", type: "date" },
            { k: "need", l: "المطلوب (اختياري)", v: "" },
          ]}
          onSubmit={(v) => {
            const stage = v.level === "محطة محددة" ? Math.max(0, stationNames.indexOf(v.station)) : -1;
            act.addChallenge(modal.sec, modal.idx, stage, { title: v.title, cause: v.desc, need: v.need, dept: v.dept, owner: v.owner, state: v.state, priority: v.priority, due: v.due });
            setModal(null);
          }} />
      )}

      {modal?.type === "editChallenge" && (() => {
        const c = p.cases.find((x) => x.id === modal.cid);
        if (!c) return null;
        const atStation = c.stage != null && c.stage >= 0;
        return (
          <FormModal title={`تعديل التحدي — ${c.title}`} label="حفظ التعديل" onClose={() => setModal(null)}
            fields={[
              { k: "title", l: "عنوان التحدي", v: c.title, req: true },
              { k: "desc", l: "وصف التحدي", v: c.cause === "—" ? "" : c.cause, area: true, rows: 5, req: true },
              { k: "level", l: "مستوى الارتباط", v: atStation ? "محطة محددة" : `${word} كاملًا`, opts: [`${word} كاملًا`, "محطة محددة"], req: true },
              { k: "station", l: "المحطة المرتبطة", v: atStation ? (stationNames[c.stage] || "") : "", opts: stationNames, req: true, when: (v) => v.level === "محطة محددة" },
              { k: "dept", l: "الجهة", v: c.dept, opts: settings.depts, req: true },
              { k: "owner", l: "المالك/المسؤول", v: c.owner, opts: [...new Set([c.owner, ...ownerOpts])] },
              { k: "state", l: "الحالة", v: c.state, opts: settings.challengeStatus, req: true },
              { k: "priority", l: "الأولوية", v: c.priority || settings.challengePriority[1], opts: settings.challengePriority, req: true },
              { k: "due", l: "تاريخ الاستحقاق", v: /^\d{4}-\d{2}-\d{2}$/.test(c.due) ? c.due : "", type: "date" },
              { k: "need", l: "المطلوب (اختياري)", v: c.need === "—" ? "" : c.need },
            ]}
            onSubmit={(v) => {
              const stage = v.level === "محطة محددة" ? Math.max(0, stationNames.indexOf(v.station)) : -1;
              act.updateChallenge(c.id, { title: v.title, cause: v.desc, need: v.need, dept: v.dept, owner: v.owner, state: v.state, priority: v.priority, due: v.due, stage });
              setModal(null);
            }} />
        );
      })()}

      {modal?.type === "editItem" && (
        <FormModal title={modal.sec === "opps" ? `تعديل الفرصة — ${cur.name}` : `تعديل التطوير — ${cur.name}`} label="حفظ التعديل" onClose={() => setModal(null)}
          validate={dateValidate}
          fields={[
            { k: "name", l: modal.sec === "opps" ? "اسم الفرصة" : "اسم التطوير", v: cur.name, req: true },
            { k: "desc", l: "الوصف", v: cur.desc === "—" ? "" : cur.desc, area: true, rows: 5, req: true },
            { k: "owner", l: "المالك", v: cur.owner, opts: [...new Set([cur.owner, ...ownerOpts])], req: true },
            ...(modal.sec === "opps" ? [{ k: "status", l: "حالة الفرصة", v: cur.status && cur.status !== "—" ? cur.status : settings.oppStatus[0], opts: settings.oppStatus, req: true }] : []),
            { k: "start", l: "تاريخ البداية", v: /^\d{4}-\d{2}-\d{2}$/.test(cur.start) ? cur.start : "", type: "date", hint: cur.start && !/^\d{4}-\d{2}-\d{2}$/.test(cur.start) ? `القيمة الحالية: ${cur.start}` : undefined },
            { k: "due", l: "التاريخ المستهدف", v: /^\d{4}-\d{2}-\d{2}$/.test(cur.due) ? cur.due : "", type: "date", hint: cur.due && cur.due !== "—" && !/^\d{4}-\d{2}-\d{2}$/.test(cur.due) ? `القيمة الحالية: ${cur.due}` : undefined },
          ]}
          onSubmit={(v) => { act.editItem(modal.sec, modal.idx, v); setModal(null); }} />
      )}

      {modal?.type === "confirm" && (
        <ConfirmModal message={modal.message} onClose={() => setModal(null)}
          onConfirm={() => { modal.run(); setModal(null); }} />
      )}
    </>
  );
}

function DevView({ p, act, owners: ownerUsers, settings, currentUser }) {
  const [sec, setSec] = useState(null);
  const [openOpp, setOpenOpp] = useState(null);
  const [openDev, setOpenDev] = useState(null);
  const [modal, setModal] = useState(null);
  const cOppNew = can(currentUser, P.OPP_CREATE, { product: p });
  const cDevNew = can(currentUser, P.DEV_CREATE, { product: p });

  const secSummary = (key) => {
    const cs = p.cases.filter((c) => c.sec === key && c.state !== "مغلق");
    if (!cs.length) return <span className="msec-sum"><Sdot s="green" /> يمشي</span>;
    const red = cs.filter((c) => c.decision).length;
    return <span className="msec-sum"><Sdot s={red ? "red" : "yellow"} />{cs.length} تحدٍّ{red ? ` · ${red} قرار` : ""}</span>;
  };

  const Section = ({ id, title, n, onAdd, addLabel, canAdd, children }) => {
    const open = sec === id;
    return (
      <section className={`msec ${open ? "on" : ""}`}>
        <button className="msec-h" onClick={() => setSec(open ? null : id)}>
          <span className="chev">{open ? "−" : "+"}</span>
          <span className="mtitle">{title}</span>
          <span className="mcount">{n}</span>
          {secSummary(id)}
          <span className="msec-act">{open ? "إخفاء" : "عرض"}</span>
        </button>
        {open && (
          <div className="msec-b fade">
            {canAdd
              ? <div className="mhead" style={{ justifyContent: "flex-end" }}><button className="btn btn-v btn-sm" onClick={onAdd}>{addLabel}</button></div>
              : <div className="mhead" style={{ justifyContent: "flex-end" }}><span className="mut" style={{ fontSize: 11 }}>{I.lock(12)} لا تملك صلاحية الإضافة هنا</span></div>}
            {n === 0 && <div className="empty">لا عناصر بعد.</div>}
            {children}
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="fade mwrap">
      <p className="hint" style={{ margin: "0 0 4px" }}>قسمان مستقلان. لكل عنصر أزرار واضحة: تحديث · محطة · تحدٍّ — كلٌّ في نموذجه المستقل.</p>

      <Section id="opps" title="فرص المنتج" n={p.opps.length} onAdd={() => setModal({ type: "addItem", sec: "opps" })} addLabel="+ فرصة جديدة" canAdd={cOppNew}>
        <TabbedItemList items={p.opps} sec="opps" p={p} act={act} setModal={setModal} currentUser={currentUser}
          openId={openOpp} setOpenId={setOpenOpp} />
      </Section>

      <Section id="devs" title="تطويرات 360" n={p.devs.length} onAdd={() => setModal({ type: "addItem", sec: "devs" })} addLabel="+ تطوير جديد" canAdd={cDevNew}>
        <TabbedItemList items={p.devs} sec="devs" p={p} act={act} setModal={setModal} currentUser={currentUser}
          openId={openDev} setOpenId={setOpenDev} />
      </Section>

      <ItemModals p={p} act={act} settings={settings} owners={ownerUsers} modal={modal} setModal={setModal} />
    </div>
  );
}

function ImpactView({ p, act, owners: ownerUsers, settings, currentUser }) {
  const [open, setOpen] = useState(null);
  const [modal, setModal] = useState(null);
  const [tab, setTab] = useState("active");
  const cClose = can(currentUser, P.IMP_CLOSE, { product: p });

  const active = p.impacts.filter((im) => !im.closed);
  const done = p.impacts.filter((im) => im.closed);
  const list = tab === "done" ? done : active;

  return (
    <div className="fade mwrap">
      <p className="hint" style={{ margin: "0 0 4px" }}>يدخل التطوير هنا حين تختار «بدء قياس الأثر» له من تطويرات 360 بعد اكتماله — بنفس بطاقة تطويرات 360، تابعه بتحديثات ومحطات وتحديات كما هناك.</p>

      {p.impacts.length > 0 && (
        <div className="ctabs">
          <button className={`ctab ${tab === "active" ? "on" : ""}`} onClick={() => setTab("active")}>نشطة <b>{active.length}</b></button>
          <button className={`ctab ${tab === "done" ? "on" : ""}`} onClick={() => setTab("done")}>✓ مكتملة <b>{done.length}</b></button>
        </div>
      )}

      {p.impacts.length === 0 && (
        <div className="empty" style={{ padding: 30 }}>لا شيء بعد. أكمل تطويرًا في تطويرات 360 ثم اختر «بدء قياس الأثر».</div>
      )}

      {p.impacts.length > 0 && list.length === 0 && (
        <div className="empty" style={{ padding: 30 }}>{tab === "active" ? "لا عناصر قيد المتابعة." : "لا عناصر مكتملة بعد."}</div>
      )}

      <div className="cards-list">
        {list.map((im) => {
          const devItem = p.devs.find((d) => d.id === im.devId) || null;
          const devIdx = devItem ? p.devs.indexOf(devItem) : -1;
          const ii = p.impacts.indexOf(im);
          const isOpen = open === im.id;
          const toggle = () => setOpen(isOpen ? null : im.id);
          const statusAction = cClose && (
            im.closed
              ? <ActBtn icon={I.edit(15)} label="إعادة فتح" tip="إعادة العنصر إلى المتابعة" onClick={() => act.setImpactStatus(ii, false)} />
              : <ActBtn cls="edit" icon={I.edit(15)} label="تحديد كمكتمل" tip="إغلاق متابعة قياس الأثر لهذا العنصر" onClick={() => act.setImpactStatus(ii, true)} />
          );
          const doneBadge = im.closed && <span className="vtag v-green">✓ مكتمل</span>;

          if (!devItem) {
            return (
              <div className="ocard" key={im.id}>
                <button className="ocard-head" onClick={toggle}>
                  <Sdot s="gray" title="العنصر الأصلي محذوف" />
                  <span className="oname">{im.name}</span>
                  {doneBadge}
                  <span className="chev">{isOpen ? "−" : "+"}</span>
                </button>
                {isOpen && (
                  <div className="ocard-body fade">
                    <p className="odesc mut">العنصر الأصلي محذوف من تطويرات 360 — لا مزيد من التفاصيل متاحة.</p>
                    {statusAction && <div className="actbar">{statusAction}</div>}
                  </div>
                )}
              </div>
            );
          }
          return (
            <ItemCard key={im.id} p={p} sec="devs" item={devItem} idx={devIdx} act={act} setModal={setModal} currentUser={currentUser}
              isOpen={isOpen} toggle={toggle} showIndex={false} headerBadge={doneBadge} extraAction={statusAction} />
          );
        })}
      </div>

      <ItemModals p={p} act={act} settings={settings} owners={ownerUsers} modal={modal} setModal={setModal} />
    </div>
  );
}

function Board({ products, onOpen, onAdd, onDelete, users, settings, currentUser }) {
  const [dlg, setDlg] = useState(false);
  const [del, setDel] = useState(null);
  const [f, setF] = useState({ name: "", desc: "", owner: "", entity: settings.orgUnits[0], start: "", stage: "", target: "", has360: "none", status: "green", method: "يدوي" });
  const visible = visibleProducts(currentUser, products);
  const canAdd = can(currentUser, P.PRODUCTS_CREATE);
  const canDelete = can(currentUser, P.PRODUCTS_DELETE);
  const pmOpts = users.filter((u) => u.active && (u.role === "product" || u.role === "project")).map((u) => u.name);
  return (
    <div className="fade" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div className="h-row">
        <div>
          <h1 style={{ margin: 0, fontSize: 21 }}>المنتجات العدلية</h1>
          <p className="hint">أنت الآن: <b style={{ color: T.verdi }}>{currentUser?.name}</b> — <span className={`urole ${roleOf(currentUser?.role).cls}`}>{roleOf(currentUser?.role).name}</span> · النطاق: {roleOf(currentUser?.role).scope}. تظهر لك {visible.length} من {products.length} منتجات.</p>
        </div>
        {canAdd && <button className="btn btn-p" onClick={() => setDlg(true)}>+ إضافة منتج</button>}
      </div>

      {visible.length === 0 && (
        <div className="card pad empty">لا توجد منتجات ضمن صلاحياتك الحالية. غيّر المستخدم من الأعلى للاطلاع بحساب آخر.</div>
      )}

      <div className="grid-cards">
        {visible.map((p) => {
          const pct = finPct(p), is360 = p.has360 === "full", ps = prodStatus(p);
          return (
            <div className={`pcard ${is360 ? "is360" : ""}`} key={p.id} role="button" tabIndex={0}
              onClick={() => onOpen(p.id)} onKeyDown={(e) => e.key === "Enter" && onOpen(p.id)}>
              <div className="pc-top">
                <div>
                  <div className="pc-name">{p.name}</div>
                  <div className="pc-lbl">مدير المنتج</div>
                  <div className="pc-val">{p.owner}</div>
                </div>
                {is360 ? <Seal /> : <span className={`pill ${p.has360 === "inprogress" ? "p-gray" : "p-bad"}`}>{p.has360 === "inprogress" ? "قيد بناء 360" : "لا يملك 360"}</span>}
              </div>

              <div>
                <div className="pc-lbl" style={{ marginTop: 0 }}>وصف المنصة</div>
                <div className="pc-desc">{p.desc}</div>
              </div>

              <div className="pc-money">
                <div>
                  <div className="pc-lbl" style={{ marginTop: 0 }}>الإيراد الحالي</div>
                  <div className="pc-rev">{fmt(p.revenue)} ﷼</div>
                </div>
                <div style={{ textAlign: "start" }}>
                  <div className="pc-lbl" style={{ marginTop: 0 }}>المستهدف</div>
                  <div className="pc-tgt2">{fmt(p.finTarget)} ﷼</div>
                </div>
              </div>
              <div className="pc-dates">
                <div>البداية<b>{fmtD(p.start)}</b></div>
                <div>{p.target && p.target !== "غير محدد" ? "المستهدف" : "المرحلة"}<b>{p.target && p.target !== "غير محدد" ? fmtD(p.target) : p.stage}</b></div>
              </div>
              <div className="track"><div style={{ width: `${Math.min(pct, 100)}%`, background: pct >= 90 ? T.ok : pct >= 70 ? T.verdi : T.warn }} /></div>

              <div className="pc-foot">
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span className="sdot" style={{ background: CLR[ps.s] }} />{totals(p).open > 0 ? `${totals(p).open} لمبة نشطة` : "لا لمبات"}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
                  آخر تحديث: {p.updated}
                  {canDelete && (
                    <button className="icon" title="حذف المنتج" style={{ width: 24, height: 24 }}
                      onClick={(e) => { e.stopPropagation(); setDel(p); }}>{I.trash(12)}</button>
                  )}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {del && (
        <ConfirmModal title="حذف المنتج" confirmLabel="نعم، احذف نهائيًا"
          message={`سيُحذف منتج «${del.name}» وكل بياناته (المؤشرات والفرص والتطويرات والحالات) نهائيًا. لا يمكن التراجع عن هذا الإجراء.`}
          onClose={() => setDel(null)} onConfirm={() => { onDelete(del.id); setDel(null); }} />
      )}

      {dlg && (
        <Modal title="إضافة منتج" onClose={() => setDlg(false)} disabled={!f.name} label="إضافة المنتج"
          onSubmit={() => { onAdd(f); setDlg(false); }}>
          <Field label="اسم المنتج"><input className="inp" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="وصف مختصر"><textarea className="inp" rows={4} value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="مدير المنتج"><select className="inp" value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })}><option value="">— اختر —</option>{pmOpts.map((o) => <option key={o}>{o}</option>)}</select></Field>
            <Field label="الإدارة/الجهة"><select className="inp" value={f.entity} onChange={(e) => setF({ ...f, entity: e.target.value })}>{settings.orgUnits.map((o) => <option key={o}>{o}</option>)}</select></Field>
            <Field label="تاريخ البداية"><input type="date" className="inp" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></Field>
            <Field label="المرحلة الحالية"><input className="inp" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })} placeholder="تحليل" /></Field>
          </div>
          <Field label="استراتيجية 360"><Chips opts={["لا يملك 360", "قيد بناء 360", "لديه 360"]} v={{ none: "لا يملك 360", inprogress: "قيد بناء 360", full: "لديه 360" }[f.has360]} on={(v) => setF({ ...f, has360: { "لا يملك 360": "none", "قيد بناء 360": "inprogress", "لديه 360": "full" }[v] })} /></Field>
          <Field label="آلية تحديث المؤشرات"><Chips opts={["يدوي", "API"]} v={f.method} on={(v) => setF({ ...f, method: v })} /></Field>
        </Modal>
      )}
    </div>
  );
}

const H360 = { none: "لا يملك 360", inprogress: "قيد بناء 360", full: "لديه 360" };
const H360_R = { "لا يملك 360": "none", "قيد بناء 360": "inprogress", "لديه 360": "full" };
const OVERDUE = (c) => {
  if (!c.due || c.due === "—" || c.state === "مغلق") return false;
  let d; const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(c.due);
  if (m) d = new Date(+m[1], +m[2] - 1, +m[3]);
  else { const mm = /^(\d{2})-(\d{2})$/.exec(c.due); if (mm) d = new Date(2026, +mm[2] - 1, +mm[1]); }
  if (!d || isNaN(d.getTime())) return false;
  return d < new Date(2026, 6, 20);
};
function CasesBoard({ p, act, currentUser }) {
  const [q, setQ] = useState("");
  const [fDept, setFDept] = useState("");
  const [fState, setFState] = useState("");
  const [seg, setSeg] = useState("all");
  const [open, setOpen] = useState(null);
  const [txt, setTxt] = useState({});
  const cUpd = can(currentUser, P.CASE_UPDATE, { product: p });
  const itemOf = (c) => (c.sec === "opps" ? p.opps : p.devs).find((it) => it.id === c.rowId);
  const stationName = (c) => { const it = itemOf(c); return (c.stage != null && c.stage >= 0 && it) ? (it.subs[c.stage]?.name || "—") : "—"; };
  const elLabel = (c) => { const it = itemOf(c); return it ? `${c.sec === "opps" ? "فرصة" : "تطوير"}: ${it.name}` : "—"; };
  const depts = [...new Set(p.cases.map((c) => c.dept))];
  const states = [...new Set(p.cases.map((c) => c.state))];
  const ql = q.trim();
  const rows = p.cases.filter((c) => {
    if (seg === "open" && c.state === "مغلق") return false;
    if (seg === "closed" && c.state !== "مغلق") return false;
    if (seg === "decision" && !c.decision) return false;
    if (seg === "late" && !OVERDUE(c)) return false;
    if (fDept && c.dept !== fDept) return false;
    if (fState && c.state !== fState) return false;
    if (ql && ![c.id, c.title, c.cause, c.need, c.owner, c.dept, elLabel(c)].join(" ").includes(ql)) return false;
    return true;
  });
  const counts = {
    all: p.cases.length, open: p.cases.filter((c) => c.state !== "مغلق").length,
    closed: p.cases.filter((c) => c.state === "مغلق").length,
    decision: p.cases.filter((c) => c.decision).length, late: p.cases.filter((c) => OVERDUE(c)).length,
  };
  return (
    <div className="fade mwrap">
      <div className="title">{I.flag(17)} حالات المنتج (Cases)</div>
      <p className="hint" style={{ margin: "3px 0 8px" }}>كل Cases الفرص والتطويرات في مكان واحد — مفلترة ومرتبطة بعنصرها ومحطتها وجهتها. الضغط على أي صف يفتح التفاصيل والإجراءات.</p>
      <div className="segbar">
        {[["all", "الكل"], ["open", "مفتوحة"], ["closed", "مغلقة"], ["decision", "تحتاج قرار"], ["late", "متأخرة"]].map(([k, l]) => (
          <button key={k} className={`seg ${seg === k ? "on" : ""}`} onClick={() => setSeg(k)}>{l} <b>{counts[k]}</b></button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "10px 0" }}>
        <div className="search-in" style={{ flex: "1 1 200px", marginBottom: 0 }}>{I.search(16)}<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في الحالات…" /></div>
        <select className="inp" style={{ width: "auto" }} value={fDept} onChange={(e) => setFDept(e.target.value)}><option value="">كل الجهات</option>{depts.map((d) => <option key={d}>{d}</option>)}</select>
        <select className="inp" style={{ width: "auto" }} value={fState} onChange={(e) => setFState(e.target.value)}><option value="">كل الحالات</option>{states.map((s) => <option key={s}>{s}</option>)}</select>
      </div>
      {rows.length === 0 ? <div className="empty" style={{ padding: 26 }}>لا حالات مطابقة للفلاتر.</div> : (
        <div className="tw">
          <table className="tbl">
            <thead><tr><th>#</th><th>العنوان</th><th>العنصر / المحطة</th><th>الجهة</th><th>المالك</th><th>الأولوية</th><th>الحالة</th><th>الاستحقاق</th></tr></thead>
            <tbody>
              {rows.map((c) => (
                <React.Fragment key={c.id}>
                  <tr onClick={() => setOpen(open === c.id ? null : c.id)} style={{ cursor: "pointer" }}>
                    <td className="mut" style={{ whiteSpace: "nowrap" }}>{c.id}</td>
                    <td><b>{c.title}</b>{c.decision && <span className="pill p-bad" style={{ marginInlineStart: 6 }}>قرار</span>}{OVERDUE(c) && <span className="pill p-warn" style={{ marginInlineStart: 6 }}>متأخر</span>}</td>
                    <td className="mut" style={{ fontSize: 11.4 }}>{elLabel(c)}{stationName(c) !== "—" && <> · <span style={{ color: T.verdi }}>{stationName(c)}</span></>}</td>
                    <td className="mut">{c.dept}</td>
                    <td className="mut">{c.owner}</td>
                    <td><span className={`pri ${PRI_CLS[c.priority] || "pri-mid"}`}>{c.priority || "متوسطة"}</span></td>
                    <td style={{ whiteSpace: "nowrap" }}><span className="sdot" style={{ background: c.state === "مغلق" ? T.ok : (c.decision ? T.bad : T.warn), display: "inline-block", width: 8, height: 8, borderRadius: 8, marginInlineEnd: 5 }} />{c.state}</td>
                    <td className="mut" style={{ whiteSpace: "nowrap" }}>{fmtD(c.due)}</td>
                  </tr>
                  {open === c.id && (
                    <tr><td colSpan={8} style={{ background: "var(--surface-2)" }}>
                      <div style={{ padding: "4px 4px 10px" }}>
                        <div className="chal-need">المشكلة/السبب: {c.cause}</div>
                        {c.need && c.need !== "—" && <div className="chal-need">المطلوب: <b>{c.need}</b></div>}
                        <div className="mut" style={{ fontSize: 10.8, margin: "5px 0" }}>آخر تحديث: {c.updated}{c.closedBy ? ` · أغلقه ${c.closedBy} (${c.closedAt})` : ""}</div>
                        {c.replies?.length > 0 && <div className="mreplies">{c.replies.map((r, i) => <div key={i}><span>{r.w} · {r.d}</span>{r.t}</div>)}</div>}
                        {c.state !== "مغلق" ? (
                          <>
                            {cUpd && <div style={{ display: "flex", gap: 7, marginTop: 8 }}>
                              <input className="inp" placeholder="أضف ردًا/تحديثًا…" value={txt[c.id] || ""} onChange={(e) => setTxt({ ...txt, [c.id]: e.target.value })} />
                              <button className="btn btn-p btn-sm" onClick={() => { if ((txt[c.id] || "").trim()) { act.addReply(c.id, txt[c.id].trim()); setTxt({ ...txt, [c.id]: "" }); } }}>رد</button>
                            </div>}
                            <div className="mlinks">
                              {cUpd && !c.decision && <Lnk onClick={() => act.setCaseState(c.id, "بانتظار قرار")} tone={T.bad}>{I.flag(12)} تصعيد لقرار</Lnk>}
                              {cUpd && c.state !== "قيد المعالجة" && <Lnk onClick={() => act.setCaseState(c.id, "قيد المعالجة")}>قيد المعالجة</Lnk>}
                              {can(currentUser, P.CASE_CLOSE, { product: p, decision: c.decision })
                                ? <Lnk onClick={() => act.closeCase(c.id)} tone={T.ok}>إغلاق</Lnk>
                                : c.decision ? <span className="mut" style={{ fontSize: 10.6 }}>{I.lock(11)} الإغلاق يتطلب قرارًا إداريًا</span> : null}
                            </div>
                          </>
                        ) : (
                          cUpd && <div className="mlinks"><Lnk onClick={() => act.setCaseState(c.id, "مفتوح")} tone={T.verdi}>إعادة فتح</Lnk></div>
                        )}
                      </div>
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProjectManagerPanel({ p, api, refreshUsers, currentUser, notify }) {
  const [assign, setAssign] = useState(false);
  const [remove, setRemove] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [list, setList] = useState([]);
  const canAssign = can(currentUser, P.PJM_ASSIGN, { product: p });
  // Both lists are fetched fresh from the server rather than derived from the app-wide `users`
  // state: that list is scoped to users the caller manages (isInManagedScope), which would hide
  // members who don't already share another product with the caller — exactly the case this
  // panel needs to handle correctly, especially now that assignment isn't limited to role='project'.
  const refreshList = () => api.projectManagers.list(p.id).then((r) => setList(r.members)).catch(() => setList([]));
  useEffect(() => {
    if (canAssign) api.projectManagers.list(p.id).then((r) => setList(r.members)).catch(() => setList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAssign, api, p.id]);
  useEffect(() => {
    if (!assign || !canAssign) return;
    api.projectManagers.candidates(p.id).then((r) => setCandidates(r.candidates)).catch(() => setCandidates([]));
  }, [assign, canAssign, api, p.id]);
  if (!canAssign) return null;
  const doAssign = async (uid) => {
    try {
      await api.projectManagers.assign(p.id, uid);
      await Promise.all([refreshList(), refreshUsers()]);
      notify("تم تعيين مدير المشروع على المنتج");
    } catch (err) { notify(err.message || "تعذّر تعيين مدير المشروع"); }
    setAssign(false);
  };
  const doRemove = async () => {
    const u = remove;
    try {
      await api.projectManagers.remove(p.id, u.id);
      await Promise.all([refreshList(), refreshUsers()]);
      notify("أُزيل مدير المشروع مع حفظ سجله");
    } catch (err) { notify(err.message || "تعذّر إزالة مدير المشروع"); }
    setRemove(null);
  };
  return (
    <div className="card pad">
      <div className="h-row" style={{ marginBottom: 8 }}>
        <div className="title">{I.users(17)} مدراء المشاريع على هذا المنتج</div>
        <button className="btn btn-v btn-sm" onClick={() => setAssign(true)}>+ تعيين مدير مشروع</button>
      </div>
      {list.length === 0 ? <div className="empty">لا مدراء مشاريع معيّنين على هذا المنتج بعد.</div> : (
        <div className="tw"><table className="tbl" style={{ minWidth: 0 }}>
          <thead><tr><th>الاسم</th><th>الدور</th><th>البريد</th><th>الحالة</th><th></th></tr></thead>
          <tbody>{list.map((u) => (
            <tr key={u.id}><td><b>{u.name}</b></td><td className="mut">{roleOf(u.role).name}</td><td className="mut">{u.email}</td>
              <td>{u.active ? <span className="pill p-ok">نشط</span> : <span className="pill p-gray">معطّل</span>}</td>
              <td><Lnk tone={T.bad} onClick={() => setRemove(u)}>{I.trash(12)} إزالة</Lnk></td></tr>
          ))}</tbody>
        </table></div>
      )}
      {assign && (
        <Modal title="تعيين مدير مشروع على المنتج" label="إغلاق" onClose={() => setAssign(false)} onSubmit={() => setAssign(false)}>
          {candidates.length === 0
            ? <div className="empty">لا يوجد مستخدمون متاحون للإسناد. جميع المستخدمين النشطين مُسندون لهذا المنتج بالفعل.</div>
            : <div className="cards-list">{candidates.map((u) => (
              <div key={u.id} className="stn"><span className="stn-n">{u.name} <span className="mut">· {roleOf(u.role).name} · {u.email}</span></span>
                <button className="btn btn-p btn-sm" onClick={() => doAssign(u.id)}>إسناد لهذا المنتج</button></div>
            ))}</div>}
        </Modal>
      )}
      {remove && (
        <ConfirmModal title="إزالة مدير مشروع" confirmLabel="نعم، أزِل الإسناد"
          message={`سيفقد «${remove.name}» الوصول إلى منتج «${p.name}» فقط. تبقى مساهماته وسجلاته محفوظة في سجل التغييرات.`}
          onClose={() => setRemove(null)} onConfirm={doRemove} />
      )}
    </div>
  );
}

function ProductPage({ p, onBack, act, users, owners, api, refreshUsers, settings, currentUser, notify }) {
  const [tab, setTab] = useState("fin");
  const [edit, setEdit] = useState(false);
  const is360 = p.has360 === "full";
  const canEdit = canEditProduct(currentUser, p);
  const ownerOpts = [...new Set([p.owner, ...(owners || []).map((u) => u.name)])].filter(Boolean);
  const pmNames = projectManagersOf(users, p.id).map((u) => u.name).join("، ") || "—";
  if (!canSeeProduct(currentUser, p)) return (
    <div className="fade card pad denied">
      <div className="lock">{I.lock(26)}</div>
      <h2 style={{ margin: "0 0 6px" }}>لا تملك صلاحية الوصول</h2>
      <p className="hint" style={{ maxWidth: 440, margin: "0 auto 16px" }}>
        هذا المنتج خارج نطاق حسابك الحالي (<b>{roleOf(currentUser?.role).name}</b>). الوصول ممنوع حتى عبر الرابط المباشر.
      </p>
      <button className="btn btn-g" onClick={onBack}>{I.back()} الرجوع للمنتجات</button>
    </div>
  );
  const info = [
    ["مدير المنتج", p.owner], ["مدراء المشاريع", pmNames], ["الجهة المسؤولة", p.entity], ["تاريخ البداية", fmtD(p.start)],
    ["المرحلة الحالية", p.stage], ["التاريخ المستهدف", fmtD(p.target)], ["الإيراد الحالي", `${fmt(p.revenue)} ﷼`], ["المستهدف", `${fmt(p.finTarget)} ﷼`],
  ];
  return (
    <div className="fade" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <button className="btn btn-g" style={{ alignSelf: "flex-start" }} onClick={onBack}>{I.back()} الرجوع للمنتجات</button>

      <div className="card">
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${T.line}`, display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ color: T.verdi, display: "grid" }}>{I.scale(17)}</span>
          <span className="dsp" style={{ fontSize: 14.5 }}>معلومات المنتج</span>
          {canEdit
            ? <button className="btn btn-g btn-sm" style={{ marginInlineStart: "auto" }} onClick={() => setEdit(true)}>{I.edit(14)} تعديل بيانات المنتج</button>
            : <span className="mut" style={{ marginInlineStart: "auto", fontSize: 11 }}>{I.lock(13)} للعرض فقط</span>}
        </div>
        <div className="pad" style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
          {is360 && <Seal big />}
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontSize: 21 }}>{p.name}</h1>
              {is360 ? <span className="badge360"><i>360</i><span>منتج ناضج</span></span>
                : <span className={`pill ${p.has360 === "inprogress" ? "p-gray" : "p-bad"}`}>{p.has360 === "inprogress" ? "قيد بناء 360" : "لا يملك 360"}</span>}
              <span className="pill p-gray" style={{ gap: 6 }}><span className="sdot" style={{ background: CLR[prodStatus(p).s] }} />{prodStatus(p).t}</span>
            </div>
            <div className="pc-lbl" style={{ marginTop: 10 }}>وصف المنتج</div>
            <p className="hint" style={{ maxWidth: 640, marginTop: 3 }}>{p.desc}</p>
          </div>
        </div>
        <div className="info">
          {info.map(([l, v]) => <div className="info-i" key={l}><div className="info-l">{l}</div><div className="info-v">{v}</div></div>)}
        </div>
      </div>

      <ProjectManagerPanel p={p} api={api} refreshUsers={refreshUsers} currentUser={currentUser} notify={notify} />

      <Lens tab={tab} setTab={setTab} p={p} />

      <div className="card pad">
        {tab === "fin" && <IndicatorLibrary p={p} kind="fin" act={act} currentUser={currentUser} />}
        {tab === "ops" && <IndicatorLibrary p={p} kind="ops" act={act} currentUser={currentUser} />}
        {tab === "dev" && <DevView p={p} act={act} owners={owners} settings={settings} currentUser={currentUser} />}
        {tab === "imp" && <ImpactView p={p} act={act} owners={owners} settings={settings} currentUser={currentUser} />}
        {tab === "cases" && <CasesBoard p={p} act={act} currentUser={currentUser} />}
      </div>

      {edit && (
        <FormModal title={`تعديل بيانات — ${p.name}`} label="حفظ التعديلات" onClose={() => setEdit(false)}
          validate={(v) => { const e = {}; const r = Number(v.revenue), t = Number(v.finTarget); if (v.revenue !== "" && (isNaN(r) || r < 0)) e.revenue = "قيمة غير صحيحة"; if (v.finTarget !== "" && (isNaN(t) || t <= 0)) e.finTarget = "المستهدف يجب أن يكون أكبر من صفر"; return e; }}
          fields={[
            { k: "name", l: "اسم المنتج", v: p.name, req: true },
            { k: "desc", l: "وصف المنتج", v: p.desc === "—" ? "" : p.desc, area: true, rows: 4, req: true },
            { k: "owner", l: "مدير المنتج", v: p.owner, opts: ownerOpts, req: true, hint: "المستخدمون المؤهلون وفق المنتج والصلاحيات." },
            { k: "entity", l: "الإدارة/الجهة", v: p.entity, opts: settings.orgUnits, req: true, hint: "تغيير الجهة يؤثر على من يراه من مديري الإدارات." },
            { k: "stage", l: "المرحلة الحالية", v: p.stage },
            { k: "start", l: "تاريخ البداية", v: p.start, ph: "مثال: 02-2024" },
            { k: "target", l: "التاريخ المستهدف", v: p.target, ph: "مثال: 12-2026 أو غير محدد" },
            { k: "revenue", l: "الإيراد الحالي (﷼)", v: String(p.revenue), type: "number", min: 0, max: 1000000000, step: 10000 },
            { k: "finTarget", l: "المستهدف المالي (﷼)", v: String(p.finTarget), type: "number", min: 0, max: 1000000000, step: 10000 },
            { k: "has360", l: "استراتيجية 360", v: H360[p.has360], opts: ["لا يملك 360", "قيد بناء 360", "لديه 360"], req: true },
            { k: "method", l: "آلية تحديث المؤشرات", v: p.method, opts: ["يدوي", "API"], req: true },
          ]}
          onSubmit={(v) => { act.editProduct({ ...v, has360: H360_R[v.has360] }); setEdit(false); }} />
      )}
    </div>
  );
}

function SettingCard({ meta, values, canEdit, onAdd, onRemove }) {
  const [nv, setNv] = useState("");
  return (
    <div className="setg-card">
      <div className="setg-name">{I.scale(14)} {meta.name}</div>
      <div className="setg-desc">{meta.desc}</div>
      <div className="usemap">
        {I.loc(13)}
        <span className="crumb">تُستخدم في: <b>{meta.page}</b> <i>←</i> {meta.form} <i>←</i> {meta.field}</span>
      </div>
      <div className="vals">
        {values.map((x) => (
          <span className="val" key={x}>{x}{canEdit && values.length > 1 && x !== OTHER && <button title="حذف" onClick={() => onRemove(x)}>✕</button>}</span>
        ))}
        {canEdit && (
          <span className="val-add">
            <input value={nv} onChange={(e) => setNv(e.target.value)} placeholder="قيمة جديدة" onKeyDown={(e) => { if (e.key === "Enter" && nv.trim()) { onAdd(nv.trim()); setNv(""); } }} />
            <button className="btn btn-v btn-sm" onClick={() => { if (nv.trim()) { onAdd(nv.trim()); setNv(""); } }}>إضافة</button>
          </span>
        )}
      </div>
    </div>
  );
}

function Settings({ settings, setSettings, currentUser }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(SETTINGS_META.map((g) => g.group));
  const canEdit = can(currentUser, P.SETTINGS);
  const ql = q.trim();
  const match = (it) => !ql || [it.name, it.desc, it.page, it.form, it.field, ...(settings[it.key] || [])].join(" ").includes(ql);
  const addVal = (key, v) => setSettings((s) => (s[key].includes(v) ? s : { ...s, [key]: [...s[key], v] }));
  const rmVal = (key, v) => setSettings((s) => ({ ...s, [key]: s[key].filter((x) => x !== v) }));
  const toggle = (g) => setOpen((o) => (o.includes(g) ? o.filter((x) => x !== g) : [...o, g]));

  return (
    <div className="fade" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div className="h-row" style={{ marginBottom: 6 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 21 }}>الإعدادات</h1>
          <p className="hint">كل إعداد يوضّح أين ينعكس بالضبط: الصفحة ← النموذج ← الحقل. {canEdit ? "لديك صلاحية إدارة القيم." : "العرض للقراءة فقط (تعديل القيم يتطلب صلاحية إدارة الإعدادات)."}</p>
        </div>
      </div>

      <div className="search-in">{I.search(16)}<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في الإعدادات: اسم، وصف، صفحة، حقل، قيمة…" /></div>

      {SETTINGS_META.map((g) => {
        const items = g.items.filter(match);
        if (!items.length) return null;
        const isOpen = open.includes(g.group);
        return (
          <section className="setg-sec" key={g.group}>
            <button className="setg-sec-h" onClick={() => toggle(g.group)}>
              <span className="chev">{isOpen ? "−" : "+"}</span>
              <span className="mtitle">{g.group}</span>
              <span className="mcount">{items.length}</span>
            </button>
            {isOpen && (
              <div className="setg-sec-b fade">
                {items.map((it) => (
                  <SettingCard key={it.key} meta={it} values={settings[it.key] || []} canEdit={canEdit}
                    onAdd={(v) => addVal(it.key, v)} onRemove={(v) => rmVal(it.key, v)} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

const roleBadge = (r) => <span className={`urole ${roleOf(r).cls}`}>{roleOf(r).name}</span>;

function AssignProductsModal({ user, assignable, onClose, onSave }) {
  const [sel, setSel] = useState(new Set(user.products || []));
  const toggle = (id) => { const n = new Set(sel); n.has(id) ? n.delete(id) : n.add(id); setSel(n); };
  return (
    <Modal title={`إسناد منتجات — ${user.name}`} label="حفظ الإسناد" onClose={onClose} onSubmit={() => onSave([...sel])}>
      <p className="hint" style={{ marginTop: 0 }}>حدّد المنتجات التي يراها ويديرها هذا المستخدم. إلغاء التحديد يزيل وصوله لذلك المنتج فقط — وتبقى مساهماته في سجل التغييرات.</p>
      <div className="cards-list">
        {assignable.length === 0 && <div className="empty">لا منتجات متاحة للإسناد ضمن نطاقك.</div>}
        {assignable.map((p) => (
          <label key={p.id} className="stn" style={{ cursor: "pointer" }}>
            <input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} style={{ width: 16, height: 16 }} />
            <span className="stn-n">{p.name}</span>
            {(user.products || []).includes(p.id) && !sel.has(p.id) && <span className="pill p-bad" style={{ marginInlineStart: "auto" }}>سيُزال</span>}
          </label>
        ))}
      </div>
    </Modal>
  );
}

function UserManagement({ users, products, settings, currentUser, notify, api, refreshUsers }) {
  const [modal, setModal] = useState(null);
  const [assignId, setAssignId] = useState(null);
  const [del, setDel] = useState(null);
  const [fRole, setFRole] = useState("");
  const [fProduct, setFProduct] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [q, setQ] = useState("");
  const mine = managedUsers(currentUser, users);
  const roles = creatableRoles(currentUser);
  const prodName = (id) => products.find((p) => p.id === id)?.name || id;
  const canManage = roles.length > 0 || can(currentUser, P.USERS);
  const assignable = viewsAll(currentUser) ? products : products.filter((p) => (currentUser.products || []).includes(p.id));
  const ql = q.trim();
  const filtered = mine.filter((u) =>
    (!fRole || u.role === fRole) &&
    (!fStatus || (fStatus === "active" ? u.active : !u.active)) &&
    (!fProduct || (u.products || []).includes(fProduct)) &&
    (!ql || [u.name, u.email, u.org].filter(Boolean).join(" ").includes(ql)));

  const save = async (v, editingId) => {
    const linkedProducts = v.role === "product" || v.role === "project"
      ? (v.product ? [products.find((p) => p.name === v.product)?.id].filter(Boolean) : [])
      : [];
    try {
      if (editingId) {
        await api.users.update(editingId, { name: v.name, email: v.email, role: v.role, org: v.org || "" });
        if (v.product && (v.role === "product" || v.role === "project")) await saveAssign(editingId, linkedProducts);
        notify("حُدّث المستخدم");
      } else {
        const { tempPassword } = await api.users.create({
          name: v.name, email: v.email, role: v.role, org: v.org || "", products: linkedProducts,
          password: v.password?.trim() || undefined,
        });
        notify(tempPassword ? `أُضيف المستخدم — كلمة المرور المؤقتة: ${tempPassword}` : "أُضيف المستخدم — سيُطالَب بتغيير كلمة المرور عند الدخول");
      }
      await refreshUsers();
    } catch (err) { notify(err.message || "تعذّر حفظ المستخدم"); }
  };
  const toggleActive = async (id) => {
    const u = users.find((x) => x.id === id);
    try {
      await api.users.setStatus(id, !u.active);
      await refreshUsers();
      notify("حُدّثت حالة الحساب");
    } catch (err) { notify(err.message || "تعذّر تحديث الحالة"); }
  };
  const deleteUser = async (id) => {
    try {
      await api.users.remove(id);
      await refreshUsers();
      notify("حُذف المستخدم نهائيًا");
    } catch (err) { notify(err.message || "تعذّر حذف المستخدم"); }
    setDel(null);
  };
  const saveAssign = async (uid, ids) => {
    const u = users.find((x) => x.id === uid);
    try {
      if (u?.role === "project") {
        // Product managers (who lack users.manage) can still reach this screen for their own
        // project managers — route through project-managers.assign/remove, which is scoped per
        // product rather than the dept/enable-only /users/:id/products endpoint.
        const before = new Set(u.products || []);
        const added = ids.filter((id) => !before.has(id));
        const removed = [...before].filter((id) => !ids.includes(id));
        await Promise.all([
          ...added.map((pid) => api.projectManagers.assign(pid, uid)),
          ...removed.map((pid) => api.projectManagers.remove(pid, uid)),
        ]);
      } else {
        await api.users.setProducts(uid, ids);
      }
      await refreshUsers();
      notify("حُدّث الإسناد");
    } catch (err) { notify(err.message || "تعذّر تحديث الإسناد"); }
    setAssignId(null);
  };

  const editing = modal?.id ? users.find((u) => u.id === modal.id) : null;
  const assignUser = assignId ? users.find((u) => u.id === assignId) : null;

  return (
    <div className="fade" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="h-row">
        <div>
          <h1 style={{ margin: 0, fontSize: 21 }}>إدارة المستخدمين</h1>
          <p className="hint">أنت: {roleBadge(currentUser?.role)} — تدير {mine.length} مستخدمًا ضمن نطاقك. الإسناد ينعكس فورًا على ما يراه كل مستخدم، ويُوثّق في سجل التغييرات.</p>
        </div>
        {canManage && <button className="btn btn-p" onClick={() => setModal({})}>+ إضافة مستخدم</button>}
      </div>

      {!canManage && <div className="gate-note">{I.lock(18)}<span>حسابك الحالي لا يملك صلاحية إدارة المستخدمين. هذه الصلاحية لمدير الإدارة وإدارة التمكين ومدير المنتج (لتعيين مدراء المشاريع على منتجاته).</span></div>}

      {canManage && (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <div className="search-in" style={{ flex: "1 1 200px", marginBottom: 0 }}>{I.search(16)}<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو البريد…" /></div>
            <select className="inp" style={{ width: "auto" }} value={fRole} onChange={(e) => setFRole(e.target.value)}><option value="">كل الأدوار</option>{Object.keys(ROLES).map((r) => <option key={r} value={r}>{roleOf(r).name}</option>)}</select>
            <select className="inp" style={{ width: "auto" }} value={fProduct} onChange={(e) => setFProduct(e.target.value)}><option value="">كل المنتجات</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
            <select className="inp" style={{ width: "auto" }} value={fStatus} onChange={(e) => setFStatus(e.target.value)}><option value="">كل الحالات</option><option value="active">نشط</option><option value="inactive">معطّل</option></select>
          </div>

          <div className="tw">
            <table className="tbl">
              <thead><tr><th>المستخدم</th><th>الدور</th><th>الإدارة</th><th>المنتجات المسندة</th><th>الحالة</th><th>آخر دخول</th><th>أضافه</th><th>تاريخ الإضافة</th><th></th></tr></thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id}>
                    <td><b>{u.name}</b><div className="mut" style={{ fontSize: 10.6 }}>{u.email}</div></td>
                    <td>{roleBadge(u.role)}</td>
                    <td className="mut">{u.org || "—"}</td>
                    <td>{u.products?.length ? u.products.map(prodName).join("، ") : <span className="mut">—</span>}</td>
                    <td>{u.active ? <span className="pill p-ok">مفعّل</span> : <span className="pill p-gray">معطّل</span>}</td>
                    <td className="mut" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{u.lastLogin || "—"}</td>
                    <td className="mut" style={{ fontSize: 11 }}>{u.addedBy || "—"}</td>
                    <td className="mut" style={{ fontSize: 11, whiteSpace: "nowrap" }}>{u.addedAt || "—"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <Lnk onClick={() => setModal({ id: u.id })}>{I.edit(13)} تعديل</Lnk>
                      {(u.role === "product" || u.role === "project") && <Lnk onClick={() => setAssignId(u.id)} tone={T.verdi} style={{ marginInlineStart: 8 }}>إسناد منتجات</Lnk>}
                      {u.id !== currentUser.id && <Lnk onClick={() => setDel(u)} tone={T.bad} style={{ marginInlineStart: 8 }}>{I.trash(13)} حذف</Lnk>}
                      <button className={`tog ${u.active ? "on" : ""}`} style={{ marginInlineStart: 10 }} title={u.active ? "تعطيل" : "تفعيل"} onClick={() => toggleActive(u.id)} />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={9} className="empty">لا مستخدمين مطابقين للفلاتر.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {modal && (
        <FormModal title={editing ? `تعديل — ${editing.name}` : "إضافة مستخدم"} label={editing ? "حفظ" : "إضافة"} onClose={() => setModal(null)}
          validate={(v) => { const e = {}; if (v.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.email)) e.email = "بريد غير صحيح"; return e; }}
          fields={[
            { k: "name", l: "الاسم", v: editing?.name || "", req: true },
            { k: "email", l: "البريد الإلكتروني", v: editing?.email || "", req: true, ph: "name@moj.gov.sa" },
            { k: "role", l: "الدور", v: editing?.role || roles[0], opts: roles, req: true, hint: "الأدوار المتاحة تتبع صلاحيتك: لا يمكنك إنشاء دور أعلى من نطاقك." },
            { k: "org", l: "الإدارة", v: editing?.org || (currentUser.role === "dept" ? currentUser.org : settings.orgUnits[0]), opts: [...settings.orgUnits, ENABLE_ORG], when: (v) => v.role === "product" || v.role === "project" || v.role === "enable" },
            { k: "product", l: "المنتج المرتبط (يمكن إضافة المزيد لاحقًا عبر «إسناد منتجات»)", v: editing?.products?.[0] ? (products.find((p) => p.id === editing.products[0])?.name || "") : "", opts: assignable.map((p) => p.name), when: (v) => v.role === "product" || v.role === "project", hint: "يحدّد المنتج الأساسي الذي سيراه هذا المستخدم." },
            { k: "password", l: "كلمة المرور المبدئية (اختياري)", v: "", type: "password", when: () => !editing, hint: "اتركه فارغًا لتوليد كلمة مرور مؤقتة تلقائيًا. في الحالتين سيُطالَب المستخدم بتغيير كلمة المرور عند أول تسجيل دخول." },
          ]}
          onSubmit={(v) => { save(v, editing?.id); setModal(null); }} />
      )}

      {assignUser && <AssignProductsModal user={assignUser} assignable={assignable} onClose={() => setAssignId(null)} onSave={(ids) => saveAssign(assignUser.id, ids)} />}

      {del && (
        <ConfirmModal
          message={`سيُحذف حساب «${del.name}» نهائيًا ولن يستطيع الدخول بعد الآن، وستُزال إسنادات منتجاته. اسمه يبقى ظاهرًا كما هو في السجلات القديمة (مالك سابق لعناصر، سجل التغييرات…) دون أي حساب فعّال خلفه. لا يمكن التراجع.`}
          onClose={() => setDel(null)} onConfirm={() => deleteUser(del.id)} />
      )}
    </div>
  );
}

const OP_CLS = { "إضافة": "p-ok", "تعديل": "p-warn", "حذف": "p-bad", "إغلاق": "p-gray", "فتح": "p-v", "تعيين": "p-v", "إزالة": "p-bad", "تصعيد": "p-bad", "نقل": "p-v" };
function AuditLog({ audit, products, currentUser }) {
  const [q, setQ] = useState("");
  const [prod, setProd] = useState("");
  const [op, setOp] = useState("");
  // مدير المنتج/المشروع يرى سجل منتجاته فقط
  const scope = viewsAll(currentUser) ? audit : audit.filter((e) => visibleProducts(currentUser, products).some((p) => p.name === e.product) || e.product === "—");
  const ql = q.trim();
  const rows = scope.filter((e) =>
    (!prod || e.product === prod) && (!op || e.op === op) &&
    (!ql || [e.by, e.element, e.detail, e.product, e.from, e.to].join(" ").includes(ql)));
  const prods = [...new Set(scope.map((e) => e.product))];
  const ops = [...new Set(scope.map((e) => e.op))];
  return (
    <div className="fade" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 21 }}>سجل التغييرات (Audit Log)</h1>
        <p className="hint">من قام بالإجراء؟ ماذا تغيّر؟ القيمة قبل/بعد؟ ومتى؟ — يُسجَّل تلقائيًا مع كل عملية ولا يُحذف عند إزالة مستخدم.</p>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <div className="search-in" style={{ flex: "1 1 220px", marginBottom: 0 }}>{I.search(16)}<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث في السجل…" /></div>
        <select className="inp" style={{ width: "auto" }} value={prod} onChange={(e) => setProd(e.target.value)}><option value="">كل المنتجات</option>{prods.map((x) => <option key={x}>{x}</option>)}</select>
        <select className="inp" style={{ width: "auto" }} value={op} onChange={(e) => setOp(e.target.value)}><option value="">كل العمليات</option>{ops.map((x) => <option key={x}>{x}</option>)}</select>
      </div>
      <div className="tw">
        <table className="tbl">
          <thead><tr><th>التاريخ/الوقت</th><th>المستخدم</th><th>الدور</th><th>العملية</th><th>المنتج</th><th>العنصر</th><th>التفاصيل</th><th>قبل ← بعد</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="empty">لا سجلات مطابقة.</td></tr>}
            {rows.map((e) => (
              <tr key={e.id}>
                <td className="mut" style={{ whiteSpace: "nowrap", fontSize: 11.4 }}>{e.at}</td>
                <td><b>{e.by}</b></td>
                <td>{roleBadge(e.role)}</td>
                <td><span className={`pill ${OP_CLS[e.op] || "p-gray"}`}>{e.op}</span></td>
                <td className="mut">{e.product}</td>
                <td>{e.element}</td>
                <td className="mut" style={{ maxWidth: 220 }}>{e.detail}</td>
                <td className="mut" style={{ fontSize: 11.4 }}>{e.from || e.to ? <>{e.from || "—"} <span style={{ color: T.verdi }}>←</span> {e.to || "—"}</> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ForceChangePasswordScreen({ api, onDone }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { setError("كلمتا المرور غير متطابقتين"); return; }
    setBusy(true); setError(null);
    try {
      await api.changePassword(cur, next);
      onDone();
    } catch (err) { setError(err.message || "تعذّر تغيير كلمة المرور"); }
    finally { setBusy(false); }
  };

  return (
    <div className="app" dir="rtl" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <Style />
      <form className="card pad" style={{ width: 380, maxWidth: "92vw", display: "flex", flexDirection: "column", gap: 12 }} onSubmit={submit}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <div className="brand-mark" style={{ background: T.verdi }}>{I.scale(20)}</div>
          <div><div className="dsp" style={{ fontSize: 15 }}>يجب تغيير كلمة المرور</div><p className="hint" style={{ margin: 0 }}>هذا حساب جديد — لا يمكن المتابعة قبل تعيين كلمة مرور خاصة بك.</p></div>
        </div>
        <Field label="كلمة المرور الحالية"><input className="inp" type="password" required autoFocus value={cur} onChange={(e) => setCur(e.target.value)} /></Field>
        <Field label="كلمة المرور الجديدة (8 أحرف على الأقل)"><input className="inp" type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} /></Field>
        <Field label="تأكيد كلمة المرور الجديدة"><input className="inp" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
        {error && <div className="reqmsg">{error}</div>}
        <button className="btn btn-p" type="submit" disabled={busy} style={{ justifyContent: "center" }}>{busy ? "جارٍ الحفظ…" : "تغيير كلمة المرور والمتابعة"}</button>
      </form>
    </div>
  );
}

function LoginScreen({ onLogin, error, loading }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  return (
    <div className="app" dir="rtl" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <Style />
      <form className="card pad" style={{ width: 360, maxWidth: "92vw", display: "flex", flexDirection: "column", gap: 12 }}
        onSubmit={(e) => { e.preventDefault(); onLogin(email.trim(), password); }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <div className="brand-mark" style={{ background: T.verdi }}>{I.scale(20)}</div>
          <div><div className="dsp" style={{ fontSize: 15 }}>نظام إدارة المنظومة العدلية</div><p className="hint" style={{ margin: 0 }}>تسجيل الدخول</p></div>
        </div>
        <Field label="البريد الإلكتروني"><input className="inp" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@moj.gov.sa" /></Field>
        <Field label="كلمة المرور"><input className="inp" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {error && <div className="reqmsg">{error}</div>}
        <button className="btn btn-p" type="submit" disabled={loading} style={{ justifyContent: "center" }}>{loading ? "جارٍ الدخول…" : "دخول"}</button>
      </form>
    </div>
  );
}

function ProfileModal({ user, api, notify, onClose, onSaved }) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [busy, setBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [err, setErr] = useState(null);

  const saveProfile = async () => {
    setBusy(true); setErr(null);
    try {
      const { user: u } = await api.updateProfile({ name: name.trim(), email: email.trim() });
      onSaved(u);
      notify("تم تحديث الملف الشخصي");
      onClose();
    } catch (e) { setErr(e.message || "تعذّر حفظ التغييرات"); }
    finally { setBusy(false); }
  };

  const savePassword = async () => {
    setBusy(true); setErr(null);
    try {
      await api.changePassword(curPw, newPw);
      notify("تم تغيير كلمة المرور");
      setCurPw(""); setNewPw(""); setPwOpen(false);
    } catch (e) { setErr(e.message || "تعذّر تغيير كلمة المرور"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="الملف الشخصي" onClose={onClose} label={busy ? "جارٍ الحفظ…" : "حفظ"}
      disabled={busy || !name.trim() || !email.trim()} onSubmit={saveProfile}>
      <Field label="الاسم"><input className="inp" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="البريد الإلكتروني"><input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
      {err && <div className="reqmsg">{err}</div>}

      <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 6, paddingTop: 12 }}>
        {!pwOpen ? (
          <button type="button" className="lnk" onClick={() => setPwOpen(true)}>تغيير كلمة المرور</button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <Field label="كلمة المرور الحالية"><input className="inp" type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} /></Field>
            <Field label="كلمة المرور الجديدة (8 أحرف على الأقل)"><input className="inp" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} /></Field>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn btn-p btn-sm" disabled={busy || !curPw || newPw.length < 8} onClick={savePassword}>حفظ كلمة المرور</button>
              <button type="button" className="btn btn-g btn-sm" onClick={() => { setPwOpen(false); setCurPw(""); setNewPw(""); }}>إلغاء</button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Dashboard() {
  const [token, setTokenState] = useState(() => { try { return localStorage.getItem("jp360_token") || null; } catch { return null; } });
  const api = useMemo(() => createApi(token), [token]);
  const [currentUser, setCurrentUser] = useState(null);
  const [boot, setBoot] = useState(token ? "loading" : "anon"); // anon | loading | ready | error
  const [bootError, setBootError] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [loginBusy, setLoginBusy] = useState(false);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(SETTINGS_SEED);
  const [audit, setAudit] = useState([]);
  const [eligibleOwnersByProduct, setEligibleOwnersByProduct] = useState({});
  const [route, setRoute] = useState({ page: "board", id: null });
  const [toast, setToast] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const p = products.find((x) => x.id === route.id);
  const productUsers = p ? (eligibleOwnersByProduct[p.id] || []) : [];

  const setToken = (t) => { setTokenState(t); try { if (t) localStorage.setItem("jp360_token", t); else localStorage.removeItem("jp360_token"); } catch { /* storage unavailable */ } };

  const loadInitial = async (activeApi, user) => {
    setCurrentUser(user);
    const tasks = [
      activeApi.products.list().then((r) => setProducts(r.products)),
      activeApi.settings.get().then((r) => setSettings(r.settings)),
    ];
    if (creatableRoles(user).length > 0 || can(user, P.USERS)) tasks.push(activeApi.users.list().then((r) => setUsers(r.users)));
    await Promise.all(tasks);
  };

  const bootstrap = async () => {
    if (!token) { setBoot("anon"); return; }
    setBoot("loading"); setBootError(null);
    const freshApi = createApi(token);
    try {
      const { user } = await freshApi.me();
      await loadInitial(freshApi, user);
      setBoot("ready");
    } catch (err) {
      if (err.status === 401) { setToken(null); setBoot("anon"); return; }
      setBootError(err.message || "تعذّر الاتصال بالخادم"); setBoot("error");
    }
  };
  useEffect(() => { bootstrap(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (email, password) => {
    setLoginBusy(true); setLoginError(null);
    try {
      const { token: t, user } = await api.auth.login(email, password);
      setToken(t);
      if (user.mustChangePassword) { setCurrentUser(user); setBoot("mustChangePassword"); return; }
      await loadInitial(createApi(t), user);
      setBoot("ready");
    } catch (err) {
      setLoginError(err.message || "تعذّر تسجيل الدخول");
    } finally {
      setLoginBusy(false);
    }
  };
  const finishForcedPasswordChange = async () => {
    setBoot("loading");
    try { await loadInitial(api, currentUser); setBoot("ready"); }
    catch (err) { setBootError(err.message || "تعذّر الاتصال بالخادم"); setBoot("error"); }
  };
  const logout = () => {
    api.auth.logout().catch(() => {});
    setToken(null); setCurrentUser(null); setProducts([]); setUsers([]); setAudit([]);
    setEligibleOwnersByProduct({}); setRoute({ page: "board", id: null }); setBoot("anon");
  };

  useEffect(() => { window.scrollTo(0, 0); }, [route]);
  useEffect(() => { if (toast) { const t = setTimeout(() => setToast(null), 2400); return () => clearTimeout(t); } }, [toast]);

  // سجل التغييرات مصدره الخادم فقط الآن — يُجلب عند فتح الصفحة
  useEffect(() => {
    if (boot === "ready" && route.page === "audit" && can(currentUser, P.AUDIT)) {
      api.audit.list().then((r) => setAudit(r.entries.map((e) => ({ ...e, at: String(e.at).replace("T", " ").slice(0, 16) })))).catch(() => {});
    }
  }, [boot, route.page]); // eslint-disable-line react-hooks/exhaustive-deps

  // مالكو المنتج المؤهلون لاختياره كمالك — متاحة لأي مستخدم يرى المنتج، بغض النظر عن صلاحية إدارة المستخدمين
  useEffect(() => {
    if (boot === "ready" && route.page === "product" && route.id) {
      api.products.eligibleOwners(route.id).then((r) => setEligibleOwnersByProduct((m) => ({ ...m, [route.id]: r.owners }))).catch(() => {});
    }
  }, [boot, route.page, route.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshProduct = async (productId) => {
    const { product } = await api.products.get(productId);
    setProducts((prev) => prev.map((x) => (x.id === productId ? product : x)));
  };
  const refreshUsers = async () => {
    try { const { users: u } = await api.users.list(); setUsers(u); } catch { /* role may lack users.manage */ }
  };
  const refreshSettings = async (patch) => {
    const { settings: s } = await api.settings.update(patch);
    setSettings(s);
  };

  // مركز التنفيذ: ينفّذ الطلب عبر الـ API، ثم يُحدّث حالة المنتج من استجابة الخادم (لا صلاحيات محلية — الخادم هو خط الدفاع الفعلي)
  const mut = async (call, msg) => {
    try {
      await call();
      if (p) await refreshProduct(p.id);
      if (msg) setToast(msg);
    } catch (err) {
      setToast(err.message || "تعذّر تنفيذ العملية");
    }
  };

  const act = {
    editProduct: (v) => mut(() => api.products.update(p.id, {
      name: v.name || undefined, desc: v.desc, owner: v.owner || undefined, entity: v.entity || undefined,
      stage: v.stage, start: v.start, target: v.target,
      revenue: v.revenue !== "" && !isNaN(Number(v.revenue)) ? Number(v.revenue) : undefined,
      finTarget: v.finTarget !== "" && !isNaN(Number(v.finTarget)) ? Number(v.finTarget) : undefined,
      has360: v.has360 || undefined, method: v.method || undefined,
    }), "حُفظت بيانات المنتج"),

    addInd: (kind, f) => mut(() => api.indicators.create(p.id, { kind, name: f.name, value: f.value, unit: f.unit, gran: f.gran, source: f.source, method: f.method }), "أُضيف المؤشر"),

    editInd: (kind, id, f) => mut(() => api.indicators.update(p.id, id, {
      name: f.name, unit: f.unit, source: f.source, method: f.method, gran: f.gran,
      good: f.good === "ارتفاع أفضل" ? "up" : f.good === "انخفاض أفضل" ? "down" : undefined,
    }), "حُفظت تعديلات المؤشر"),

    deleteInd: (kind, id) => mut(() => api.indicators.remove(p.id, id), "حُذف المؤشر"),

    updateReading: (kind, id, val, note) => mut(() => api.indicators.addReading(p.id, id, { value: val, note }), "حُدثت القراءة وسُجلت في السجل"),

    addItem: (sec, v) => mut(() => api.items.create(p.id, sec, { name: v.name, desc: v.desc, owner: v.owner, status: v.status, start: v.start, due: v.due }), "أُضيف العنصر وحُفظ — أضف محطاته وتحدياته الآن"),

    editItem: (sec, i, v) => mut(() => api.items.update(p.id, sec, p[sec][i]?.id, { name: v.name, desc: v.desc, owner: v.owner, status: v.status, start: v.start, due: v.due }), "حُفظت تعديلات العنصر"),

    deleteItem: (sec, i) => mut(() => api.items.remove(p.id, sec, p[sec][i]?.id), "حُذف العنصر وفروعه"),

    deleteStation: (sec, i, si) => mut(() => api.items.removeStation(p.id, sec, p[sec][i]?.id, si), "حُذفت المحطة"),

    deleteChallenge: (cid) => mut(() => api.cases.remove(p.id, cid), "حُذف التحدي"),

    deleteUpdate: (sec, i, ui) => mut(() => api.items.removeUpdate(p.id, sec, p[sec][i]?.id, ui)),

    addUpdate: (sec, i, text) => mut(() => api.items.addUpdate(p.id, sec, p[sec][i]?.id, text), "سُجّل التحديث"),

    addStation: (sec, i, v) => mut(() => api.items.addStation(p.id, sec, p[sec][i]?.id, { name: v.name, owner: v.owner, progress: v.progress }), "أُضيفت المحطة"),

    updateStation: (sec, i, si, v) => mut(() => api.items.updateStation(p.id, sec, p[sec][i]?.id, si, { name: v.name, owner: v.owner, progress: v.progress, note: v.note }), "حُدثت المحطة"),

    reorderStation: (sec, i, si, dir) => mut(() => api.items.reorderStations(p.id, sec, p[sec][i]?.id, si, si + dir), "أُعيد ترتيب المحطة"),

    startImpact: (i) => mut(() => api.items.startImpact(p.id, p.devs[i]?.id), "بدأ قياس الأثر لهذا التطوير"),

    addChallenge: (sec, i, stage, ch) => mut(() => api.cases.create(p.id, {
      sec, rowId: p[sec][i]?.id, stage, title: ch.title, cause: ch.cause, need: ch.need,
      dept: ch.dept, owner: ch.owner || ch.dept, state: ch.state, priority: ch.priority, due: ch.due,
    }), "فُتح التحدي وحُفظ"),

    updateChallenge: (cid, v) => mut(() => api.cases.update(p.id, cid, {
      title: v.title, cause: v.cause, need: v.need, dept: v.dept, owner: v.owner,
      state: v.state, priority: v.priority, due: v.due, stage: v.stage,
    }), "حُدّث التحدي"),

    addReply: (cid, t) => mut(() => api.cases.reply(p.id, cid, t), "أُضيف رد"),

    setImpactStatus: (ii, closed) => mut(() => api.impacts.setStatus(p.id, p.impacts[ii]?.id, closed), closed ? "أُغلق قياس الأثر" : "أُعيد فتح قياس الأثر"),

    closeCase: (cid) => mut(() => api.cases.close(p.id, cid), "أُغلق الـ Case"),

    setCaseState: (cid, state) => mut(() => (state === "مفتوح" ? api.cases.reopen(p.id, cid) : api.cases.update(p.id, cid, { state })),
      state === "بانتظار قرار" ? "صُعّد الـ Case (بانتظار قرار)" : state === "مفتوح" ? "أُعيد فتح الـ Case" : "حُدّثت الحالة"),
  };

  const addProduct = async (f) => {
    try {
      await api.products.create({ name: f.name, desc: f.desc, owner: f.owner, entity: f.entity, has360: f.has360, method: f.method });
      const { products: list } = await api.products.list();
      setProducts(list);
      setToast(`أُضيف المنتج «${f.name}»`);
    } catch (err) { setToast(err.message || "تعذّر إضافة المنتج"); }
  };

  const deleteProduct = async (id) => {
    try {
      await api.products.remove(id);
      setProducts((prev) => prev.filter((x) => x.id !== id));
      if (route.id === id) setRoute({ page: "board", id: null });
      setToast("حُذف المنتج نهائيًا");
    } catch (err) { setToast(err.message || "تعذّر حذف المنتج"); }
  };

  if (boot === "anon") return <LoginScreen onLogin={handleLogin} error={loginError} loading={loginBusy} />;
  if (boot === "mustChangePassword") return <ForceChangePasswordScreen api={api} onDone={finishForcedPasswordChange} />;
  if (boot === "loading") return (
    <div className="app" dir="rtl" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <Style /><p className="hint">جارٍ التحميل…</p>
    </div>
  );
  if (boot === "error") return (
    <div className="app" dir="rtl" style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
      <Style />
      <div className="card pad" style={{ maxWidth: 420, textAlign: "center" }}>
        <h2 style={{ marginTop: 0 }}>تعذّر الاتصال بالخادم</h2>
        <p className="hint">{bootError}</p>
        <button className="btn btn-p" onClick={bootstrap}>إعادة المحاولة</button>
      </div>
    </div>
  );

  return (
    <div className="app" dir="rtl">
      <Style />
      <header className="top">
        <div className="top-in">
          <div className="brand">
            <div className="brand-mark">{I.scale(20)}</div>
            <div><div className="brand-name dsp">نظام إدارة المنظومة العدلية</div><div className="brand-sub">مالي · تشغيلي · 360</div></div>
          </div>
          <div className="hd-actions">
            <div className="nav">
              <button className={route.page === "board" ? "on" : ""} onClick={() => setRoute({ page: "board", id: null })}>المنتجات</button>
              {(creatableRoles(currentUser).length > 0 || can(currentUser, P.USERS)) && <button className={route.page === "users" ? "on" : ""} onClick={() => setRoute({ page: "users", id: null })}>المستخدمون</button>}
              {can(currentUser, P.AUDIT) && <button className={route.page === "audit" ? "on" : ""} onClick={() => setRoute({ page: "audit", id: null })}>سجل التغييرات</button>}
              {can(currentUser, P.SETTINGS) && <button className={route.page === "settings" ? "on" : ""} onClick={() => setRoute({ page: "settings", id: null })}>الإعدادات</button>}
            </div>
            <div className="switcher">
              <button className="lnk" style={{ color: "#F1F5F4" }} onClick={() => setShowProfile(true)}>{currentUser.name} — {roleOf(currentUser.role).name}</button>
              <button className="btn btn-g btn-sm" onClick={logout}>خروج</button>
            </div>
          </div>
        </div>
      </header>

      {showProfile && (
        <ProfileModal user={currentUser} api={api} notify={setToast}
          onClose={() => setShowProfile(false)}
          onSaved={(u) => setCurrentUser(u)} />
      )}

      <div className="wrap">
        {route.page === "board" && <Board products={products} onOpen={(id) => setRoute({ page: "product", id })} onAdd={addProduct} onDelete={deleteProduct} users={users} settings={settings} currentUser={currentUser} />}
        {route.page === "product" && p && <ProductPage p={p} onBack={() => setRoute({ page: "board", id: null })} act={act} users={users} owners={productUsers} api={api} refreshUsers={refreshUsers} settings={settings} currentUser={currentUser} notify={setToast} />}
        {route.page === "product" && !p && <div className="card pad empty">المنتج غير موجود أو خارج نطاق صلاحيتك.</div>}
        {route.page === "users" && <UserManagement users={users} products={products} settings={settings} currentUser={currentUser} notify={setToast} api={api} refreshUsers={refreshUsers} />}
        {route.page === "audit" && (can(currentUser, P.AUDIT) ? <AuditLog audit={audit} products={products} currentUser={currentUser} /> : <div className="card pad denied"><div className="lock">{I.lock(26)}</div><h2>غير مصرّح</h2></div>)}
        {route.page === "settings" && (can(currentUser, P.SETTINGS) ? <Settings settings={settings} setSettings={(fn) => refreshSettings(typeof fn === "function" ? fn(settings) : fn)} currentUser={currentUser} /> : <div className="card pad denied"><div className="lock">{I.lock(26)}</div><h2>غير مصرّح</h2><p className="hint">إدارة الإعدادات تتطلب صلاحية settings.manage.</p></div>)}
      </div>

      {toast && <div className="toast fade">{toast}</div>}
      <footer className="foot"><div className="foot-in">
        <span>الإصدار v{VERSION} · RBAC مركزي مُطبّق على الواجهة والتنفيذ · سجل تغييرات حيّ</span>
        <span className="verd">التطوير الناجح.. يستوعب السياقات!</span>
      </div></footer>
    </div>
  );
}

// حاجز أخطاء: يُظهر سبب أي خطأ في العرض بدل الشاشة البيضاء
class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error("Render error:", err, info); }
  render() {
    if (this.state.err) {
      return (
        <div style={{ direction: "rtl", fontFamily: "'Noto Sans Arabic',sans-serif", padding: 24, maxWidth: 760, margin: "40px auto", background: "#fff", border: "1px solid #eee", borderRadius: 14 }}>
          <h2 style={{ color: "#A84A3F", marginTop: 0 }}>حدث خطأ أثناء العرض</h2>
          <p style={{ color: "#4B5D60" }}>واجه النظام خطأً غير متوقع أثناء الرسم. النص التالي يحدّد موضع الخطأ (انسخه لي إن استمرّت المشكلة):</p>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#F6E7E5", padding: 12, borderRadius: 8, fontSize: 12, color: "#6f2f27", lineHeight: 1.6 }}>
            {String((this.state.err && this.state.err.stack) || this.state.err)}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return <ErrorBoundary><Dashboard /></ErrorBoundary>;
}
